import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { Stack, DatePicker, IconButton, MessageBar, MessageBarType, Spinner, SpinnerSize, Dropdown, Pivot, PivotItem, DetailsList, DetailsListLayoutMode, SelectionMode, ProgressIndicator, Dialog as FluentDialog, DialogType, DialogFooter, PrimaryButton, DefaultButton, TextField } from '@fluentui/react';
import { Panel, PanelType } from '@fluentui/react/lib/Panel';
import { DetailsRow } from '@fluentui/react/lib/DetailsList';
import { Combobox, Option, Button, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, Dialog, DialogTrigger, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent, Input, Label, makeStyles, Drawer, DrawerHeader, DrawerHeaderTitle, DrawerBody } from '@fluentui/react-components';
import { Dismiss24Regular, AddSquare28Regular, ChevronRight20Regular, ChevronDown20Regular } from '@fluentui/react-icons';
import { FiArrowLeft, FiFilter, FiDownload, FiFileText, FiEdit2, FiRotateCcw, FiGrid, FiFolder, FiPhone, FiMoreVertical, FiTrash2, FiMove, FiSave } from 'react-icons/fi';
import { exportTabToExcel, buildWidgetRows, exportSheetsToExcel } from '../../Utils/excelExport';
import { CiFilter } from 'react-icons/ci';
import { useTranslation } from 'react-i18next';

import { DismissFilled, Add20Filled, EditRegular, Edit24Regular, ArrowMove24Regular, ArrowDownload24Regular, Delete24Regular } from "@fluentui/react-icons";
import Widget from '../Widgets/Widget';
import { inboundCallsDashboard } from '../../Config/dashboardConfigs';
import { exportToPDF, exportTabsToPDF } from '../../Utils/pdfExport';
import { isCustomWidgetName, resolveWidgetTitle } from '../../Utils/widgetTitle';
import { WidgetTypes } from '../../Types/widgetTypes';
import DashboardFilters from './DashboardFilters';
import WidgetCatalog, { WIDGET_CATALOG } from './WidgetCatalog';
import TabManager from './TabManager';
import WidgetInstanceConfig from './WidgetInstanceConfig';
import AnalyticsWidgetCatalogPanel from './AnalyticsWidgetCatalogPanel';
import AnalyticsWidgetConfigPanel from './AnalyticsWidgetConfigPanel';
import { ConfirmAlert } from '../GenericComponent/ConfirmAlert';
import { useNotification } from '../GenericComponent/NotificationSystem';
import { StaticMetricWidget, StaticBarChartWidget, StaticDonutChartWidget, StaticLineChartWidget, StaticGaugeWidget, STATIC_WIDGET_DATA } from '../Widgets/StaticWidgets';
import { FluentMetricWidget, FluentBarChartWidget, FluentDonutChartWidget, FluentLineChartWidget, FluentHorizontalBarChartWidget } from '../Widgets/FluentUIWidgets';
import { fetchWidgetData, fetchBatchWidgetData, buildWidgetFilters, saveDashboardLayout, getDashboardLayout } from '../../Services/AnalyticsService';
import { PREFETCH_TTL_MS, PREFETCH_AWAIT_MAX_MS, getInFlightPrefetch } from '../../Services/AnalyticsPrefetchService';
import { startExcelExport, pollExportStatus } from '../../Services/ExportService';
import { updateScope } from '../../Services/HeedifyServices';
import { setScopes } from '../../Redux/Actions/ScopesActions';
import { setAnalyticsProfiles } from '../../Redux/Actions/AnalyticsActions';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './AnalyticsDashboard.css';

import { registerIcons } from '@fluentui/react/lib/Styling';

// Register missing icons with simple SVGs
registerIcons({
    icons: {
        clear: (
            <svg viewBox="0 0 2048 2048" width="1em" height="1em">
                <path d="M1115 1024l690 691-90 90-691-690-691 690-90-90 690-691-690-691 90-90 691 690 691-690 90 90-690 691z" fill="currentColor" />
            </svg>
        ),
        info: (
            <svg viewBox="0 0 2048 2048" width="1em" height="1em">
                <path d="M1024 0q141 0 272 36t245 103 207 160 160 207 103 245 36 272q0 141-36 272t-103 245-160 207-207 160-245 103-272 36q-141 0-272-36t-245-103-207-160-160-207-103-245-36-272q0-141 36-272t103-245 160-207 207-160T752 36t272-36zm128 1408v-640H896v640h256zM896 640h256V384H896v256z" fill="currentColor" />
            </svg>
        )
    }
});

const ResponsiveGridLayout = WidthProvider(Responsive);

// Requêtes dont les COLONNES sont des files / des flows : elles reçoivent la liste provisionnée
// correspondante (`queueColumns` / `flowColumns`) pour qu'une file ou un flow sans trafic sur la
// période apparaisse quand même, à zéro. Ce n'est PAS un filtre — le backend ne s'en sert que pour
// construire les colonnes ; le filtrage reste porté par flowKey/queueKey.
const QUEUE_COLUMN_QUERIES = ['overflow-by-reason', 'overflow-matrix'];
const FLOW_COLUMN_QUERIES = [
    'queue-kpi-summary',
    'month-to-date-summary',
    'calls-served-by-time-ranges',
    'calls-abandoned-by-time-ranges',
    'overflow-by-reason-flow'
];
// Même principe, appliqué aux LIGNES : ce tableau tire ses lignes des agents vus dans les
// statuts et les CDR de la période. Un agent qui n'a ni changé de statut ni pris d'appel
// n'existe dans aucune des deux sources — il ne produit donc aucune ligne et DISPARAÎT du
// rapport, alors que « connecté 0 h, 0 appel » est exactement ce que le superviseur veut lire.
// Constaté sur l'entité `default` : 9 agents provisionnés, 4 lignes affichées.
// La liste provisionnée sert aussi à NOMMER les lignes : le backend n'a que le displayName
// éventuellement présent dans les enregistrements, et affichait l'identifiant brut à défaut.
const AGENT_ROW_QUERIES = ['agent-status-statistics'];

/**
 * Convertit une date "YYYY-MM-DD" (telle que stockée dans la config d'un widget) en bornes UTC
 * de journée. On repasse par Date.UTC plutôt que par `new Date(str)` pour que le jour calendaire
 * choisi par l'utilisateur soit exactement celui envoyé au backend (qui lit l'ISO en UTC).
 * @param {string} dayString - Jour au format YYYY-MM-DD
 * @param {boolean} endOfDay - true → 23:59:59.999, false → 00:00:00.000
 * @returns {Date|null}
 */
const parseDayString = (dayString, endOfDay = false) => {
    if (!dayString || typeof dayString !== 'string') return null;
    const [y, m, d] = dayString.split('-').map(Number);
    if (!y || !m || !d) return null;
    return endOfDay
        ? new Date(Date.UTC(y, m - 1, d, 23, 59, 59, 999))
        : new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
};

/**
 * Calculate date range based on custom period
 * @param {string} period - Period type (today, yesterday, week, month, quarter, year, custom)
 * @param {object} [widgetConfig] - Config du widget ; pour period='custom', fournit
 *                                  customStartDate / customEndDate (YYYY-MM-DD).
 * @returns {object} - Object with startDate and endDate as ISO strings
 */
const calculateDateRangeFromPeriod = (period, widgetConfig = null) => {
    const now = new Date();
    const today = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0));
    let startDate, endDate;

    // Période FIGÉE : une date précise ou une plage saisie par l'utilisateur. Une seule date
    // renseignée (début OU fin) = journée unique, c'est le cas « une date donnée ».
    if (period === 'custom') {
        const start = parseDayString(widgetConfig?.customStartDate, false)
            || parseDayString(widgetConfig?.customEndDate, false);
        const end = parseDayString(widgetConfig?.customEndDate, true)
            || parseDayString(widgetConfig?.customStartDate, true);

        // Config incomplète (aucune date saisie) → on ne borne pas plutôt que d'inventer
        // une période : le widget affichera l'intégralité de l'historique.
        if (!start || !end) {
            return { startDate: null, endDate: null };
        }
        // Plage saisie à l'envers → on la remet dans l'ordre.
        return start <= end
            ? { startDate: start.toISOString(), endDate: end.toISOString() }
            : { startDate: parseDayString(widgetConfig?.customEndDate, false).toISOString(),
                endDate: parseDayString(widgetConfig?.customStartDate, true).toISOString() };
    }

    switch (period) {
        case 'today':
            startDate = new Date(today);
            endDate = new Date(today);
            endDate.setUTCDate(endDate.getUTCDate() + 1);
            endDate.setUTCMilliseconds(-1); // Fin de la journée
            break;
        
        case 'yesterday':
            startDate = new Date(today);
            startDate.setUTCDate(startDate.getUTCDate() - 1);
            endDate = new Date(today);
            endDate.setUTCMilliseconds(-1);
            break;

        // Dernier jour OUVRÉ : « hier », mais en sautant samedi et dimanche. Un lundi matin,
        // « Yesterday » renvoie un dimanche vide ; cette période remonte au vendredi.
        // Seul le week-end est sauté — les jours fériés ne sont pas connus du portail, ils
        // dépendent du calendrier de chaque entité (filtre « jours ouvrés » côté CDR).
        case 'lastBusinessDay': {
            const day = new Date(today);
            do {
                day.setUTCDate(day.getUTCDate() - 1);
            } while (day.getUTCDay() === 0 || day.getUTCDay() === 6);   // 0 = dimanche, 6 = samedi
            startDate = new Date(day);
            endDate = new Date(day);
            endDate.setUTCDate(endDate.getUTCDate() + 1);
            endDate.setUTCMilliseconds(-1);
            break;
        }

        case 'week':
            startDate = new Date(today);
            startDate.setUTCDate(startDate.getUTCDate() - 7);
            endDate = new Date(today);
            endDate.setUTCDate(endDate.getUTCDate() + 1);
            endDate.setUTCMilliseconds(-1);
            break;
        
        case 'month':
            startDate = new Date(today);
            startDate.setUTCDate(startDate.getUTCDate() - 30);
            endDate = new Date(today);
            endDate.setUTCDate(endDate.getUTCDate() + 1);
            endDate.setUTCMilliseconds(-1);
            break;
        
        case 'quarter':
            startDate = new Date(today);
            startDate.setUTCDate(startDate.getUTCDate() - 90);
            endDate = new Date(today);
            endDate.setUTCDate(endDate.getUTCDate() + 1);
            endDate.setUTCMilliseconds(-1);
            break;
        
        case 'year':
            startDate = new Date(today);
            startDate.setUTCDate(startDate.getUTCDate() - 365);
            endDate = new Date(today);
            endDate.setUTCDate(endDate.getUTCDate() + 1);
            endDate.setUTCMilliseconds(-1);
            break;
        
        default:
            // Default to last 30 days
            startDate = new Date(today);
            startDate.setUTCDate(startDate.getUTCDate() - 30);
            endDate = new Date(today);
            endDate.setUTCDate(endDate.getUTCDate() + 1);
            endDate.setUTCMilliseconds(-1);
    }

    return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
    };
};

// Styles pour le dialog d'ajout de tab
const useDialogStyles = makeStyles({
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
    },
    inputContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
});

// Widgets displayed in the hardcoded "Dashboard" tab (= Overview content)
const DASHBOARD_WIDGET_IDS = [
    'widget-total-calls',
    'widget-answered-calls',
    'widget-abandoned-calls',
    'widget-ivr-abandons',
    'widget-outbound-calls',
    'widget-service-level',
    'widget-average-wait-time',
    'widget-avg-abandon-time',
    // AHT et temps de conversation : ces widgets existaient déjà et étaient INTERROGÉS à
    // chaque rafraîchissement, mais n'apparaissaient dans aucun onglet — trois requêtes
    // Cosmos payées pour rien. Ils sont désormais affichés.
    'widget-handle-time',
    'widget-talk-time',
    'widget-avg-inbound-call-duration',
    'widget-avg-outbound-call-duration',
    'widget-sla-trend',
    'widget-call-status-distribution',
    'widget-calls-by-country',
    'widget-call-volume-daily'
];

/**
 * Drill-down : cliquer sur une carte de comptage ouvre l'onglet « Calls List » filtré sur
 * la population EXACTE que compte cette carte.
 *
 * ⚠️ N'inscrire ici qu'un widget dont la définition backend correspond, au document près, à
 * un filtre de colonne de la liste. Un mapping approximatif est pire que pas de drill-down :
 * l'utilisateur clique sur 100 et compte 123 lignes, et c'est tout le rapport qu'il cesse de
 * croire. Les trois catégories ci-dessous ont été réconciliées sur données de production
 * (handled / abandoned / quick_drop : écart nul), la liste et les KPI passant désormais tous
 * par `TAXO.classify()` côté Data_Pipe.
 *
 * Cas volontairement ABSENTS :
 *   - widget-transferred-calls : compte `CALL_TERMINATION_REASON = 'CALL_TRANSFERRED'`, ce qui
 *     inclut les transferts faits PAR UN AGENT — ce n'est donc pas la catégorie `forwarded`
 *     (redirections du flow). Il descend sur la colonne `terminationReason`, pas sur `category`.
 *   - les widgets de taux (%), de moyenne et les graphes : pas de population d'appels unique.
 */
const CALL_DRILLDOWNS = {
    'widget-total-calls':        {},                                        // aucun filtre : tous les appels
    'widget-answered-calls':     { category: '=handled' },
    'widget-abandoned-calls':    { category: '=abandoned' },
    'widget-quick-drop-count':   { category: '=quick_drop' },
    'widget-ivr-abandons':       { category: '=ivr_abandon' },
    'widget-transferred-calls':  { terminationReason: '=CALL_TRANSFERRED' }
};

/** Libellés des catégories de la taxonomie (clé stable renvoyée par Data_Pipe → clé i18n). */
const CALL_CATEGORY_LABELS = {
    handled:     'CALL_CATEGORY_HANDLED',
    forwarded:   'CALL_CATEGORY_FORWARDED',
    quick_drop:  'CALL_CATEGORY_QUICK_DROP',
    abandoned:   'CALL_CATEGORY_ABANDONED',
    ivr_abandon: 'CALL_CATEGORY_IVR_ABANDON',
    // Le backend résout désormais les paires de rappel (resolveCallbacks) : un appel où le client
    // a accepté d'être rappelé ne sort plus en `abandoned`. Ces deux clés-là arrivent donc dans la
    // liste, et sans entrée ici elles s'affichaient telles quelles.
    callback_pending: 'CALL_CATEGORY_CALLBACK_PENDING',
    callback_failed:  'CALL_CATEGORY_CALLBACK_FAILED'
};

// Default widget positions for the Dashboard tab (lg: 12 cols) — Overview layout
const DEFAULT_WIDGET_POSITIONS = {
    // Ligne 1 — l'issue de l'appel : répondu / abandonné / abandonné au SVI
    "widget-total-calls":               { x: 0, y: 0, w: 3, h: 2 },
    "widget-answered-calls":            { x: 3, y: 0, w: 3, h: 2 },
    "widget-abandoned-calls":           { x: 6, y: 0, w: 3, h: 2 },
    "widget-ivr-abandons":              { x: 9, y: 0, w: 3, h: 2 },

    // Ligne 2 — la qualité de service
    "widget-service-level":             { x: 0, y: 2, w: 3, h: 2 },
    "widget-average-wait-time":         { x: 3, y: 2, w: 3, h: 2 },
    "widget-avg-abandon-time":          { x: 6, y: 2, w: 3, h: 2 },
    "widget-outbound-calls":            { x: 9, y: 2, w: 3, h: 2 },

    // Ligne 3 — les temps de traitement
    "widget-handle-time":               { x: 0, y: 4, w: 3, h: 2 },
    "widget-talk-time":                 { x: 3, y: 4, w: 3, h: 2 },
    "widget-avg-inbound-call-duration": { x: 6, y: 4, w: 3, h: 2 },
    "widget-avg-outbound-call-duration":{ x: 9, y: 4, w: 3, h: 2 },

    // Ligne 4 — tendance SLA + répartition des issues
    "widget-sla-trend":                 { x: 0, y: 6, w: 6, h: 4 },
    "widget-call-status-distribution":  { x: 6, y: 6, w: 6, h: 4 },

    // Ligne 5 — volume quotidien
    "widget-call-volume-daily":         { x: 0, y: 10, w: 12, h: 4 },

    // Ligne 6 — répartition des appels par pays
    "widget-calls-by-country":          { x: 0, y: 14, w: 6, h: 4 }
};

// Responsive positions for medium screens (md: 10 cols)
// Stack metrics vertically (2 per row x 5 cols), charts full width
const MD_WIDGET_POSITIONS = {
    // === ROW 1: Main Call Metrics (2 widgets x 5 cols) ===
    "widget-total-calls": { x: 0, y: 0, w: 5, h: 2 },
    "widget-answered-calls": { x: 5, y: 0, w: 5, h: 2 },

    // === ROW 2: More Call Metrics (2 widgets x 5 cols) ===
    "widget-abandoned-calls": { x: 0, y: 2, w: 5, h: 2 },
    "widget-outbound-calls": { x: 5, y: 2, w: 5, h: 2 },

    // === ROW 3: Performance Metrics (2 widgets x 5 cols) ===
    "widget-average-wait-time": { x: 0, y: 4, w: 5, h: 2 },
    "widget-avg-inbound-call-duration": { x: 5, y: 4, w: 5, h: 2 },

    // === ROW 4: More Performance Metrics (2 widgets x 5 cols) ===
    "widget-avg-outbound-call-duration": { x: 0, y: 6, w: 5, h: 2 },
    "widget-service-level": { x: 5, y: 6, w: 5, h: 2 },

    // === ROW 5: Additional Metrics (1 widget x 5 cols) ===
    "widget-transferred-calls": { x: 0, y: 8, w: 5, h: 2 },

    // === ROW 6: Charts (2 widgets x 5 cols = 10 cols) ===
    "widget-call-volume-hourly": { x: 0, y: 10, w: 5, h: 4 },
    "widget-call-status-distribution": { x: 5, y: 10, w: 5, h: 4 },

    // === ROW 7: Distribution Charts (2 widgets x 5 cols) ===
    "widget-calls-by-queue": { x: 0, y: 14, w: 5, h: 4 },
    "widget-calls-by-flow": { x: 5, y: 14, w: 5, h: 4 },
    "widget-calls-by-country": { x: 0, y: 18, w: 5, h: 4 },

    // === ROW 8: Agent Chart (1 widget full width) ===
    "widget-calls-per-agent": { x: 5, y: 18, w: 5, h: 4 },

    // === ROW 9+: Full width charts ===
    "widget-call-volume-daily": { x: 0, y: 22, w: 10, h: 4 },
    "widget-peak-hours": { x: 0, y: 26, w: 10, h: 5 },
    "widget-queue-kpi-summary": { x: 0, y: 31, w: 10, h: 6 },
    "widget-month-to-date-summary": { x: 0, y: 37, w: 10, h: 7 },
    "widget-calls-served-time-ranges": { x: 0, y: 44, w: 10, h: 5 },
    "widget-calls-abandoned-time-ranges": { x: 0, y: 49, w: 10, h: 5 },
    "widget-daily-sla-stats": { x: 0, y: 54, w: 10, h: 7 },
    "widget-sla-trend": { x: 0, y: 61, w: 10, h: 5 },
    "widget-daily-abandon-stats": { x: 0, y: 66, w: 10, h: 10 }
};

// Responsive positions for small screens (sm: 6 cols)
// 2 widgets per row (3 cols each) for metrics, full width for charts
const SM_WIDGET_POSITIONS = {
    // Metrics (2 per row)
    "widget-total-calls": { x: 0, y: 0, w: 3, h: 2 },
    "widget-answered-calls": { x: 3, y: 0, w: 3, h: 2 },
    "widget-abandoned-calls": { x: 0, y: 2, w: 3, h: 2 },
    "widget-outbound-calls": { x: 3, y: 2, w: 3, h: 2 },
    "widget-average-wait-time": { x: 0, y: 4, w: 3, h: 2 },
    "widget-avg-inbound-call-duration": { x: 3, y: 4, w: 3, h: 2 },
    "widget-avg-outbound-call-duration": { x: 0, y: 6, w: 3, h: 2 },
    "widget-service-level": { x: 3, y: 6, w: 3, h: 2 },
    "widget-transferred-calls": { x: 0, y: 8, w: 3, h: 2 },

    // Charts (full width)
    "widget-call-volume-hourly": { x: 0, y: 10, w: 6, h: 4 },
    "widget-call-status-distribution": { x: 0, y: 14, w: 6, h: 4 },
    "widget-calls-by-queue": { x: 0, y: 18, w: 6, h: 4 },
    "widget-calls-by-flow": { x: 0, y: 22, w: 6, h: 4 },
    "widget-calls-per-agent": { x: 0, y: 26, w: 6, h: 4 },
    "widget-call-volume-daily": { x: 0, y: 30, w: 6, h: 4 },
    "widget-peak-hours": { x: 0, y: 34, w: 6, h: 5 },
    "widget-queue-kpi-summary": { x: 0, y: 39, w: 6, h: 6 },
    "widget-month-to-date-summary": { x: 0, y: 45, w: 6, h: 7 },
    "widget-calls-served-time-ranges": { x: 0, y: 52, w: 6, h: 5 },
    "widget-calls-abandoned-time-ranges": { x: 0, y: 57, w: 6, h: 5 },
    "widget-daily-sla-stats": { x: 0, y: 62, w: 6, h: 7 },
    "widget-sla-trend": { x: 0, y: 69, w: 6, h: 5 },
    "widget-daily-abandon-stats": { x: 0, y: 74, w: 6, h: 10 },
    "widget-calls-by-country": { x: 0, y: 84, w: 6, h: 4 }
};

// Responsive positions for extra small screens (xs: 4 cols)
// 2 widgets per row (2 cols each) for metrics, full width for charts
const XS_WIDGET_POSITIONS = {
    // Metrics (2 per row)
    "widget-total-calls": { x: 0, y: 0, w: 2, h: 2 },
    "widget-answered-calls": { x: 2, y: 0, w: 2, h: 2 },
    "widget-abandoned-calls": { x: 0, y: 2, w: 2, h: 2 },
    "widget-outbound-calls": { x: 2, y: 2, w: 2, h: 2 },
    "widget-average-wait-time": { x: 0, y: 4, w: 2, h: 2 },
    "widget-avg-inbound-call-duration": { x: 2, y: 4, w: 2, h: 2 },
    "widget-avg-outbound-call-duration": { x: 0, y: 6, w: 2, h: 2 },
    "widget-service-level": { x: 2, y: 6, w: 2, h: 2 },
    "widget-transferred-calls": { x: 0, y: 8, w: 2, h: 2 },

    // Charts (full width)
    "widget-call-volume-hourly": { x: 0, y: 10, w: 4, h: 4 },
    "widget-call-status-distribution": { x: 0, y: 14, w: 4, h: 4 },
    "widget-calls-by-queue": { x: 0, y: 18, w: 4, h: 4 },
    "widget-calls-by-flow": { x: 0, y: 22, w: 4, h: 4 },
    "widget-calls-per-agent": { x: 0, y: 26, w: 4, h: 4 },
    "widget-call-volume-daily": { x: 0, y: 30, w: 4, h: 4 },
    "widget-peak-hours": { x: 0, y: 34, w: 4, h: 5 },
    "widget-queue-kpi-summary": { x: 0, y: 39, w: 4, h: 6 },
    "widget-month-to-date-summary": { x: 0, y: 45, w: 4, h: 7 },
    "widget-calls-served-time-ranges": { x: 0, y: 52, w: 4, h: 5 },
    "widget-calls-abandoned-time-ranges": { x: 0, y: 57, w: 4, h: 5 },
    "widget-daily-sla-stats": { x: 0, y: 62, w: 4, h: 7 },
    "widget-sla-trend": { x: 0, y: 69, w: 4, h: 5 },
    "widget-daily-abandon-stats": { x: 0, y: 74, w: 4, h: 10 },
    "widget-calls-by-country": { x: 0, y: 84, w: 4, h: 4 }
};

// Responsive positions for mobile (xxs: 2 cols)
// Full width vertical stack
const XXS_WIDGET_POSITIONS = {
    // Metrics (stacked vertically, full width)
    "widget-total-calls": { x: 0, y: 0, w: 2, h: 2 },
    "widget-answered-calls": { x: 0, y: 2, w: 2, h: 2 },
    "widget-abandoned-calls": { x: 0, y: 4, w: 2, h: 2 },
    "widget-outbound-calls": { x: 0, y: 6, w: 2, h: 2 },
    "widget-average-wait-time": { x: 0, y: 8, w: 2, h: 2 },
    "widget-avg-inbound-call-duration": { x: 0, y: 10, w: 2, h: 2 },
    "widget-avg-outbound-call-duration": { x: 0, y: 12, w: 2, h: 2 },
    "widget-service-level": { x: 0, y: 14, w: 2, h: 2 },
    "widget-transferred-calls": { x: 0, y: 16, w: 2, h: 2 },

    // Charts (full width)
    "widget-call-volume-hourly": { x: 0, y: 18, w: 2, h: 4 },
    "widget-call-status-distribution": { x: 0, y: 22, w: 2, h: 4 },
    "widget-calls-by-queue": { x: 0, y: 26, w: 2, h: 4 },
    "widget-calls-by-flow": { x: 0, y: 30, w: 2, h: 4 },
    "widget-calls-per-agent": { x: 0, y: 34, w: 2, h: 4 },
    "widget-call-volume-daily": { x: 0, y: 38, w: 2, h: 4 },
    "widget-peak-hours": { x: 0, y: 42, w: 2, h: 5 },
    "widget-queue-kpi-summary": { x: 0, y: 47, w: 2, h: 6 },
    "widget-month-to-date-summary": { x: 0, y: 53, w: 2, h: 7 },
    "widget-calls-served-time-ranges": { x: 0, y: 60, w: 2, h: 5 },
    "widget-calls-abandoned-time-ranges": { x: 0, y: 65, w: 2, h: 5 },
    "widget-daily-sla-stats": { x: 0, y: 70, w: 2, h: 7 },
    "widget-sla-trend": { x: 0, y: 77, w: 2, h: 5 },
    "widget-daily-abandon-stats": { x: 0, y: 82, w: 2, h: 10 },
    "widget-calls-by-country": { x: 0, y: 92, w: 2, h: 4 }
};

// Tabs système par défaut, visibles pour TOUS les users (en plus de Dashboard et Calls List).
// Injectés au chargement si absents (migration auto pour profils existants) ET dans
// initializeDefaultLayout pour les nouveaux profils. Type "system" pour les distinguer.
const DEFAULT_SYSTEM_TABS = {
    sla: {
        displayName: "Service Level & SLA",
        type: "system",
        widgets: {
            "sla-service-level":     { widgetId: "widget-service-level" },
            "sla-waiting-time":      { widgetId: "widget-waiting-time" },
            "sla-talking-time":      { widgetId: "widget-talking-time" },
            "sla-abandon-time":      { widgetId: "widget-abandon-time" },
            "sla-trend":             { widgetId: "widget-sla-trend" },
            "sla-thresholds-dist":   { widgetId: "widget-thresholds-distribution" },
            "sla-daily-table":       { widgetId: "widget-daily-sla-stats" },
            "sla-served-ranges":     { widgetId: "widget-calls-served-time-ranges" }
        },
        widgetsPositions: {
            "sla-service-level":   { x: 0, y: 0,  w: 3, h: 2 },
            "sla-waiting-time":    { x: 3, y: 0,  w: 3, h: 2 },
            "sla-talking-time":    { x: 6, y: 0,  w: 3, h: 2 },
            "sla-abandon-time":    { x: 9, y: 0,  w: 3, h: 2 },
            "sla-trend":           { x: 0, y: 2,  w: 6, h: 4 },
            "sla-thresholds-dist": { x: 6, y: 2,  w: 6, h: 4 },
            "sla-daily-table":     { x: 0, y: 6,  w: 12, h: 6 },
            "sla-served-ranges":   { x: 0, y: 12, w: 12, h: 5 }
        }
    },
    abandons: {
        displayName: "Abandons & Qualité",
        type: "system",
        widgets: {
            "ab-abandoned":      { widgetId: "widget-abandoned-calls" },
            "ab-abandon-time":   { widgetId: "widget-abandon-time" },
            "ab-waiting-time":   { widgetId: "widget-waiting-time" },
            "ab-transferred":    { widgetId: "widget-transferred-calls" },
            "ab-daily-table":    { widgetId: "widget-daily-abandon-stats" },
            "ab-aband-ranges":   { widgetId: "widget-calls-abandoned-time-ranges" },
            "ab-served-ranges":  { widgetId: "widget-calls-served-time-ranges" }
        },
        widgetsPositions: {
            "ab-abandoned":     { x: 0, y: 0,  w: 3, h: 2 },
            "ab-abandon-time":  { x: 3, y: 0,  w: 3, h: 2 },
            "ab-waiting-time":  { x: 6, y: 0,  w: 3, h: 2 },
            "ab-transferred":   { x: 9, y: 0,  w: 3, h: 2 },
            "ab-daily-table":   { x: 0, y: 2,  w: 12, h: 10 },
            "ab-aband-ranges":  { x: 0, y: 12, w: 12, h: 5 },
            "ab-served-ranges": { x: 0, y: 17, w: 12, h: 7 }
        }
    },
    flows: {
        displayName: "Flows, Queues & Agents",
        type: "system",
        widgets: {
            "fl-by-flow":     { widgetId: "widget-calls-by-flow" },
            "fl-by-queue":    { widgetId: "widget-calls-by-queue" },
            "fl-per-agent":   { widgetId: "widget-calls-per-agent" },
            "fl-kpi-summary": { widgetId: "widget-queue-kpi-summary" },
            "fl-mtd-summary": { widgetId: "widget-month-to-date-summary" }
        },
        widgetsPositions: {
            "fl-by-flow":     { x: 0, y: 0,  w: 6, h: 4 },
            "fl-by-queue":    { x: 6, y: 0,  w: 6, h: 4 },
            "fl-per-agent":   { x: 0, y: 4,  w: 12, h: 5 },
            "fl-kpi-summary": { x: 0, y: 9,  w: 12, h: 6 },
            "fl-mtd-summary": { x: 0, y: 15, w: 12, h: 7 }
        }
    },
    trends: {
        displayName: "Volume & Trends",
        type: "system",
        widgets: {
            "tr-volume-hourly": { widgetId: "widget-call-volume-hourly" },
            "tr-volume-daily":  { widgetId: "widget-call-volume-daily" },
            "tr-peak-hours":    { widgetId: "widget-peak-hours" }
        },
        widgetsPositions: {
            "tr-volume-hourly": { x: 0, y: 0, w: 12, h: 4 },
            "tr-volume-daily":  { x: 0, y: 4, w: 12, h: 4 },
            "tr-peak-hours":    { x: 0, y: 8, w: 12, h: 5 }
        }
    }
};

// Tailles minimales raisonnables par type de widget pour éviter les widgets
// minuscules après resize manuel ou les KPI compressés.
const getMinSizeForType = (type) => {
    switch (type) {
        case WidgetTypes.METRIC: return { minW: 2, minH: 3 };
        case WidgetTypes.DONUT: return { minW: 3, minH: 3 };
        case WidgetTypes.LINE:
        case WidgetTypes.BAR: return { minW: 4, minH: 3 };
        case WidgetTypes.TABLE: return { minW: 4, minH: 4 };
        default: return { minW: 2, minH: 2 };
    }
};


/**
 * Analytics dashboard for calls
 * Uses inboundCallsDashboard configuration to display widgets
 */
// Formate un timestamp (epoch secondes) dans le fuseau sélectionné + la locale UI.
const formatEpochTz = (epochSec, timeZone, locale) => {
    if (!epochSec) return '—';
    const d = new Date(Number(epochSec) * 1000);
    if (Number.isNaN(d.getTime())) return '—';
    try {
        return d.toLocaleString(locale || undefined, {
            timeZone: timeZone || undefined,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
        });
    } catch (_) {
        return d.toLocaleString();
    }
};

const AnalyticsDashboard = ({ tenantId, token }) => {
    const { t, i18n } = useTranslation();
    const dialogStyles = useDialogStyles();
    const { showSuccess, showError } = useNotification();

    // Get scope from URL
    const { scope } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const decodedScope = decodeURIComponent(scope || 'default');

    // Récupérer les scopes depuis Redux
    const scopesData = useSelector(state => state.reducer.SCOPES);
    const analyticsProfiles = useSelector(state => state.reducer.ANALYTICS_PROFILES);
    // Annuaire agents (pour résoudre l'id superviseur → nom dans le widget supervision).
    const agentsList = useSelector(state => state.reducer.AGENTS);
    const supervisedAgentDetails = useSelector(state => state.reducer.supervisedAgentDetails);
    // Files PROVISIONNÉES de l'entité. Les tableaux « par file » (overflow-by-reason) ont besoin
    // de cette liste pour afficher une colonne à zéro sur une file sans débordement : côté
    // backend, seuls les CDR sont disponibles, et ils listent toutes les files ayant existé un
    // jour (files de test supprimées, anciens libellés après renommage) tout en ratant les files
    // récentes sans trafic. On envoie donc la liste de configuration avec la requête.
    // `queueName` est la même valeur que celle comparée à QUEUE_NAME_1 côté CDR (cf. DashboardFilters).
    const queuesList = useSelector(state => state.reducer.QUEUES);
    // ⚠️ InitComponent charge files, flows et agents IA pour TOUS les scopes d'un coup et les
    // fusionne dans un seul objet Redux. Il faut donc re-filtrer sur le scope de la page, sinon
    // les tableaux d'une entité se retrouvent avec les colonnes des autres entités du tenant.
    //
    // Un objet sans champ `scope` appartient à "default" — même règle que la liste des call flows
    // (`flow.scope || "default"`) et que les options du filtre (cf. DashboardFilters). L'ancienne
    // version les gardait dans TOUS les scopes : sur le rapport d'une entité, les flows de
    // "default" revenaient en colonnes.
    const isCurrentScope = React.useCallback(
        (e) => String(e?.scope || 'default') === String(decodedScope || 'default'),
        [decodedScope]
    );
    const provisionedQueueNames = React.useMemo(() => {
        const arr = Array.isArray(queuesList) ? queuesList : (queuesList ? Object.values(queuesList) : []);
        return arr.filter(isCurrentScope)
            .map(q => q?.queueName || q?.displayName || q?.name)
            .filter(Boolean);
    }, [queuesList, isCurrentScope]);
    // Flows PROVISIONNÉS de l'entité, même rôle que `provisionedQueueNames` pour les tableaux
    // « par flow » (Flow KPI Summary, Month-to-Date, tranches servis/abandons). Les agents IA en
    // font partie : côté CDR ce sont des flows (FLOW_ID = id de l'agent IA), comme dans le filtre.
    // On garde le couple id → nom : le filtre porte sur l'ID (FLOW_ID) alors que les colonnes
    // sont des NOMS (FLOW_NAME) ; sans cette table, une sélection ne peut pas être traduite en
    // liste de colonnes. Le nom d'un call flow est `displayName` (cf. ListCallFlowsComponent) —
    // c'est cette valeur que FlowEngine écrit dans FLOW_NAME.
    const callFlowsList = useSelector(state => state.reducer.CALL_FLOWS);
    const agentAIsList = useSelector(state => state.reducer.AGENT_AIS);
    const provisionedFlows = React.useMemo(() => {
        const toArray = (d) => (Array.isArray(d) ? d : (d ? Object.values(d) : []));
        const flows = toArray(callFlowsList).filter(isCurrentScope).map(f => ({
            id: String(f?.flowId || f?.id || f?.flowKey || ''),
            name: f?.displayName || f?.flowName || f?.name || ''
        }));
        const ias = toArray(agentAIsList).filter(isCurrentScope).map(a => ({
            id: String(a?.id || ''),
            name: a?.displayName || a?.agentPersonality?.name || ''
        }));
        return [...flows, ...ias].filter(f => f.id && f.name);
    }, [callFlowsList, agentAIsList, isCurrentScope]);
    // Agents PROVISIONNÉS de l'entité, avec les NOMS de leurs files. Même rôle que
    // `provisionedQueueNames` mais pour les lignes du tableau « Agent Status Statistics ».
    // Les files sont stockées par ID sur l'agent (`agentQueueIds`) alors que le tableau groupe
    // par NOM de file (QUEUE_NAME_1 côté CDR) : d'où la table de traduction.
    const queueNameById = React.useMemo(() => {
        const arr = Array.isArray(queuesList) ? queuesList : (queuesList ? Object.values(queuesList) : []);
        const m = {};
        arr.forEach(q => {
            const id = String(q?.id || '');
            const name = q?.queueName || q?.displayName || q?.name;
            if (id && name) m[id] = name;
        });
        return m;
    }, [queuesList]);
    const provisionedAgents = React.useMemo(() => {
        const arr = Array.isArray(agentsList) ? agentsList : (agentsList ? Object.values(agentsList) : []);
        return arr.filter(isCurrentScope).map(a => ({
            id: String(a?.id || ''),
            name: a?.displayName || a?.name || '',
            // Seules les files de l'entité courante sont retenues : un id inconnu de la table
            // est soit une file d'une autre entité, soit une file supprimée.
            queues: (Array.isArray(a?.agentQueueIds) ? a.agentQueueIds : [])
                .map(qid => queueNameById[String(qid)])
                .filter(Boolean)
        })).filter(a => a.id);
    }, [agentsList, isCurrentScope, queueNameById]);
    // Annuaire id → nom SANS filtre de scope : sert uniquement à NOMMER une sélection
    // enregistrée qui pointe hors de l'entité (pastille de filtres du widget). On ne veut pas
    // afficher un GUID à l'utilisateur juste parce que le flow appartient à une autre entité.
    const allFlowNameById = React.useMemo(() => {
        const toArray = (d) => (Array.isArray(d) ? d : (d ? Object.values(d) : []));
        const m = {};
        toArray(callFlowsList).forEach(f => {
            const id = String(f?.flowId || f?.id || f?.flowKey || '');
            const name = f?.displayName || f?.flowName || f?.name || '';
            if (id && name) m[id] = name;
        });
        toArray(agentAIsList).forEach(a => {
            const id = String(a?.id || '');
            const name = a?.displayName || a?.agentPersonality?.name || '';
            if (id && name) m[id] = `🤖 ${name}`;
        });
        return m;
    }, [callFlowsList, agentAIsList]);

    // Listes de colonnes à joindre à une requête, restreintes à la sélection courante : avec un
    // filtre flow actif, le tableau ne doit montrer que les flows sélectionnés (chacun à zéro s'il
    // n'a pas de trafic), pas l'entité entière. Sans sélection → toute l'entité.
    const buildColumnFilters = React.useCallback((queryId, filters) => {
        const out = {};
        // Une sélection peut arriver en tableau OU en valeur simple (les deux formes circulent
        // jusqu'à buildWhereClause). Traitée comme « pas de sélection », une valeur simple
        // laissait le tableau afficher TOUTE l'entité alors que la donnée, elle, était filtrée
        // sur un seul flow : des dizaines de colonnes à zéro autour de la seule qui compte.
        const asSelection = (v) => [
            ...new Set((Array.isArray(v) ? v : (v === undefined || v === null || v === '' ? [] : [v])).map(String))
        ];

        if (FLOW_COLUMN_QUERIES.includes(queryId)) {
            const selected = asSelection(filters?.flowKey);
            const names = (selected.length > 0
                ? provisionedFlows.filter(f => selected.includes(f.id))
                : provisionedFlows
            ).map(f => f.name);
            if (names.length > 0) out.flowColumns = names;
            // Diagnostic : sans cette liste, le backend retombe sur les flows VUS EN CDR — donc
            // les flows morts apparaissent et les flows récents sans trafic manquent.
            console.log(
                `📊 ${queryId}: ${names.length} colonne(s) flow envoyée(s)`,
                { provisionnes: provisionedFlows.length, selection: selected.length, colonnes: names }
            );
            // Sélection enregistrée pointant sur des flows ÉTRANGERS à l'entité (ou supprimés) :
            // le widget filtre alors sur des flows qui n'appartiennent pas au rapport, et le
            // tableau se réduit à ce que les CDR de ces flows contiennent. Cas vu en vrai — un
            // widget d'un onglet de `default` filtré sur deux flows de l'entité UK, la sélection
            // ayant été faite quand le filtre proposait encore toutes les entités du tenant.
            if (selected.length > 0 && names.length === 0) {
                console.warn(
                    `⚠️ ${queryId}: filtre flow enregistré sur ${selected.length} flow(s) inconnu(s) de l'entité — `
                    + `configuration du widget à revoir`, selected
                );
            }
        }
        if (QUEUE_COLUMN_QUERIES.includes(queryId)) {
            const selected = asSelection(filters?.queueKey);
            // Le filtre file porte déjà sur le NOM de la file (cf. DashboardFilters).
            const names = selected.length > 0
                ? provisionedQueueNames.filter(n => selected.includes(String(n)))
                : provisionedQueueNames;
            if (names.length > 0) out.queueColumns = names;
            console.log(
                `📊 ${queryId}: ${names.length} colonne(s) file envoyée(s)`,
                { provisionnees: provisionedQueueNames.length, selection: selected.length, colonnes: names }
            );
        }
        if (AGENT_ROW_QUERIES.includes(queryId)) {
            const selectedAgents = asSelection(filters?.agentKey);
            // Le filtre FILE doit restreindre les lignes d'agents comme il restreint la donnée.
            // Sans ça, filtrer sur une file laissait remonter tous les agents de l'entité, y
            // compris ceux qui n'appartiennent pas à la file demandée — vu en prod : 33 agents
            // affichés sous un filtre « 1 file ». Le filtre porte sur le NOM de la file
            // (cf. DashboardFilters), soit la même valeur que `a.queues`.
            const selectedQueues = asSelection(filters?.queueKey);
            const rows = provisionedAgents
                .filter(a => selectedAgents.length === 0 || selectedAgents.includes(a.id))
                .map(a => (selectedQueues.length === 0
                    ? a
                    : { ...a, queues: a.queues.filter(q => selectedQueues.includes(String(q))) }))
                // Une ligne n'existe que pour une file : un agent qui n'en a aucune — soit parce
                // qu'il n'est rattaché nulle part, soit parce qu'aucune des siennes n'est
                // sélectionnée — n'a rien à envoyer.
                .filter(a => a.queues.length > 0);
            if (rows.length > 0) out.agentRows = rows;
            console.log(
                `📊 ${queryId}: ${rows.length} ligne(s) agent envoyée(s)`,
                {
                    provisionnes: provisionedAgents.length,
                    sansFile: provisionedAgents.filter(a => a.queues.length === 0).length,
                    selectionAgents: selectedAgents.length,
                    selectionFiles: selectedQueues.length
                }
            );
        }
        return out;
    }, [provisionedFlows, provisionedQueueNames, provisionedAgents]);
    const supervisorNameById = React.useMemo(() => {
        const m = {};
        (Array.isArray(agentsList) ? agentsList : []).forEach(a => { if (a?.id) m[a.id] = a.displayName || a.name || a.id; });
        if (supervisedAgentDetails && typeof supervisedAgentDetails === 'object') {
            Object.entries(supervisedAgentDetails).forEach(([id, d]) => { if (!m[id] && d?.displayName) m[id] = d.displayName; });
        }
        return m;
    }, [agentsList, supervisedAgentDetails]);
    // Résolution agentId -> nom pour la Calls List : on se base UNIQUEMENT sur la liste
    // AGENTS complète (Redux), pas les supervised. L'id du CDR OBO est l'AAD userId ; on
    // indexe donc sur tous les champs id plausibles pour être robuste au nom du champ.
    const agentNameById = React.useMemo(() => {
        const m = {};
        (Array.isArray(agentsList) ? agentsList : []).forEach(a => {
            const name = a?.displayName || a?.name;
            if (!name) return;
            [a.id, a.aadId, a.userId, a.objectId, a.principalId, a.RowKey].forEach(k => {
                if (k) m[String(k).toLowerCase()] = name;
            });
        });
        return m;
    }, [agentsList]);
    // Remplace l'id superviseur par son nom dans les données du widget supervision-summary.
    const resolveSupervisorNames = (widget, data) => {
        if (widget?.queryId !== 'supervision-summary' || !Array.isArray(data)) return data;
        return data.map(row => ({ ...row, supervisorName: supervisorNameById[row.supervisorName] || row.supervisorName }));
    };

    // État pour les filtres de date
    const [filterValues, setFilterValues] = useState({
        startDate: null,
        endDate: null
    });

    // État pour le timezone sélectionné (défaut: timezone du navigateur)
    const [selectedTimezone, setSelectedTimezone] = useState(
        Intl.DateTimeFormat().resolvedOptions().timeZone
    );

    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    /**
     * TOUS les fuseaux IANA, à plat, chacun avec son décalage courant.
     *
     * La liste d'origine en comptait seize, écrites en dur. Un client hors de cette liste —
     * Lisbonne, Tunis, Casablanca, Bucarest — n'avait aucun moyen de lire son rapport à l'heure
     * de ses agents, et rien à l'écran ne lui disait pourquoi.
     *
     * Pas de section « fréquents » : toute liste de raccourcis désigne un client principal.
     * Celle-ci était américaine ; un superviseur européen y lisait qu'il n'était pas le sujet,
     * et l'inverse aurait été vrai en la rendant européenne. La recherche ne prend parti pour
     * personne.
     *
     * `Intl.supportedValuesOf('timeZone')` en donne 418. Ce n'est pas 24 : il y a 37 décalages
     * distincts, de UTC−11 à UTC+14, avec des demi-heures (Inde +5:30) et des quarts d'heure
     * (Népal +5:45, Chatham +12:45).
     *
     * Le décalage AFFICHÉ est celui d'aujourd'hui, heure d'été comprise : « Europe/Paris » vaut
     * UTC+02:00 en août et UTC+01:00 en janvier. C'est bien le nom IANA qui est enregistré, pas
     * le décalage — sans quoi un rapport de janvier consulté en août serait décalé d'une heure.
     *
     * L'API n'existe pas avant Chrome 99 / Safari 15.4 : repli sur le seul fuseau du navigateur,
     * qui reste sélectionné et fonctionnel.
     */
    const TIMEZONE_OPTIONS = React.useMemo(() => {
        // Décalage courant, en « +02:00 ».
        //
        // La référence est ancrée sur la MINUTE PLEINE : les parties formatées s'arrêtent à la
        // minute, donc comparer à un `Date.now()` qui porte des secondes perd jusqu'à 59 s dans
        // la soustraction — d'où « UTC+00:59 » pour Lisbonne et « UTC+12:44 » pour Chatham.
        const offsetLabel = (tz) => {
            try {
                const now = Math.floor(Date.now() / 60000) * 60000;
                const p = {};
                new Intl.DateTimeFormat('en-US', { timeZone: tz, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
                    .formatToParts(new Date(now)).forEach(x => { p[x.type] = x.value; });
                const hh = p.hour === '24' ? '00' : p.hour;
                const mins = Math.round((Date.UTC(+p.year, +p.month - 1, +p.day, +hh, +p.minute) - now) / 60000);
                const sign = mins < 0 ? '-' : '+';
                const a = Math.abs(mins);
                return `UTC${sign}${String(Math.floor(a / 60)).padStart(2, '0')}:${String(a % 60).padStart(2, '0')}`;
            } catch (e) { return 'UTC'; }
        };

        let all = [];
        try {
            all = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
        } catch (e) { /* navigateur trop ancien */ }
        if (all.length === 0) all = [browserTimezone];
        if (!all.includes(browserTimezone)) all = [browserTimezone, ...all];

        return all.map((tz) => ({
            // Nom IANA COMPLET — « Europe/Paris », pas « Paris ». C'est la valeur réellement
            // enregistrée et envoyée au back-end, et c'est aussi ce sur quoi porte la recherche :
            // taper « euro » doit ramener l'Europe entière.
            key: tz, text: `${tz.replace(/_/g, ' ')} (${offsetLabel(tz)})`
        })).sort((a, b) => a.text.localeCompare(b.text));
    }, [browserTimezone]);

    // Contenu du champ. Il porte la SÉLECTION COURANTE, pas un placeholder : écrit pour de bon,
    // il devient modifiable — on efface « Lisbon » de « Europe/Lisbon » et la liste se rabat sur
    // l'Europe. C'est le geste qu'appelle un nom hiérarchique.
    const [timezoneQuery, setTimezoneQuery] = useState(
        () => Intl.DateTimeFormat().resolvedOptions().timeZone
    );
    const filteredTimezones = React.useMemo(() => {
        const q = timezoneQuery.trim().toLowerCase();
        // Tant que le champ contient exactement la sélection, on n'a rien cherché : filtrer
        // dessus ne laisserait qu'un résultat, celui déjà choisi, et rouvrir la liste pour en
        // changer deviendrait impossible sans tout effacer d'abord.
        if (!q || q === selectedTimezone.toLowerCase()) return TIMEZONE_OPTIONS;
        return TIMEZONE_OPTIONS.filter(o => o.key.toLowerCase().includes(q) || o.text.toLowerCase().includes(q));
    }, [timezoneQuery, TIMEZONE_OPTIONS, selectedTimezone]);

    // État pour les filtres appliqués (déclenchent le rafraîchissement des widgets)
    const [appliedFilters, setAppliedFilters] = useState(null);

    // Le sélecteur de timezone doit agir immédiatement, sans re-cliquer "Apply".
    // Les widgets à filtres globaux lisent appliedFilters → on y propage la tz live.
    // (Le changement d'appliedFilters re-déclenche les effets de fetch.)
    useEffect(() => {
        setAppliedFilters(prev => (prev ? { ...prev, timezone: selectedTimezone } : prev));
    }, [selectedTimezone]);

    // État pour les filtres dynamiques (flows, queues, agents)
    const [dynamicFilters, setDynamicFilters] = useState({});

    // État pour l'export PDF
    const [isExporting, setIsExporting] = useState(false);

    // État pour le layout du dashboard
    const [layouts, setLayouts] = useState(null);

    // État pour afficher/masquer les filtres
    const [showFilters, setShowFilters] = useState(false);

    // État pour le mode édition (déplacement des widgets)
    const [isEditMode, setIsEditMode] = useState(false);

    // États pour éviter les réinitialisations pendant drag/resize
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);

    // État pour les données pré-chargées des widgets
    const [widgetsData, setWidgetsData] = useState({});
    const [isLoadingWidgets, setIsLoadingWidgets] = useState(false);

    // État pour l'onglet actif principal (Dashboard ou Calls List)
    const [selectedTab, setSelectedTab] = useState('dashboard');

    // État pour la liste des appels
    const [callsList, setCallsList] = useState([]);
    const [isLoadingCalls, setIsLoadingCalls] = useState(false);

    // Annuaire agentId -> nom dérivé des lignes DÉJÀ chargées (les OBO sortants n'ont pas
    // d'AGENT_DISPLAYNAME, mais le même agent apparaît nommé sur ses appels entrants du
    // même jeu de résultats). Fiable et indépendant du chargement Redux.
    // Le backend renvoie "—" (tiret) comme placeholder "pas de nom" : à traiter comme vide.
    const isBlankName = (v) => {
        if (!v) return true;
        const s = String(v).trim();
        return s === '' || s === '—' || s === '–' || s === '-';
    };
    const agentNameFromCalls = React.useMemo(() => {
        const m = {};
        (Array.isArray(callsList) ? callsList : []).forEach(c => {
            if (c?.agentId && !isBlankName(c?.agentName)) m[String(c.agentId).toLowerCase()] = c.agentName;
        });
        return m;
    }, [callsList]);
    // Résout le nom de l'agent d'une ligne : CDR -> liste AGENTS complète -> lignes sœurs -> supervised.
    const resolveAgentName = React.useCallback((item) => {
        if (!isBlankName(item?.agentName)) return item.agentName;
        const id = item?.agentId;
        if (!id) return item?.agentName || '';
        const key = String(id).toLowerCase();
        const hit = agentNameById[key] || agentNameFromCalls[key] || supervisorNameById[id];
        if (!hit && process.env.NODE_ENV !== 'production') {
            console.warn('[CallsList] agent non résolu', { agentId: id, agentsLoaded: Array.isArray(agentsList) ? agentsList.length : 0, callsIndexed: Object.keys(agentNameFromCalls).length });
        }
        return hit || item?.agentName || '';
    }, [agentNameById, agentNameFromCalls, supervisorNameById, agentsList]);

    // États pour les filtres de colonnes du Calls List
    const [callsFilters, setCallsFilters] = useState({});
    const [callsFilterVisible, setCallsFilterVisible] = useState({});
    // Widget d'origine quand la liste a été ouverte par drill-down (null sinon) : sert
    // uniquement à afficher le bandeau « filtré depuis … » et son bouton d'annulation.
    const [drillDownSource, setDrillDownSource] = useState(null);
    const tableContainerRef = useRef(null);
    const scrollPositionRef = useRef(0);
    const [expandedRows, setExpandedRows] = useState({});
    const [selectedTranscription, setSelectedTranscription] = useState(null);
    const [showTranscriptionPanel, setShowTranscriptionPanel] = useState(false);
    const [recordingUrls, setRecordingUrls] = useState({}); // Store recording URLs by callId
    const [transcriptions, setTranscriptions] = useState({}); // Store transcriptions by callId

    // État pour l'export Excel
    const [isExportingExcel, setIsExportingExcel] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    const [exportMessage, setExportMessage] = useState('');
    const [showExportDialog, setShowExportDialog] = useState(false);

    // PDF export progress state — feeds the blocking dialog shown during export
    const [pdfExportProgress, setPdfExportProgress] = useState({ current: 0, total: 0, tabName: '' });
    const [exportDownloadUrl, setExportDownloadUrl] = useState(null);

    // Ref pour suivre l'état précédent du mode édition
    const wasEditModeRef = useRef(false);

    // Ref pour tracker si on vient de charger depuis Cosmos (ne pas réinitialiser pendant édition)
    const isLoadingFromCosmosRef = useRef(false);
    
    // Ref pour tracker si on vient de sauvegarder (éviter rechargement complet)
    const isSavingWidgetRef = useRef(false);
    
    // Ref pour toujours avoir les valeurs à jour de widgetInstances (éviter closure stale)
    const widgetInstancesRef = useRef({});

    // Clé du dernier load déclenché — empêche les refetches en cascade quand les refs
    // upstream (analyticsProfiles...) changent sans que les vrais inputs
    // des queries (filtres/scope/tab) aient changé.
    const lastLoadKeyRef = useRef(null);

    // Drapeau: a-t-on déjà consommé le cache prefetch pour ce scope ? Évite de réutiliser
    // le cache après que l'utilisateur ait changé les filtres ou rechargé manuellement.
    const usedPrefetchRef = useRef(false);
    useEffect(() => { usedPrefetchRef.current = false; }, [decodedScope]);

    // Cache prefetch chargé depuis /analytics (ScopeListComponent)
    const analyticsPrefetch = useSelector(state => state.reducer.ANALYTICS_PREFETCH);

    // Dans Redux, SCOPES contient { scopeName: scopeUUID }
    const scopeUUID = scopesData?.[decodedScope];

    // Tabs personnalisés (en plus de Dashboard et Calls List)
    const [customTabs, setCustomTabs] = useState([]);

    // Instances de widgets pour les tabs personnalisés uniquement
    const [widgetInstances, setWidgetInstances] = useState({});
    const [widgetInstancesLoaded, setWidgetInstancesLoaded] = useState(false);  // Flag pour éviter race condition

    // Positions des widgets par tab: {tabId: {instanceId: {x, y, w, h}}}
    const [tabWidgetsPositions, setTabWidgetsPositions] = useState({});

    // Nouveau: Layout state pour chaque tab avec react-grid-layout
    const [tabLayouts, setTabLayouts] = useState({});

    // Dialog states
    const [isCatalogOpen, setIsCatalogOpen] = useState(false);
    const [isTabManagerOpen, setIsTabManagerOpen] = useState(false);
    const [isInstanceConfigOpen, setIsInstanceConfigOpen] = useState(false);
    const [selectedWidgetInfo, setSelectedWidgetInfo] = useState(null);
    const [editingInstance, setEditingInstance] = useState(null);
    const [isAddTabDialogOpen, setIsAddTabDialogOpen] = useState(false);
    const [newTabName, setNewTabName] = useState('');
    const [deleteTabConfirm, setDeleteTabConfirm] = useState({ open: false, tabId: null, tabName: '' });

    // New states for the redesigned catalog/config panels
    const [showCatalogPanel, setShowCatalogPanel] = useState(false);
    const [catalogInitiallyCollapsed, setCatalogInitiallyCollapsed] = useState(true);
    const [showConfigPanel, setShowConfigPanel] = useState(false);
    const [selectedWidget, setSelectedWidget] = useState(null);
    const [draggedWidget, setDraggedWidget] = useState(null);
    const [dropZoneActive, setDropZoneActive] = useState(false);

    // Récupérer le dashboard actif - NE PAS filtrer les widgets ici
    const dashboardConfig = React.useMemo(() => {
        const config = inboundCallsDashboard;

        // ✅ Retourner TOUS les widgets sans filtrage
        // Le filtrage sera fait au niveau du rendu si nécessaire
        return config;
    }, []);

    // Widgets proposés par le panneau catalogue de la page Analytics : ceux du layout courant,
    // PLUS tout widget du catalogue adossé à une requête analytics.
    //
    // C'était auparavant une liste d'ids écrite à la main, qu'il fallait penser à rallonger à
    // chaque nouveau widget — sans quoi le widget existait dans le catalogue, était rendu
    // correctement une fois posé, mais restait introuvable dans le panneau. La règle remplace
    // l'énumération : un widget avec un `dataSource.queryId` est utilisable, donc proposé.
    //
    // La catégorie `dashboard` est exclue : ce sont les widgets du dashboard temps réel (queryId
    // en MAJUSCULES type TOTAL_CALLS_TODAY), qui ne correspondent à aucune requête analytics.
    const availableCatalogWidgetIds = React.useMemo(() => {
        const fromLayout = dashboardConfig?.widgets?.map(w => w.id) || [];
        const fromCatalog = WIDGET_CATALOG
            .filter(w => w.dataSource?.queryId && w.category !== 'dashboard')
            .map(w => w.id);
        return Array.from(new Set([...fromLayout, ...fromCatalog]));
    }, [dashboardConfig]);

    // Charger le Dashboard Layout depuis l'Analytics Profile
    useEffect(() => {
        const loadDashboardLayout = async () => {
            if (!tenantId || !token || !decodedScope || !analyticsProfiles) return;

            // 🔹 Trouver le profile analytics par le champ 'scope'
            let analyticsProfile = Object.values(analyticsProfiles).find(
                profile => profile.scope === decodedScope
            );

            // 🔹 FALLBACK: Si pas de profile pour ce scope, utiliser celui de "default"
            if (!analyticsProfile) {
                analyticsProfile = Object.values(analyticsProfiles).find(
                    profile => profile.scope === "default"
                );
            }

            if (!analyticsProfile) {
                return;
            }

            try {
                // ✅ OPTIMISATION: Utiliser les tabs depuis Redux si disponibles (éviter appel API)
                let tabs = analyticsProfile.dashboardLayout?.tabs;
                
                if (tabs && Object.keys(tabs).length > 0) {
                    console.log('⚡ Using tabs from Redux (no API call needed)', Object.keys(tabs).length, 'tabs');
                } else {
                    // ❌ FALLBACK: Charger depuis l'API si absent de Redux
                    console.log('📡 Tabs not in Redux, fetching from API...');
                    const response = await getDashboardLayout(
                        tenantId,
                        analyticsProfile.id,
                        token
                    );

                    console.log('🔎 [DEBUG] getDashboardLayout API response:', JSON.stringify(response, null, 2));

                    if (response.success && response.data && response.data.tabs) {
                        tabs = response.data.tabs;
                    } else {
                        console.warn('⚠️ [DEBUG] API response missing tabs:', { success: response?.success, hasData: !!response?.data, hasTabs: !!response?.data?.tabs });
                        await initializeDefaultLayout();
                        return;
                    }
                }

                // ✅ Traiter les tabs (qu'ils viennent de Redux ou de l'API)
                if (tabs && Object.keys(tabs).length > 0) {

                    // 🔎 DEBUG — list tabs and their type
                    console.log('🔎 [DEBUG] Tabs in response:', Object.keys(tabs));
                    Object.keys(tabs).forEach(tabId => {
                        console.log(`   → "${tabId}": type=${tabs[tabId].type}, displayName=${tabs[tabId].displayName}, widgets=${Object.keys(tabs[tabId].widgets || {}).length}, positions=${Object.keys(tabs[tabId].widgetsPositions || {}).length}`);
                    });

                    // Vérifier si le dashboard a des positions sauvegardées
                    const dashboardTab = tabs.dashboard;
                    const hasSavedPositions = dashboardTab && dashboardTab.widgetsPositions &&
                        Object.keys(dashboardTab.widgetsPositions).length > 0;

                    // Vérifier si des tabs custom existent (pour ne pas reset si l'utilisateur a déjà personnalisé)
                    const hasCustomTabs = Object.keys(tabs).some(tabId => tabs[tabId].type === 'custom');

                    console.log('🔎 [DEBUG] hasSavedPositions (dashboard tab):', hasSavedPositions, '| hasCustomTabs:', hasCustomTabs);

                    if (!hasSavedPositions && !hasCustomTabs) {
                        // Aucune position sauvegardée ET aucun tab custom: initialiser avec DEFAULT_WIDGET_POSITIONS
                        console.log('🎬 No saved positions AND no custom tabs — initializing with defaults');
                        await initializeDefaultLayout();
                        return; // L'initialisation va recharger les données
                    }

                    if (!hasSavedPositions && hasCustomTabs) {
                        console.log('✅ Dashboard tab empty but custom tabs exist — skipping default init');
                    }

                    console.log('💾 Using saved positions from Cosmos (not defaults)');

                    // Tabs système standard : toujours écraser avec la version par défaut.
                    // Les positions/widgets perso de l'utilisateur dans sla/abandons/flows/trends
                    // sont remplacés à chaque chargement par la définition canonique.
                    Object.keys(DEFAULT_SYSTEM_TABS).forEach(tabId => {
                        const existing = tabs[tabId];
                        const action = existing ? '🔄 Overwriting' : '➕ Injecting';
                        console.log(`${action} system tab "${tabId}"`);
                        tabs[tabId] = {
                            ...DEFAULT_SYSTEM_TABS[tabId],
                            createdAt: existing?.createdAt || new Date().toISOString()
                        };
                    });

                    // Marquer qu'on charge depuis Cosmos pour permettre la mise à jour du layout
                    isLoadingFromCosmosRef.current = true;

                    // ✅ Charger TOUS les tabs depuis Cosmos pour les données, mais FILTRER les système pour l'affichage
                    console.log('🔎 [DEBUG] ALL tabs in Cosmos:', Object.keys(tabs).map(tabId => ({
                        id: tabId,
                        type: tabs[tabId]?.type,
                        displayName: tabs[tabId]?.displayName
                    })));
                    
                    const tabsArray = Object.keys(tabs)
                        .filter(tabId => {
                            // ✅ FILTRER: Exclure les tabs système (dashboard et calls)
                            // pour éviter les duplications dans l'UI
                            return tabId !== 'dashboard' && tabId !== 'calls';
                        })
                        .map(tabId => {
                            const tabData = tabs[tabId];
                            return {
                                id: tabId,
                                name: tabData?.displayName || tabData?.name || tabId,
                                type: tabData?.type || 'custom',
                                createdAt: tabData?.createdAt || new Date().toISOString()
                            };
                        });
                    
                    console.log(`✅ Loaded ${tabsArray.length} custom tab(s) from Cosmos (system tabs excluded):`, tabsArray.map(t => `${t.id} (${t.type})`));
                    setCustomTabs(tabsArray);

                    // Charger les positions des widgets par tab
                    const positions = {};
                    Object.keys(tabs).forEach(tabId => {
                        if (tabs[tabId].widgetsPositions) {
                            positions[tabId] = tabs[tabId].widgetsPositions;
                        }
                    });
                    setTabWidgetsPositions(positions);
                    setTabLayouts(positions); // Synchroniser avec tabLayouts
                    
                    // ✅ Charger TOUS les widgets avec configs (custom tabs ET widgets dashboard configurés)
                    const allWidgets = {};
                    Object.keys(tabs).forEach(tabId => {
                        const tabWidgets = tabs[tabId].widgets || {};
                        Object.keys(tabWidgets).forEach(widgetInstanceId => {
                            const raw = tabWidgets[widgetInstanceId];
                            // Normalize stored widget instance: some entries may be a string (widgetId)
                            const normalized = (typeof raw === 'string') ? { widgetId: raw } : (raw || {});
                            allWidgets[widgetInstanceId] = {
                                ...normalized,
                                id: widgetInstanceId,
                                tabId: tabId
                            };
                        });
                    });
                    setWidgetInstances(allWidgets);
                    setWidgetInstancesLoaded(true);  // ← Marquer comme chargé

                } else {
                    console.warn('⚠️ [DEBUG] No tabs found in Redux or API, initializing with defaults');
                    // Aucune structure existante: initialiser avec les valeurs par défaut
                    await initializeDefaultLayout();
                }
            } catch (error) {
                console.error('❌ Error loading dashboard layout:', error);
            }
        };

        loadDashboardLayout();
    }, [tenantId, token, decodedScope, analyticsProfiles ]);

    // Initialiser les valeurs par défaut des filtres
    useEffect(() => {
        const defaultStartDate = new Date();
        defaultStartDate.setDate(defaultStartDate.getDate() - 7);
        defaultStartDate.setHours(0, 0, 0, 0); // Normaliser à minuit

        const defaultEndDate = new Date();
        defaultEndDate.setHours(0, 0, 0, 0); // Normaliser à minuit

        setFilterValues({
            startDate: defaultStartDate,
            endDate: defaultEndDate
        });

        // Appliquer automatiquement les filtres par défaut.
        // IMPORTANT : normaliser EXACTEMENT comme handleApplyFilters (jour calendaire local
        // → minuit UTC). `.toISOString()` sur une Date à minuit LOCAL décalait la fenêtre de
        // l'offset du fuseau (ex. Paris été : 14/07 00:00 local → 2026-07-13T22:00:00Z), si
        // bien que le chargement initial interrogeait une plage différente du bouton Apply —
        // mêmes dates affichées, résultats différents (bug « 120 au chargement / 100 après Apply »).
        const toUtcMidnight = (d) =>
            new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)).toISOString();

        const filters = {
            startDate: toUtcMidnight(defaultStartDate),
            endDate: toUtcMidnight(defaultEndDate),
            scope: decodedScope,  // IMPORTANT: Utiliser decodedScope pour partition pruning
            timezone: selectedTimezone  // tz courante dès le premier chargement
        };

        setAppliedFilters(filters);
    }, [decodedScope]);

    // Construire le layout à partir de la configuration des widgets
    // IMPORTANT: Initialiser avec useMemo pour éviter les recalculs inutiles
    const initialLayouts = React.useMemo(() => {
        if (!dashboardConfig || !dashboardConfig.widgets) {
            return null;
        }

        // Helper function to generate layout for a breakpoint
        const generateLayoutForBreakpoint = (positionsMap) => {
            return dashboardConfig.widgets.map(widget => {
                const position = positionsMap[widget.id];
                return {
                    i: widget.id,
                    x: position?.x ?? widget.layout.x ?? 0,
                    y: position?.y ?? widget.layout.y ?? 0,
                    w: position?.w ?? widget.layout.w ?? 2,
                    h: position?.h ?? widget.layout.h ?? 2,
                    minW: widget.layout.minW || 2,
                    minH: widget.layout.minH || 2,
                    static: false
                };
            });
        };

        // Generate lg layout avec positions par défaut
        const lgLayout = dashboardConfig.widgets.map(widget => {
            const defaultPosition = DEFAULT_WIDGET_POSITIONS[widget.id];

            if (defaultPosition) {
                return {
                    i: widget.id,
                    x: defaultPosition.x,
                    y: defaultPosition.y,
                    w: defaultPosition.w,
                    h: defaultPosition.h,
                    minW: widget.layout.minW || 2,
                    minH: widget.layout.minH || 2,
                    static: false
                };
            }

            return {
                i: widget.id,
                x: widget.layout.x,
                y: widget.layout.y,
                w: widget.layout.w,
                h: widget.layout.h,
                minW: widget.layout.minW || 2,
                minH: widget.layout.minH || 2,
                static: false
            };
        });

        return {
            lg: lgLayout,
            md: generateLayoutForBreakpoint(MD_WIDGET_POSITIONS),
            sm: generateLayoutForBreakpoint(SM_WIDGET_POSITIONS),
            xs: generateLayoutForBreakpoint(XS_WIDGET_POSITIONS),
            xxs: generateLayoutForBreakpoint(XXS_WIDGET_POSITIONS)
        };
    }, [dashboardConfig]);

    // Mettre à jour le layout quand les positions sauvegardées changent
    useEffect(() => {
        // ⚠️ NE PAS réinitialiser le layout pendant drag/resize en mode édition
        if (isEditMode && (isDragging || isResizing)) {
            return;
        }

        // ⚠️ NE PAS réinitialiser si on est en mode édition et que ce n'est pas un chargement Cosmos
        if (isEditMode && !isLoadingFromCosmosRef.current) {
            return;
        }

        if (!dashboardConfig || !dashboardConfig.widgets) {
            return;
        }

        // Vérifier si on a des positions sauvegardées depuis Cosmos pour le dashboard
        const savedDashboardPositions = tabWidgetsPositions['dashboard'];
        const hasSavedPositions = savedDashboardPositions && Object.keys(savedDashboardPositions).length > 0;
        
        // Helper function to generate layout for a breakpoint
        const generateLayoutForBreakpoint = (positionsMap) => {
            return dashboardConfig.widgets.map(widget => {
                // ✅ Si on a des positions sauvegardées depuis Cosmos, les utiliser en priorité
                if (hasSavedPositions && savedDashboardPositions[widget.id]) {
                    const savedPos = savedDashboardPositions[widget.id];
                    return {
                        i: widget.id,
                        x: savedPos.x,
                        y: savedPos.y,
                        w: savedPos.w,
                        h: savedPos.h,
                        minW: widget.layout.minW || 2,
                        minH: widget.layout.minH || 2,
                        static: false
                    };
                }

                // ✅ Sinon, utiliser les positions par défaut du breakpoint
                const position = positionsMap[widget.id];
                return {
                    i: widget.id,
                    x: position?.x ?? 0,
                    y: position?.y ?? 0,
                    w: position?.w ?? 2,
                    h: position?.h ?? 2,
                    minW: widget.layout.minW || 2,
                    minH: widget.layout.minH || 2,
                    static: false
                };
            });
        };

        // Generate lg layout: utiliser Cosmos si disponible, sinon DEFAULT_WIDGET_POSITIONS
        const lgLayout = dashboardConfig.widgets.map(widget => {
            // ✅ Priorité 1: Positions sauvegardées dans Cosmos
            if (hasSavedPositions && savedDashboardPositions[widget.id]) {
                const savedPos = savedDashboardPositions[widget.id];
                return {
                    i: widget.id,
                    x: savedPos.x,
                    y: savedPos.y,
                    w: savedPos.w,
                    h: savedPos.h,
                    minW: widget.layout.minW || 2,
                    minH: widget.layout.minH || 2,
                    static: false
                };
            }

            // ✅ Priorité 2: Positions par défaut (fallback)
            const defaultPosition = DEFAULT_WIDGET_POSITIONS[widget.id];
            if (defaultPosition) {
                return {
                    i: widget.id,
                    x: defaultPosition.x,
                    y: defaultPosition.y,
                    w: defaultPosition.w,
                    h: defaultPosition.h,
                    minW: widget.layout.minW || 2,
                    minH: widget.layout.minH || 2,
                    static: false
                };
            }

            // ✅ Priorité 3: Config du widget (fallback ultime)
            return {
                i: widget.id,
                x: widget.layout.x,
                y: widget.layout.y,
                w: widget.layout.w,
                h: widget.layout.h,
                minW: widget.layout.minW || 2,
                minH: widget.layout.minH || 2,
                static: false
            };
        });

        const gridLayouts = {
            lg: lgLayout,
            md: lgLayout,  // Utiliser les mêmes positions que lg
            sm: lgLayout,  // Utiliser les mêmes positions que lg
            xs: lgLayout,  // Utiliser les mêmes positions que lg
            xxs: lgLayout  // Utiliser les mêmes positions que lg
        };

        setLayouts(gridLayouts);

        // Réinitialiser la ref après application du layout
        isLoadingFromCosmosRef.current = false;
    }, [dashboardConfig, initialLayouts, tabWidgetsPositions, isEditMode, isDragging, isResizing]);

    // Détecter la sortie du mode édition et sauvegarder les positions
    useEffect(() => {
        // Si on passe de true à false (sortie du mode édition)
        if (wasEditModeRef.current === true && isEditMode === false) {
            // Positions will be saved via "Save Layout" button
        }

        // Mettre à jour la référence
        wasEditModeRef.current = isEditMode;
    }, [isEditMode, layouts]);

    /**
     * Charger les données de TOUS les widgets en parallèle (Promise.all)
     * Optimise le temps de chargement vs requêtes séquentielles
     */
    const loadAllWidgetsData = useCallback(async () => {
        if (!dashboardConfig || !dashboardConfig.widgets) {
            return;
        }

        setIsLoadingWidgets(true);
        const totalWidgets = dashboardConfig.widgets.length;

        try {
            const startTime = performance.now();

            // Fonction pour charger un widget
            const loadWidget = async (widget) => {
                // 🔹 Déterminer les filtres à utiliser pour ce widget
                // ✅ IMPORTANT: Utiliser widgetInstancesRef.current pour avoir TOUJOURS les valeurs à jour
                const instanceId = `dashboard-${widget.id}`;
                const widgetInstance = widgetInstancesRef.current[instanceId];
                
              
                
                let widgetFilters = appliedFilters;  // Par défaut, filtres globaux
                
                // ⚠️ IMPORTANT: Vérifier STRICTEMENT si useGlobalFilters est explicitement false
                if (widgetInstance && widgetInstance.config?.useGlobalFilters === false) {
                    // Mode: FILTRES PERSONNALISÉS du widget (tous optionnels)
                    const customPeriod = widgetInstance.config?.customPeriod;
                    const customFilters = widgetInstance.config?.customFilters || {};
                    
                    widgetFilters = {
                        scope: decodedScope,
                        timezone: selectedTimezone
                    };
                    
                    // Ajouter les dates SEULEMENT si une période est définie
                    if (customPeriod) {
                        const dateRange = calculateDateRangeFromPeriod(customPeriod, widgetInstance.config);
                        if (dateRange.startDate) widgetFilters.startDate = dateRange.startDate;
                        if (dateRange.endDate) widgetFilters.endDate = dateRange.endDate;
                    }
                    
                    // Ajouter les filtres flow/queue/agent s'ils sont définis
                    if (customFilters.flowKey) widgetFilters.flowKey = customFilters.flowKey;
                    if (customFilters.queueKey) widgetFilters.queueKey = customFilters.queueKey;
                    if (customFilters.agentKey) widgetFilters.agentKey = customFilters.agentKey;
                    
                    } else {
                    // Mode: FILTRES GLOBAUX (défaut pour tous les widgets non configurés)
                }
                
                // Vérifier si le widget utilise reportConfig comme source
                if (widget.config?.dataSource === 'reportConfig' && widget.config?.dataPath) {
                    try {
                        // 🔹 Trouver le profile analytics par le champ 'scope'
                        let analyticsProfile = Object.values(analyticsProfiles).find(
                            profile => profile.scope === decodedScope
                        );

                        // 🔹 FALLBACK: Si pas de profile pour ce scope, utiliser celui de "default"
                        if (!analyticsProfile) {
                            analyticsProfile = Object.values(analyticsProfiles).find(
                                profile => profile.scope === "default"
                            );
                        }

                        if (analyticsProfile && analyticsProfile.reportConfig) {
                            // Extraire la valeur depuis le reportConfig en utilisant le dataPath
                            const pathParts = widget.config.dataPath.split('.');
                            let value = analyticsProfile.reportConfig;
                            
                            // Parcourir le path pour extraire la valeur
                            for (const part of pathParts) {
                                value = value?.[part];
                            }
                            
                            // Vérifier le type de widget pour formater les données correctement
                            if (widget.type === WidgetTypes.DONUT) {
                                // Pour DonutChart: convertir l'objet en segments
                                const colors = widget.config.colors || {};
                                const labels = widget.config.labels || {};
                                
                                const segments = Object.entries(value || {}).map(([key, val]) => ({
                                    label: `${labels[key] || key} (${val}s)`,
                                    value: val,
                                    color: colors[key] || '#0078D4',
                                    percentage: 0  // Sera calculé par le DonutChartWidget
                                }));

                                return {
                                    widgetId: widget.id,
                                    data: segments
                                };
                            } else {
                                // Pour MetricWidget: extraire value, targetValue et maxValue
                                let targetValue = null;
                                if (widget.config?.targetPath) {
                                    const targetParts = widget.config.targetPath.split('.');
                                    targetValue = analyticsProfile.reportConfig;
                                    
                                    for (const part of targetParts) {
                                        targetValue = targetValue?.[part];
                                    }
                                }
                                
                                let maxValue = null;
                                if (widget.config?.maxPath) {
                                    const maxParts = widget.config.maxPath.split('.');
                                    maxValue = analyticsProfile.reportConfig;
                                    
                                    for (const part of maxParts) {
                                        maxValue = maxValue?.[part];
                                    }
                                }
                                
                                // Retourner la valeur dans le format attendu par MetricWidget
                                return { 
                                    widgetId: widget.id, 
                                    data: { 
                                        value: value || 0,
                                        maxValue: maxValue,
                                        targetValue: targetValue
                                    } 
                                };
                            }
                        }
                    } catch (error) {
                        console.error(`Error loading reportConfig for widget ${widget.id}:`, error);
                        return { widgetId: widget.id, error: error.message };
                    }

                    // Si on arrive ici, c'est que analyticsProfile n'est pas encore chargé
                    console.warn(`⚠️ Analytics Profile not available for ${widget.id}, skipping for now`);
                    return { widgetId: widget.id, data: null };
                }

                if (!widget.queryId) {
                    return { widgetId: widget.id, data: null };
                }

                try {
                    // 🔹 Enrichir les filtres avec les thresholds du reportConfig
                    let enrichedFilters = { ...widgetFilters };
                    
                    // 1. Chercher le profile du scope actuel
                    let analyticsProfile = Object.values(analyticsProfiles).find(
                        profile => profile.scope === decodedScope
                    );
                    
                    // 2. Si pas de profile, chercher celui de "default" UNIQUEMENT pour les thresholds
                    if (!analyticsProfile) {
                        analyticsProfile = Object.values(analyticsProfiles).find(
                            profile => profile.scope === "default"
                        );
                    }

                    let widgetWithThresholds = { ...widget };

                    // 3. Ajouter les thresholds depuis reportConfig (du scope actuel ou de "default")
                    if (analyticsProfile?.reportConfig?.thresholds) {
                        const thresholds = analyticsProfile.reportConfig.thresholds;
                        enrichedFilters = {
                            ...enrichedFilters,
                            quickAnswerThreshold: thresholds.quickAnswerThreshold,
                            standardAnswerThreshold: thresholds.standardAnswerThreshold,
                            delayedAnswerThreshold: thresholds.delayedAnswerThreshold,
                            quickDropThreshold: thresholds.quickDropThreshold,
                            // Seuil SLA en SECONDES, envoyé à TOUTES les requêtes pour qu'elles
                            // partagent la même définition. Il provenait auparavant de
                            // waitingTime.targetWaitingTime — la cible de colorisation du widget
                            // « Max Waiting Time », dont le défaut est 150 s : un SLA à 150 s
                            // affichait évidemment ~100 % en permanence.
                            slaSeconds: thresholds.slaSeconds
                        };
                    }

                    // Listes provisionnées (flows / files) : même règle que partout ailleurs,
                    // sinon ce chemin-ci renverrait des colonnes issues des CDR.
                    enrichedFilters = {
                        ...enrichedFilters,
                        ...buildColumnFilters(widget.queryId, enrichedFilters)
                    };

                    // Fetch data from API avec les filtres enrichis (timing)
                    const widgetStart = performance.now();
                    const result = await fetchWidgetData(widget, enrichedFilters, tenantId, token);
                    const widgetMs = Math.round(performance.now() - widgetStart);
                    console.log(`⏱️  ${widget.queryId.padEnd(38)} ${widgetMs}ms`);

                    // Fetch data from API avec les filtres enrichis
                    console.log(widgetWithThresholds, enrichedFilters, 'enrichedFilters for widget', widget.id);
                    // Si le widget a un targetPath ou maxPath dans reportConfig, les injecter
                    if (widget.config?.targetPath || widget.config?.maxPath) {
                        // 🔹 Trouver le profile analytics par le champ 'scope'
                        let analyticsProfile = Object.values(analyticsProfiles).find(
                            profile => profile.scope === decodedScope
                        );

                        // 🔹 FALLBACK: Si pas de profile pour ce scope, utiliser celui de "default"
                        if (!analyticsProfile) {
                            analyticsProfile = Object.values(analyticsProfiles).find(
                                profile => profile.scope === "default"
                            );
                        }

                        if (analyticsProfile && analyticsProfile.reportConfig) {
                            if (widget.config?.targetPath) {
                                const pathParts = widget.config.targetPath.split('.');
                                let targetValue = analyticsProfile.reportConfig;
                                for (const part of pathParts) {
                                    targetValue = targetValue?.[part];
                                }
                                if (result && typeof result === 'object') {
                                    result.targetValue = targetValue;
                                }
                            }

                            if (widget.config?.maxPath) {
                                const maxParts = widget.config.maxPath.split('.');
                                let maxValue = analyticsProfile.reportConfig;
                                for (const part of maxParts) {
                                    maxValue = maxValue?.[part];
                                }
                                if (result && typeof result === 'object') {
                                    result.maxValue = maxValue;
                                }
                            }
                        }
                    }

                    // Le mapping UUID→Name sera fait dans chaque Widget via Redux hooks
                    return { widgetId: widget.id, data: result };
                } catch (error) {
                    return { widgetId: widget.id, error: error.message };
                }
            };

            // Commit en UN SEUL setState pour éviter les render storms
            const commitBatch = (batchResults) => {
                setWidgetsData(prev => {
                    const next = { ...prev };
                    batchResults.forEach(({ widgetId, data, error }) => {
                        next[widgetId] = error ? { error } : data;
                    });
                    return next;
                });
            };

            // Helper: récupérer le profil analytics du scope (avec fallback "default")
            const getAnalyticsProfileForScope = () => {
                let profile = Object.values(analyticsProfiles).find(p => p.scope === decodedScope);
                if (!profile) {
                    profile = Object.values(analyticsProfiles).find(p => p.scope === 'default');
                }
                return profile;
            };

            // Helper: calculer les filtres enrichis (thresholds + SLA) AVANT l'appel batch
            const buildEnrichedFiltersForWidget = (widget) => {
                // 1. Filtres de base (custom widget vs globaux)
                let widgetFilters = appliedFilters;
                const instanceId = `dashboard-${widget.id}`;
                const widgetInstance = widgetInstancesRef.current[instanceId];

                if (widgetInstance && widgetInstance.config?.useGlobalFilters === false) {
                    const customPeriod = widgetInstance.config?.customPeriod;
                    const customFilters = widgetInstance.config?.customFilters || {};
                    widgetFilters = { scope: decodedScope, timezone: selectedTimezone };
                    if (customPeriod) {
                        const dateRange = calculateDateRangeFromPeriod(customPeriod, widgetInstance.config);
                        if (dateRange.startDate) widgetFilters.startDate = dateRange.startDate;
                        if (dateRange.endDate) widgetFilters.endDate = dateRange.endDate;
                    }
                    if (customFilters.flowKey) widgetFilters.flowKey = customFilters.flowKey;
                    if (customFilters.queueKey) widgetFilters.queueKey = customFilters.queueKey;
                    if (customFilters.agentKey) widgetFilters.agentKey = customFilters.agentKey;
                }

                // 2. Filtres widget-specific (thresholds depuis config)
                let enrichedFilters = buildWidgetFilters(widget, widgetFilters);

                // 3. Thresholds globaux depuis reportConfig
                const profile = getAnalyticsProfileForScope();
                if (profile?.reportConfig?.thresholds) {
                    const t = profile.reportConfig.thresholds;
                    enrichedFilters = {
                        ...enrichedFilters,
                        quickAnswerThreshold: t.quickAnswerThreshold,
                        standardAnswerThreshold: t.standardAnswerThreshold,
                        delayedAnswerThreshold: t.delayedAnswerThreshold,
                        quickDropThreshold: t.quickDropThreshold,
                        // Seuil SLA en secondes — un seul, partagé par toutes les requêtes.
                        slaSeconds: t.slaSeconds
                    };
                }

                // 4. Listes provisionnées (flows / files) — définissent les COLONNES des tableaux
                //    « par flow » et « par file », ne filtrent rien. Envoyées seulement aux
                //    requêtes qui les consomment.
                enrichedFilters = {
                    ...enrichedFilters,
                    ...buildColumnFilters(widget.queryId, enrichedFilters)
                };

                return enrichedFilters;
            };

            // Helper: appliquer targetValue/maxValue (depuis reportConfig) APRÈS l'appel batch
            const applyReportConfigEnrichment = (widget, data) => {
                if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
                if (!widget.config?.targetPath && !widget.config?.maxPath) return data;
                const profile = getAnalyticsProfileForScope();
                if (!profile?.reportConfig) return data;

                if (widget.config?.targetPath) {
                    const parts = widget.config.targetPath.split('.');
                    let v = profile.reportConfig;
                    for (const p of parts) v = v?.[p];
                    data.targetValue = v;
                }
                if (widget.config?.maxPath) {
                    const parts = widget.config.maxPath.split('.');
                    let v = profile.reportConfig;
                    for (const p of parts) v = v?.[p];
                    data.maxValue = v;
                }
                return data;
            };

            // Séparer reportConfig (pas d'API) vs API widgets
            const reportConfigWidgets = dashboardConfig.widgets.filter(w =>
                w.config?.dataSource === 'reportConfig'
            );
            const apiWidgets = dashboardConfig.widgets.filter(w =>
                (!w.config?.dataSource || w.config?.dataSource !== 'reportConfig') && w.queryId
            );

            console.log(`🚀 Loading ${reportConfigWidgets.length} reportConfig + ${apiWidgets.length} API widgets (batch endpoint)`);
            const t0 = performance.now();

            // ÉTAPE 1: Widgets reportConfig (synchrone, pas d'API)
            const reportConfigResults = await Promise.all(reportConfigWidgets.map(loadWidget));
            commitBatch(reportConfigResults);
            console.log(`✅ reportConfig done in ${Math.round(performance.now() - t0)}ms`);

            // ÉTAPE 2: Widgets API → 1 SEUL appel batch (au lieu de N round-trips)
            if (apiWidgets.length > 0) {
                const batchPayload = apiWidgets.map(w => ({
                    widgetId: w.id,
                    queryId: w.queryId,
                    widgetType: w.type,
                    filters: buildEnrichedFiltersForWidget(w)
                }));

                // ⚡ FAST PATH: si on a un prefetch frais (chargé depuis /analytics),
                // l'utiliser au lieu de refaire l'appel API → affichage instantané.
                // Si une promise est encore en vol, on l'attend brièvement (race condition
                // si l'utilisateur clique avant la fin du prefetch).
                const tryUsePrefetch = (cached) => {
                    if (!cached || usedPrefetchRef.current) return false;
                    usedPrefetchRef.current = true;
                    const widgetById = Object.fromEntries(apiWidgets.map(w => [w.id, w]));
                    const finalResults = cached.results.map(r => {
                        if (r.error) return { widgetId: r.widgetId, error: r.error };
                        const widget = widgetById[r.widgetId];
                        return { widgetId: r.widgetId, data: applyReportConfigEnrichment(widget, r.data) };
                    });
                    commitBatch(finalResults);
                    return true;
                };

                const cached = analyticsPrefetch?.[decodedScope];
                const isFresh = cached && (Date.now() - cached.timestamp) < PREFETCH_TTL_MS;
                if (isFresh && tryUsePrefetch(cached)) {
                    console.log(`⚡ Used prefetched data for "${decodedScope}" (${cached.results.length} widgets, instant)`);
                    console.log(`🏁 TOTAL load time: ${Math.round(performance.now() - t0)}ms`);
                    setIsLoadingWidgets(false);
                    return;
                }

                // Cache absent → vérifier si une prefetch est en vol. On l'attend brièvement
                // (race avec un timeout) — si elle ne répond pas vite, on fallback sur le
                // fetch normal (probablement servi en partie depuis le cache serveur).
                const inFlight = getInFlightPrefetch(decodedScope);
                if (inFlight) {
                    console.log(`⏳ Awaiting in-flight prefetch for "${decodedScope}" (max ${PREFETCH_AWAIT_MAX_MS}ms)...`);
                    const timeoutSentinel = Symbol('timeout');
                    const result = await Promise.race([
                        inFlight,
                        new Promise(r => setTimeout(() => r(timeoutSentinel), PREFETCH_AWAIT_MAX_MS)),
                    ]);
                    if (result === timeoutSentinel) {
                        console.warn(`⏱️ Prefetch too slow for "${decodedScope}" — falling back to direct fetch`);
                    } else if (result && tryUsePrefetch(result)) {
                        console.log(`⚡ Used in-flight prefetch for "${decodedScope}" (${result.results.length} widgets)`);
                        console.log(`🏁 TOTAL load time: ${Math.round(performance.now() - t0)}ms`);
                        setIsLoadingWidgets(false);
                        return;
                    }
                }

                const batchStart = performance.now();
                const batchResponse = await fetchBatchWidgetData(tenantId, batchPayload, token);
                const batchMs = Math.round(performance.now() - batchStart);

                if (!batchResponse.success) {
                    console.error(`❌ Batch endpoint failed: ${batchResponse.error}`);
                    // Marquer tous les widgets en erreur pour ne pas laisser le UI bloqué
                    commitBatch(apiWidgets.map(w => ({ widgetId: w.id, error: batchResponse.error })));
                } else {
                    const widgetById = Object.fromEntries(apiWidgets.map(w => [w.id, w]));
                    const finalResults = batchResponse.results.map(r => {
                        if (r.error) return { widgetId: r.widgetId, error: r.error };
                        const widget = widgetById[r.widgetId];
                        return {
                            widgetId: r.widgetId,
                            data: resolveSupervisorNames(widget, applyReportConfigEnrichment(widget, r.data))
                        };
                    });
                    commitBatch(finalResults);

                    const meta = batchResponse.metadata || {};
                    console.log(`✅ Batch endpoint: ${meta.count || apiWidgets.length} widgets, ${meta.cacheHits || 0} cache hits, ${meta.errors || 0} errors, server=${meta.executionTime}ms, total=${batchMs}ms`);
                }
            }

            console.log(`🏁 TOTAL load time: ${Math.round(performance.now() - t0)}ms`);
        } catch (error) {
            // Error handled silently
        } finally {
            setIsLoadingWidgets(false);
        }
    // ⚠️ IMPORTANT: widgetInstances est utilisé dans loadWidget mais PAS dans les dépendances
    // pour éviter de recharger TOUS les widgets quand on modifie la config d'UN SEUL widget
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardConfig, appliedFilters, tenantId, token, analyticsProfiles, decodedScope, selectedTimezone, buildColumnFilters]);

    // Charger toutes les données des widgets en parallèle quand les filtres appliqués changent
    // ⚠️ widgetInstances est inclus pour détecter le chargement initial depuis Cosmos
    useEffect(() => {
        // 🔹 Ne pas recharger si on vient de sauvegarder un widget individuel
        if (isSavingWidgetRef.current) {
            isSavingWidgetRef.current = false;
            return;
        }
        
       
        
        // 🔹 Vérifications strictes avant de charger les widgets
        if (!appliedFilters || !tenantId || !dashboardConfig) {
            return;
        }

        // 🔹 Vérifier que les analytics profiles sont chargés
        if (!analyticsProfiles) {
            console.log('⏳ Waiting for analytics profiles to load...');
            return;
        }

        // 🔹 Trouver le profile analytics par le champ 'scope'
        let analyticsProfile = Object.values(analyticsProfiles).find(
            profile => profile.scope === decodedScope
        );

        // 🔹 FALLBACK: Si pas de profile pour ce scope, utiliser celui de "default"
        if (!analyticsProfile) {
            analyticsProfile = Object.values(analyticsProfiles).find(
                profile => profile.scope === "default"
            );
        }

        // 🔹 Guard anti-cascade : ne refetch que si les vrais inputs des queries ont changé.
        // Les refs upstream (analyticsProfiles, widgetInstancesLoaded...)
        // peuvent muter sans qu'on ait besoin de re-querier Cosmos.
        const loadKey = JSON.stringify({
            tenantId,
            decodedScope,
            selectedTab,
            // Inclure la résolution du profile analytics : un 1er passage peut avoir lieu
            // avant que le profile soit chargé (loadAllWidgetsData ne charge alors rien).
            // Sans ce champ, le guard anti-cascade bloque le rechargement une fois le
            // profile arrivé → dashboard vide au refresh jusqu'à un changement de filtre manuel.
            profileReady: !!analyticsProfile,
            startDate: appliedFilters?.startDate,
            endDate: appliedFilters?.endDate,
            flowKey: appliedFilters?.flowKey,
            queueKey: appliedFilters?.queueKey,
            agentKey: appliedFilters?.agentKey,
            businessHours: appliedFilters?.businessHours,
            businessDays: appliedFilters?.businessDays,
        });

        if (lastLoadKeyRef.current === loadKey) {
            return;
        }
        lastLoadKeyRef.current = loadKey;

        loadAllWidgetsData();
    }, [appliedFilters, tenantId, selectedTab, dashboardConfig, analyticsProfiles, decodedScope, loadAllWidgetsData, widgetInstancesLoaded, widgetInstances, tabLayouts, tabWidgetsPositions]);
    // Synchroniser widgetInstances depuis Cosmos si le tab custom sélectionné n'a pas de widgets (ex: après retour sur l'UI)
    useEffect(() => {
        if (!selectedTab || selectedTab === 'dashboard' || selectedTab === 'calls') return;
        if (!customTabs || customTabs.length === 0) return;
        if (!widgetInstances) return;
        // Vérifier si le tab sélectionné est custom et vide côté widgetInstances
        const widgetsForTab = typeof getWidgetInstancesForTab === 'function' ? getWidgetInstancesForTab(selectedTab) : [];
        if (Array.isArray(widgetsForTab) && widgetsForTab.length === 0) {
            // Relancer le chargement depuis Cosmos
            console.log(`[SYNC] Tab '${selectedTab}' custom sans widgets en mémoire, resynchronisation avec Cosmos...`);
            if (typeof analyticsProfiles !== 'undefined' && typeof tenantId !== 'undefined' && typeof token !== 'undefined' && typeof decodedScope !== 'undefined') {
                // Appeler la même logique que le useEffect initial
                (async () => {
                    let analyticsProfile = Object.values(analyticsProfiles).find(profile => profile.scope === decodedScope);
                    if (!analyticsProfile) {
                        analyticsProfile = Object.values(analyticsProfiles).find(profile => profile.scope === "default");
                    }
                    if (!analyticsProfile) return;
                    const response = await getDashboardLayout(tenantId, analyticsProfile.id, token);
                    if (response.success && response.data && response.data.tabs) {
                        const tabs = response.data.tabs;
                        const allWidgets = {};
                        Object.keys(tabs).forEach(tabId => {
                            const tabWidgets = tabs[tabId].widgets || {};
                            Object.keys(tabWidgets).forEach(widgetInstanceId => {
                                const raw = tabWidgets[widgetInstanceId];
                                const normalized = (typeof raw === 'string') ? { widgetId: raw } : (raw || {});
                                allWidgets[widgetInstanceId] = {
                                    ...normalized,
                                    id: widgetInstanceId,
                                    tabId: tabId
                                };
                            });
                        });
                        setWidgetInstances(allWidgets);
                        setWidgetInstancesLoaded(true);
                    }
                })();
            }
        }
    }, [selectedTab, analyticsProfiles, tenantId, token, decodedScope]);

 useEffect(() => {
    // Garde anti-race : cet effet se re-déclenche à chaque MAJ de widgetInstances /
    // appliedFilters / analyticsProfiles → plusieurs fetch concurrents. Sans garde, c'est
    // le dernier RÉSOLU qui écrit (pas le dernier DÉCLENCHÉ) → la période d'un widget en mode
    // figé (customPeriod) peut flip-flop (today/yesterday) selon quel batch répond en dernier.
    // On n'applique QUE le résultat du run courant ; le cleanup annule les runs précédents.
    let cancelled = false;
    const loadCustomTabWidgetsData = async () => {
        const isCustomTab = selectedTab !== 'dashboard' && selectedTab !== 'calls';
        if (!isCustomTab || !tenantId ||  !token || !analyticsProfiles) return;

        const tabWidgets = getWidgetInstancesForTab(selectedTab);
        if (tabWidgets.length === 0) return;

        const getProfile = () => {
            let p = Object.values(analyticsProfiles).find(x => x.scope === decodedScope);
            if (!p) p = Object.values(analyticsProfiles).find(x => x.scope === 'default');
            return p;
        };

        const applyEnrichment = (widgetConfig, data) => {
            if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
            if (!widgetConfig?.config?.targetPath && !widgetConfig?.config?.maxPath) return data;

            const profile = getProfile();
            if (!profile?.reportConfig) return data;

            if (widgetConfig.config?.targetPath) {
                const parts = widgetConfig.config.targetPath.split('.');
                let v = profile.reportConfig;
                for (const p of parts) v = v?.[p];
                data.targetValue = v;
            }

            if (widgetConfig.config?.maxPath) {
                const parts = widgetConfig.config.maxPath.split('.');
                let v = profile.reportConfig;
                for (const p of parts) v = v?.[p];
                data.maxValue = v;
            }

            return data;
        };

        const batchPayload = [];
        const widgetConfigById = {};

        for (const widgetInstance of tabWidgets) {
           const catalogWidget = WIDGET_CATALOG.find(w => w.id === widgetInstance.widgetId);

const originalWidgetConfig =
  widgetInstance.originalConfig ||
  dashboardConfig?.widgets?.find(w => w.id === widgetInstance.widgetId);

const widgetConfig = {
  queryId:
    widgetInstance.config?.queryId ||
    originalWidgetConfig?.queryId ||
    catalogWidget?.dataSource?.queryId,

  type:
    widgetInstance.config?.type ||
    originalWidgetConfig?.type ||
    catalogWidget?.type,

  config: widgetInstance.config || originalWidgetConfig?.config || originalWidgetConfig
};

if (!widgetConfig.queryId) {
  console.warn(`⚠️ Widget ${widgetInstance.widgetId} sans queryId`);
  continue;
}

            // Clé = id d'INSTANCE (pas widgetId de base) : deux instances du même widget dans
            // un onglet doivent avoir leur propre data/config. Avant, clées par widgetId, elles
            // partageaient une seule case → même donnée affichée, le dernier fetch écrasant l'autre.
            widgetConfigById[widgetInstance.id] = widgetConfig;

            // Config EFFECTIVE : celle de l'instance (widget ajouté par l'utilisateur) ou, à
            // défaut, la config par défaut du widget (dashboardConfigs.js, résolue via
            // originalConfig). Les widgets des onglets « system » (sla/abandons/flows/trends)
            // sont persistés en forme NUE `{ widgetId }` SANS `config` : ils doivent utiliser le
            // filtre GLOBAL, pas être sautés. Avant, `if (!widgetInstance.config) continue;`
            // les éliminait tous → batchPayload vide → onglet définitivement vide (« No data »).
            // On ne saute plus faute de config ; sans `useGlobalFilters:false` explicite, on
            // retombe sur le filtre global — comportement attendu pour ces widgets.
            const effectiveConfig = widgetInstance.config || originalWidgetConfig?.config || null;

            let widgetFilters;
            if (effectiveConfig?.useGlobalFilters !== false) {
                widgetFilters = appliedFilters;
            } else {
                const customPeriod = effectiveConfig?.customPeriod;
                const customFilters = effectiveConfig?.customFilters || {};

                widgetFilters = { scope: decodedScope, timezone: selectedTimezone };

                if (customPeriod) {
                    const dateRange = calculateDateRangeFromPeriod(customPeriod, effectiveConfig);
                    if (dateRange.startDate) widgetFilters.startDate = dateRange.startDate;
                    if (dateRange.endDate) widgetFilters.endDate = dateRange.endDate;
                }

                if (customFilters.flowKey) widgetFilters.flowKey = customFilters.flowKey;
                if (customFilters.queueKey) widgetFilters.queueKey = customFilters.queueKey;
                if (customFilters.agentKey) widgetFilters.agentKey = customFilters.agentKey;
            }

            // ✅ KEEP: enrichment logic from HEAD
            let enrichedFilters = buildWidgetFilters(widgetConfig, widgetFilters);

            const profile = getProfile();

            if (profile?.reportConfig?.thresholds) {
                const t = profile.reportConfig.thresholds;
                enrichedFilters = {
                    ...enrichedFilters,
                    quickAnswerThreshold: t.quickAnswerThreshold,
                    standardAnswerThreshold: t.standardAnswerThreshold,
                    delayedAnswerThreshold: t.delayedAnswerThreshold,
                    quickDropThreshold: t.quickDropThreshold,
                    // Seuil SLA en secondes — un seul, partagé par toutes les requêtes.
                    slaSeconds: t.slaSeconds
                };
            }

            // Listes provisionnées (flows / files) — définissent les COLONNES des tableaux.
            enrichedFilters = {
                ...enrichedFilters,
                ...buildColumnFilters(widgetConfig.queryId, enrichedFilters)
            };

            // Inventaire des widgets qui portent leurs PROPRES filtres. Deux instances du même
            // widget peuvent coexister dans un onglet (c'est permis) : même titre, même allure,
            // mais l'une filtrée et l'autre non. Sans cette trace, impossible de savoir laquelle
            // on regarde — et « nettoyer » l'une laisse l'autre filtrer.
            const ownFilters = effectiveConfig?.useGlobalFilters === false
                ? (effectiveConfig?.customFilters || {})
                : null;
            if (ownFilters && Object.keys(ownFilters).length > 0) {
                console.log(
                    `🔎 filtres PROPRES sur "${widgetInstance.name || widgetInstance.widgetId}" (${widgetInstance.id})`,
                    ownFilters
                );
            }

            batchPayload.push({
                // `widgetId` sert ici de clé de CORRÉLATION (renvoyée telle quelle dans la réponse
                // batch) : on met l'id d'INSTANCE pour que chaque instance ait sa propre réponse.
                // Le vrai widget/requête, c'est `queryId`.
                widgetId: widgetInstance.id,
                queryId: widgetConfig.queryId,
                widgetType: widgetConfig.type,
                filters: enrichedFilters
            });
        }

        if (batchPayload.length === 0) return;

        console.log(`🚀 Custom tab "${selectedTab}": loading ${batchPayload.length} widgets (batch endpoint)`);

        const t0 = performance.now();
        const batchResponse = await fetchBatchWidgetData(tenantId, batchPayload, token);
        const totalMs = Math.round(performance.now() - t0);

        if (!batchResponse.success) {
            console.error(`❌ Custom tab batch failed: ${batchResponse.error}`);
            setWidgetsData(prev => ({
                ...prev,
                ...Object.fromEntries(batchPayload.map(p => [p.widgetId, { error: batchResponse.error }]))
            }));
            return;
        }

        const dataMap = {};
        batchResponse.results.forEach(r => {
            if (r.error) {
                dataMap[r.widgetId] = { error: r.error };
            } else {
                dataMap[r.widgetId] = resolveSupervisorNames(widgetConfigById[r.widgetId], applyEnrichment(widgetConfigById[r.widgetId], r.data));
            }
        });

        if (cancelled) return;   // un run plus récent a pris le relais → on ignore ce résultat périmé
        setWidgetsData(prev => ({ ...prev, ...dataMap }));

        const meta = batchResponse.metadata || {};
        console.log(
            `✅ Custom tab "${selectedTab}" loaded: ${meta.count || batchPayload.length} widgets, ${meta.cacheHits || 0} cached, ${meta.errors || 0} errors, server=${meta.executionTime}ms, total=${totalMs}ms`
        );
    };

    loadCustomTabWidgetsData();
    return () => { cancelled = true; };
}, [
    selectedTab,
    widgetInstances,
    appliedFilters,
    tenantId,
    token,
    analyticsProfiles,
    decodedScope,
    selectedTimezone,
    // Les listes provisionnées (files, flows) arrivent souvent APRÈS le premier rendu : sans cette
    // dépendance, l'effet garderait une liste vide et les tableaux repartiraient sans colonnes.
    buildColumnFilters
]);
    /**
     * Gérer le changement de la date de début
     */
    const handleStartDateChange = (date) => {
        setFilterValues(prev => ({
            ...prev,
            startDate: date
        }));
    };

    /**
     * Gérer le changement de la date de fin
     */
    const handleEndDateChange = (date) => {
        setFilterValues(prev => ({
            ...prev,
            endDate: date
        }));
    };

    /**
     * Normaliser une date à minuit UTC
     */
    const normalizeDate = (date) => {
        if (!date) return null;

        // Si c'est une Date object, extraire year/month/day en local puis créer en UTC
        const d = new Date(date);
        if (isNaN(d.getTime())) {
            return null;
        }

        // Extraire composants en timezone locale
        const year = d.getFullYear();
        const month = d.getMonth(); // 0-11
        const day = d.getDate();

        // Recréer en UTC (mois 0-11, donc pas besoin de +1)
        return new Date(Date.UTC(year, month, day, 0, 0, 0, 0)).toISOString();
    };

    /**
     * Appliquer les filtres sélectionnés
     */
    const handleApplyFilters = () => {
        if (!filterValues.startDate || !filterValues.endDate) {
            return;
        }

        // ✅ VALIDATION: Cosmos DB est rapide, on autorise jusqu'à 1 an (365 jours)
        const diffMs = new Date(filterValues.endDate) - new Date(filterValues.startDate);
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        const maxDays = 365; // 1 an max (Cosmos DB est ultra-rapide)

        if (diffDays > maxDays) {
            alert(t('DATE_RANGE_TOO_LARGE', { maxDays, diffDays }));
            return;
        }

        const startDateNormalized = normalizeDate(filterValues.startDate);
        const endDateNormalized = normalizeDate(filterValues.endDate);

        if (!startDateNormalized || !endDateNormalized) {
            return;
        }

        const filters = {
            startDate: startDateNormalized,
            endDate: endDateNormalized,
            scope: decodedScope, // IMPORTANT: Utiliser decodedScope pour partition pruning
            timezone: selectedTimezone, // Timezone pour conversion des heures
            ...dynamicFilters // Inclure flowKey, queueKey, agentKey
        };

        setAppliedFilters(filters);
    };

    /**
     * Gérer les changements de filtres dynamiques (flows/queues/agents)
     */
    const handleDynamicFilterChange = (filters) => {
        setDynamicFilters(filters);

        // Auto-apply filters when dynamic filters change
        if (filterValues.startDate && filterValues.endDate) {
            const startDateNormalized = normalizeDate(filterValues.startDate);
            const endDateNormalized = normalizeDate(filterValues.endDate);

            if (startDateNormalized && endDateNormalized) {
                const updatedFilters = {
                    startDate: startDateNormalized,
                    endDate: endDateNormalized,
                    scope: decodedScope,
                    timezone: selectedTimezone,
                    ...filters // Use the new filters directly
                };
                setAppliedFilters(updatedFilters);
            }
        }
    };

    /**
     * Charger l'enregistrement et la transcription pour un appel
     * Utilise directement les données de callsList (pas de service externe)
     * @param {string} callId - L'ID de l'appel
     * @param {string} recordingScopeId - Non utilisé, gardé pour compatibilité
     */
    const loadRecordingAndTranscription = async (callId, recordingScopeId) => {
        try {
            // ✅ CHECK CACHE LOCAL : Si déjà chargé, ne rien faire
            if (recordingUrls[callId] !== undefined && transcriptions[callId] !== undefined) {
                console.log(`✅ Using cached data for call ${callId}`);
                return;
            }

            // Chercher les données directement dans callsList
            const callData = callsList.find(call => call.callId === callId);
            
            if (!callData) {
                console.log(`⚠️ Call ${callId} not found in callsList`);
                setRecordingUrls(prev => ({ ...prev, [callId]: null }));
                setTranscriptions(prev => ({ ...prev, [callId]: null }));
                return;
            }

            console.log(`🔍 Found call data for ${callId}:`, {
                hasRecordingBlobUrl: !!callData.recordingBlobUrl,
                isTranscriptionActive: callData.isTranscriptionActive,
                hasTranscriptionText: !!callData.transcriptionText
            });

            // 🎧 CHARGER L'ENREGISTREMENT AUDIO
            // Le SAS est généré côté Data_Pipe (recording-sas) à partir de
            // RECORDING_FILE_URL du doc CDR → bon compte de stockage garanti
            // (les recordings ACS vivent sur strrecordingfilesprodne, pas sur le
            // compte de l'ancien BlobSasGenerator de Provisioning_v2 → 404).
            if (recordingUrls[callId] === undefined) {
                const { getRecordingSasUrl } = await import('../../Services/AnalyticsService');
                const sasUrl = await getRecordingSasUrl(tenantId, callId, token, 'recording');
                setRecordingUrls(prev => ({ ...prev, [callId]: sasUrl || null }));
                if (sasUrl) {
                    console.log(`🎧 Recording SAS loaded for call ${callId}`);
                } else {
                    console.log(`⚠️ No recording available for call ${callId}`);
                }
            }
            
            // 📝 CHARGER LA TRANSCRIPTION
            // La transcription ACS est un blob .json séparé (pas inline dans la liste).
            // On la récupère + parse côté Data_Pipe (recording-transcript) → segments.
            if (transcriptions[callId] === undefined) {
                if (callData.transcriptionAvailable === false) {
                    setTranscriptions(prev => ({ ...prev, [callId]: null }));
                } else {
                    const { getRecordingTranscript } = await import('../../Services/AnalyticsService');
                    const res = await getRecordingTranscript(tenantId, callId, token);
                    const hasContent = res && ((Array.isArray(res.segments) && res.segments.length > 0) || (res.text && res.text.trim()));
                    setTranscriptions(prev => ({ ...prev, [callId]: hasContent ? res : null }));
                    console.log(hasContent ? `📝 Transcription loaded for call ${callId}` : `⚠️ No transcription for call ${callId}`);
                }
            }
            
        } catch (error) {
            console.error(`❌ Error loading recording/transcription for call ${callId}:`, error);
            setRecordingUrls(prev => ({ ...prev, [callId]: null }));
            setTranscriptions(prev => ({ ...prev, [callId]: null }));
        }
    };

    // Contenu de la ligne expand (recording + transcript), rendu en PLEINE LARGEUR
    // via onRenderRow de la DetailsList (évite le double-scroll / clipping d'une cellule).
    const renderCallExpandedDetail = (item) => (
        <div style={{ padding: '12px 24px', width: '100%', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '32px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {/* Recording (gauche) */}
                <div style={{ flex: '0 0 360px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '8px', color: '#323130' }}>{t('RECORDING')}</div>
                    {item.callId in recordingUrls ? (
                        recordingUrls[item.callId] ? (
                            <audio controls style={{ width: '100%', maxWidth: '600px' }}>
                                <source src={recordingUrls[item.callId]} type="audio/wav" />
                                {t('BROWSER_NOT_SUPPORT_AUDIO')}
                            </audio>
                        ) : (
                            <MessageBar messageBarType={MessageBarType.info}>{t('NO_RECORDING_AVAILABLE')}</MessageBar>
                        )
                    ) : (
                        <div style={{ padding: '12px', backgroundColor: '#fff4ce', borderLeft: '3px solid #ffb900', color: '#323130' }}>{t('LOADING_RECORDING')}</div>
                    )}
                </div>

                {/* Transcription → Drawer à droite (même composant que la page recordings) */}
                <div style={{ flex: '0 0 auto' }}>
                    <div style={{ fontWeight: 600, marginBottom: '8px', color: '#323130' }}>{t('TRANSCRIPTION')}</div>
                    {item.callId in transcriptions ? (
                        transcriptions[item.callId] ? (
                            <Button
                                appearance="secondary"
                                icon={<AddSquare28Regular />}
                                onClick={() => {
                                    setSelectedTranscription({
                                        callId: item.callId,
                                        segments: transcriptions[item.callId].segments,
                                        text: transcriptions[item.callId].text
                                    });
                                    setShowTranscriptionPanel(true);
                                }}
                            >
                                {t('SHOW_TRANSCRIPTION', { defaultValue: 'View transcription' })}
                            </Button>
                        ) : (
                            <MessageBar messageBarType={MessageBarType.info}>{t('NO_TRANSCRIPTION_AVAILABLE')}</MessageBar>
                        )
                    ) : (
                        <div style={{ padding: '12px', backgroundColor: '#f3f2f1', borderLeft: '3px solid #8a8886', color: '#323130' }}>{t('LOADING_TRANSCRIPTION')}</div>
                    )}
                </div>
            </div>
        </div>
    );

    /**
     * Charger la liste complète des appels
     */
    const loadCallsList = async () => {
        if (!appliedFilters || !token || !tenantId) {
            return;
        }

        setIsLoadingCalls(true);
        try {
            const { executeAnalyticsQuery } = await import('../../Services/AnalyticsService');

            // ⚠️ Les seuils du profil analytics DOIVENT accompagner cette requête.
            //
            // Ils n'étaient injectés que dans les `enrichedFilters` construits PAR WIDGET ;
            // la liste d'appels, elle, envoyait `appliedFilters` brut. Le backend retombait
            // donc sur son seuil de repli (6 s) pendant que les cartes utilisaient celui du
            // profil — et la frontière quick drop / abandon n'était pas la même des deux côtés.
            // Constaté sur le scope Heedify : profil à 0 s → carte « Abandoned Calls » = 80,
            // drill-down = 59. Même période, mêmes appels, deux définitions.
            //
            // Le seuil quick drop est le seul qui change la CATÉGORIE d'un appel, mais on
            // transmet le bloc complet pour que liste et cartes partagent exactement le même
            // jeu de paramètres.
            // Même résolution que pour les widgets (profil du scope, repli sur "default").
            const profile = Object.values(analyticsProfiles).find(p => p.scope === decodedScope)
                || Object.values(analyticsProfiles).find(p => p.scope === 'default');
            const thresholds = profile?.reportConfig?.thresholds;
            const callsFiltersWithThresholds = thresholds
                ? {
                    ...appliedFilters,
                    quickAnswerThreshold: thresholds.quickAnswerThreshold,
                    standardAnswerThreshold: thresholds.standardAnswerThreshold,
                    delayedAnswerThreshold: thresholds.delayedAnswerThreshold,
                    quickDropThreshold: thresholds.quickDropThreshold,
                    slaSeconds: thresholds.slaSeconds
                }
                : appliedFilters;

            const result = await executeAnalyticsQuery(
                tenantId,
                'all-calls-list',
                callsFiltersWithThresholds,
                token
            );

            console.log('📞 Calls List - Result from Analytics Service:', result);
            console.log('📞 Calls List - Data array:', result.data);
            console.log('📞 Calls List - Number of calls:', result.data?.length);
            if (result.data && result.data.length > 0) {
                console.log('📞 Calls List - First call COMPLETE OBJECT:', JSON.stringify(result.data[0], null, 2));
                console.log('📞 Calls List - Available fields:', Object.keys(result.data[0]));
                
                // Vérifier les champs liés à l'audio/recording
                const firstCall = result.data[0];
                console.log('🎵 Audio/Recording fields check:');
                console.log('  - recordingUrl:', firstCall.recordingUrl);
                console.log('  - recording:', firstCall.recording);
                console.log('  - audioUrl:', firstCall.audioUrl);
                console.log('  - recordingBlobUrl:', firstCall.recordingBlobUrl);
                console.log('  - blobUrl:', firstCall.blobUrl);
                console.log('  - mediaUrl:', firstCall.mediaUrl);
                console.log('  - flowId:', firstCall.flowId);
                console.log('  - agentId:', firstCall.agentId);
                
                // Vérifier les champs liés à la transcription
                console.log('📝 Transcription fields check:');
                console.log('  - transcription:', firstCall.transcription);
                console.log('  - transcriptionText:', firstCall.transcriptionText);
                console.log('  - transcript:', firstCall.transcript);
                
                // Log complet de tous les appels pour analyse
                console.log('📋 ALL CALLS LIST DATA:', result.data);
            }

            setCallsList(result.data || []);
        } catch (error) {
            console.error('❌ Error loading calls list:', error);
            setCallsList([]);
        } finally {
            setIsLoadingCalls(false);
        }
    };

    /**
     * Gérer les filtres de colonnes du Calls List
     */
    const handleCallsFilterChange = React.useCallback((columnKey, value) => {
        setCallsFilters((prev) => ({ ...prev, [columnKey]: value }));
    }, []);

    const toggleCallsFilterVisibility = React.useCallback((column) => {
        setCallsFilterVisible((prev) => ({ ...prev, [column]: !prev[column] }));
    }, []);

    /**
     * Drill-down depuis une carte de comptage : bascule sur « Calls List » filtrée sur la
     * population de la carte. Le filtrage est LOCAL — la liste contient tous les appels de la
     * période (Data_Pipe a levé le plafond de 300 lignes), donc pas de nouvel aller-retour.
     * On REMPLACE les filtres au lieu de les fusionner : le drill-down doit montrer exactement
     * ce que la carte annonce, pas l'intersection avec ce que l'utilisateur avait tapé avant.
     */
    const handleWidgetDrillDown = React.useCallback((widgetId) => {
        const filters = CALL_DRILLDOWNS[widgetId];
        if (!filters) return;
        setCallsFilters(filters);
        setDrillDownSource(widgetId);
        setSelectedTab('calls');
    }, []);

    const clearDrillDown = React.useCallback(() => {
        setCallsFilters({});
        setDrillDownSource(null);
    }, []);

    /**
     * Filtrer les appels en fonction des filtres de colonnes et ajouter les lignes expandées
     */
    const filteredCallsList = React.useMemo(() => {
        // Mots-clés spéciaux : taper l'un d'eux dans un filtre de colonne sélectionne
        // les appels dont CETTE colonne est VIDE (ex. Queue vide). Vaut pour toutes les colonnes.
        const EMPTY_TOKENS = ['(empty)', 'empty', '(vide)', 'vide', '-', '—'];
        const filtered = callsList.filter((call) => {
            return Object.entries(callsFilters).every(([key, value]) => {
                if (!value) return true;
                const raw = String(value);
                const cellValue = call[key];
                const isEmptyCell =
                    cellValue === null ||
                    cellValue === undefined ||
                    String(cellValue).trim() === '' ||
                    String(cellValue).trim() === '—';
                // Sélection EXACTE depuis un menu déroulant (préfixe "=").
                if (raw.startsWith('=')) {
                    const target = raw.slice(1).trim().toLowerCase();
                    return !isEmptyCell && String(cellValue).trim().toLowerCase() === target;
                }
                const v = raw.trim().toLowerCase();
                // Filtre "vide" demandé → ne garder que les cellules vides.
                if (EMPTY_TOKENS.includes(v)) return isEmptyCell;
                // Sinon, une cellule vide ne matche jamais un filtre texte.
                if (isEmptyCell) return false;
                return String(cellValue).toLowerCase().includes(v);
            });
        });

        // Insérer les lignes expandées
        const result = [];
        filtered.forEach(call => {
            result.push(call);
            if (expandedRows[call.callId]) {
                result.push({
                    ...call,
                    isExpandedRow: true,
                    uniqueKey: `${call.callId}-expanded`
                });
            }
        });
        return result;
    }, [callsList, callsFilters, expandedRows]);

    /**
     * Charger la liste des appels quand l'onglet change
     */
    useEffect(() => {
        if (selectedTab === 'calls' && appliedFilters) {
            loadCallsList();
        }
    }, [selectedTab, appliedFilters]);

    /**
     * Helper pour rendre le TextField avec icône de filtre
     */
    const renderFilterTextField = (columnKey, minWidth = 150) => (
        <div
            style={{ position: 'relative', minWidth, flex: 1, display: 'flex', alignItems: 'center' }}
            title={t('FILTER_EMPTY_HINT', { defaultValue: 'Type "-" or "(empty)" to show only rows where this column is empty' })}
        >
          <TextField
                value={callsFilters[columnKey] || ''}
                onChange={(e, value) => handleCallsFilterChange(columnKey, value)}
                placeholder={t('FILTER')}
                styles={{ 
                    root: { width: '90%' }, 
                    fieldGroup: { 
                        height: 23, 
                        border: '1px solid #d1d1d1',
                        borderRadius: 4,
                        paddingLeft: 3
                    },
                    field: { paddingLeft: 0 }
                }}
                onClick={(e) => e.stopPropagation()}
            />
        </div>
    );

    /**
     * Filtre par MENU DÉROULANT (colonnes catégorielles) : liste les valeurs distinctes
     * présentes dans les appels (dédupliquées + triées) + "All" + "— (empty)".
     * La valeur stockée est préfixée "=" → match EXACT côté filteredCallsList ; "-" = vide.
     */
    // `labelOf` : les colonnes dont la donnée est une CLÉ stable (catégorie de taxonomie)
    // doivent proposer le libellé traduit dans le menu, tout en filtrant sur la clé brute.
    const renderFilterDropdown = (columnKey, minWidth = 150, labelOf = null) => {
        const seen = new Set();
        callsList.forEach((c) => {
            const v = c[columnKey];
            if (v !== null && v !== undefined && String(v).trim() !== '' && String(v).trim() !== '—') {
                seen.add(String(v));
            }
        });
        const distinct = Array.from(seen).sort((a, b) => a.localeCompare(b));

        const current = callsFilters[columnKey] || '';
        let selectedKey = '__ALL__';
        if (current === '-' || current === '—') selectedKey = '__EMPTY__';
        else if (current.startsWith('=')) selectedKey = current.slice(1);

        const options = [
            { key: '__ALL__', text: t('ALL', { defaultValue: 'All' }) },
            { key: '__EMPTY__', text: t('EMPTY_VALUE', { defaultValue: '— (empty)' }) },
            ...distinct.map((v) => ({ key: v, text: labelOf ? labelOf(v) : v })),
        ];

        return (
            <div style={{ minWidth, flex: 1 }} onClick={(e) => e.stopPropagation()}>
                <Dropdown
                    selectedKey={selectedKey}
                    options={options}
                    onChange={(e, opt) => {
                        const val =
                            opt.key === '__ALL__' ? '' :
                            opt.key === '__EMPTY__' ? '-' :
                            `=${opt.key}`;
                        handleCallsFilterChange(columnKey, val);
                    }}
                    styles={{
                        root: { width: '95%' },
                        title: { height: 23, lineHeight: '21px', fontSize: 12, paddingLeft: 6 },
                        caretDownWrapper: { height: 23, lineHeight: '21px' },
                    }}
                />
            </div>
        );
    };

    /**
     * Colonnes du DetailsList avec filtres - useMemo pour forcer le re-render
     */
    const callsListColumns = React.useMemo(() => {
        return [
            {
                key: 'expand',
                name: '',
                fieldName: '',
                minWidth: 20,
                maxWidth: 20,
                onRender: (item) => {
                    if (item.isExpandedRow) {
                        return null;
                    }
                    // Bouton expand uniquement si l'appel a un enregistrement ou une
                    // transcription (RECORDING_ENABLED / isTranscriptionActive du CDR).
                    if (!item.recordingAvailable && !item.transcriptionAvailable) {
                        return null;
                    }

                    const ChevronIcon = expandedRows[item.callId] ? ChevronDown20Regular : ChevronRight20Regular;
                    return (
                        <div 
                            onClick={() => {
                                const newExpandedState = { ...expandedRows, [item.callId]: !expandedRows[item.callId] };
                                setExpandedRows(newExpandedState);
                                // Si on expand et qu'on n'a pas encore l'URL, la charger
                                if (!expandedRows[item.callId] && !recordingUrls[item.callId]) {
                                     // Utiliser  || item.agentId || item. comme recordingScopeId (c'est le PartitionKey dans Cosmos)
                                     console.log(`📡 Loading recording/transcription for call ${item.callId} with scope ${item.flowId || item.agentId}`);
                                    loadRecordingAndTranscription(item.callId, item.flowId || item.agentId || item.callerNumber );
                                }
                            }}
                            style={{ 
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '42px',
                                minHeight: '42px'
                            }}
                            title={expandedRows[item.callId] ? 'Collapse' : 'Expand'}
                        >
                            <ChevronIcon style={{ fontSize: '20px',fontWeight: 'bold', color: 'rgb(91, 95, 199)' }} />
                        </div>
                    );
                }
            },
            {
                key: 'timestamp',
                name: t('START_TIME'), 
                fieldName: 'timestamp', 
                minWidth: callsFilterVisible.timestamp ? 250 : 130, 
                maxWidth: callsFilterVisible.timestamp ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.timestampEpoch ? formatEpochTz(item.timestampEpoch, selectedTimezone, i18n.language) : item.timestamp}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('START_TIME')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.timestamp ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('timestamp');
                                }}
                            />
                        </div>
                        {callsFilterVisible.timestamp && renderFilterTextField('timestamp', 100)}
                    </div>
                )
            },
            {
                // Catégorie de taxonomie (clé stable renvoyée par Data_Pipe, traduite ici).
                // Colonne indispensable au drill-down : sans elle, rien n'explique à
                // l'utilisateur pourquoi la liste est filtrée ni sur quoi.
                key: 'category',
                name: t('CALL_CATEGORY', { defaultValue: 'Outcome' }),
                fieldName: 'category',
                minWidth: callsFilterVisible.category ? 250 : 120,
                maxWidth: callsFilterVisible.category ? 300 : 170,
                onRender: (item) => item.isExpandedRow ? null : (
                    <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>
                        {item.category
                            ? t(CALL_CATEGORY_LABELS[item.category] || item.category, { defaultValue: item.category })
                            : '—'}
                    </div>
                ),
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('CALL_CATEGORY', { defaultValue: 'Outcome' })}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.category ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('category');
                                }}
                            />
                        </div>
                        {callsFilterVisible.category && renderFilterDropdown('category', 140,
                            (v) => t(CALL_CATEGORY_LABELS[v] || v, { defaultValue: v }))}
                    </div>
                )
            },
            {
                key: 'direction',
                name: t('DIRECTION'),
                fieldName: 'direction',
                minWidth: callsFilterVisible.direction ? 250 : 100, 
                maxWidth: callsFilterVisible.direction ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.direction}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('DIRECTION')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.direction ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('direction');
                                }}
                            />
                        </div>
                        {callsFilterVisible.direction && renderFilterDropdown('direction', 120)}
                    </div>
                )
            },
            {
                // COLONNE RAPPEL.
                //
                // Elle répond à une question que rien d'autre ne pose dans cette liste : cette
                // ligne fait-elle partie d'un rappel, et de quel côté ? Sans elle, la jambe
                // sortante se confond avec un appel sortant ordinaire — un superviseur qui
                // compte ses sortants compterait des rappels.
                //
                // Trois états seulement, et le vide domine : la colonne reste discrète sur une
                // file qui n'utilise pas le rappel.
                key: 'callback',
                name: t('CALLBACK', { defaultValue: 'Callback' }),
                fieldName: 'callbackRole',
                minWidth: 110,
                maxWidth: 150,
                onRender: (item) => {
                    if (item.isExpandedRow || !item.callbackRole) return null;

                    const estJambe = item.callbackRole === 'callback';
                    // La ponctualité n'a que la jambe pour support, et elle a TROIS valeurs :
                    // à l'heure, en retard, et « pas d'échéance promise ». Rendre ce dernier cas
                    // comme un retard inventerait un manquement qui n'a pas eu lieu.
                    const couleur = !estJambe ? '#1B3A6B'
                        : item.callbackOnTime === true ? '#00A4A6'
                            : item.callbackOnTime === false ? '#D13438'
                                : '#605e5c';
                    const libelle = estJambe
                        ? t('CALLBACK_LEG', { defaultValue: 'Callback' })
                        : t('CALLBACK_REQUESTED', { defaultValue: 'Requested' });
                    // Le numéro de tentative ne s'affiche qu'à partir de la deuxième : « 1 » sur
                    // chaque ligne serait du bruit, « 2 » est une information.
                    const tentative = estJambe && item.callbackAttempt > 1
                        ? ` · ${item.callbackAttempt}` : '';

                    return (
                        <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center',
                                padding: '2px 8px', borderRadius: '10px',
                                fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
                                color: couleur, border: `1px solid ${couleur}`
                            }}>
                                {libelle}{tentative}
                            </span>
                        </div>
                    );
                }
            },
            { 
                key: 'callerNumber', 
                name: t('CALLER'), 
                fieldName: 'callerNumber', 
                minWidth: callsFilterVisible.callerNumber ? 250 : 100, 
                maxWidth: callsFilterVisible.callerNumber ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.callerNumber}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('CALLER')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.callerNumber ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('callerNumber');
                                }}
                            />
                        </div>
                        {callsFilterVisible.callerNumber && renderFilterTextField('callerNumber', 100)}
                    </div>
                )
            },
            { 
                key: 'callerType', 
                name: t('TYPE'), 
                fieldName: 'callerType', 
                minWidth: callsFilterVisible.callerType ? 250 : 100, 
                maxWidth: callsFilterVisible.callerType ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.callerType}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('TYPE')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.callerType ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('callerType');
                                }}
                            />
                        </div>
                        {callsFilterVisible.callerType && renderFilterDropdown('callerType', 120)}
                    </div>
                )
            },
            {
                key: 'transferredToAgent',
                name: t('TRANSFERRED_BY_AGENT'),
                fieldName: 'transferredToAgent',
                minWidth: callsFilterVisible.transferredToAgent ? 260 : 200,
                maxWidth: callsFilterVisible.transferredToAgent ? 340 : 260,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.transferredToAgent || '—'}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('TRANSFERRED_BY_AGENT')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.transferredToAgent ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('transferredToAgent');
                                }}
                            />
                        </div>
                        {callsFilterVisible.transferredToAgent && renderFilterDropdown('transferredToAgent', 140)}
                    </div>
                )
            },
            {
                key: 'transferredToFlow',
                name: t('TRANSFERRED_BY_FLOW'),
                fieldName: 'transferredToFlow',
                minWidth: callsFilterVisible.transferredToFlow ? 260 : 200,
                maxWidth: callsFilterVisible.transferredToFlow ? 340 : 260,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.transferredToFlow || '—'}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('TRANSFERRED_BY_FLOW')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.transferredToFlow ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('transferredToFlow');
                                }}
                            />
                        </div>
                        {callsFilterVisible.transferredToFlow && renderFilterDropdown('transferredToFlow', 140)}
                    </div>
                )
            },
            {
                key: 'agentName',
                name: t('AGENT'), 
                fieldName: 'agentName', 
                minWidth: callsFilterVisible.agentName ? 250 : 100,
                maxWidth: callsFilterVisible.agentName ? 300 : 150,
                // agentName vient du CDR (AGENT_DISPLAYNAME). Pour les OBO sortants il est vide
                // (pas de file d'où résoudre le nom) → fallback : mapper agentId → nom via
                // la liste AGENTS complète (Redux) via resolveAgentName().
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{resolveAgentName(item)}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('AGENT')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.agentName ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('agentName');
                                }}
                            />
                        </div>
                        {callsFilterVisible.agentName && renderFilterDropdown('agentName', 120)}
                    </div>
                )
            },
            { 
                key: 'queueName', 
                name: t('QUEUE'), 
                fieldName: 'queueName', 
                minWidth: callsFilterVisible.queueName ? 250 : 100,
                maxWidth: callsFilterVisible.queueName ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.queueName}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('QUEUE')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.queueName ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('queueName');
                                }}
                            />
                        </div>
                        {callsFilterVisible.queueName && renderFilterDropdown('queueName', 120)}
                    </div>
                )
            },
            { 
                key: 'flowName', 
                name: t('FLOW'), 
                fieldName: 'flowName', 
                minWidth: callsFilterVisible.flowName ? 250 : 100,
                maxWidth: callsFilterVisible.flowName ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.flowName}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('FLOW')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.flowName ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('flowName');
                                }}
                            />
                        </div>
                        {callsFilterVisible.flowName && renderFilterDropdown('flowName', 120)}
                    </div>
                )
            },
            { 
                key: 'waitTime', 
                name: t('WAIT_TIME'), 
                fieldName: 'waitTime', 
                minWidth: callsFilterVisible.waitTime ? 250 : 100, 
                maxWidth: callsFilterVisible.waitTime ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.waitTime}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('WAIT_TIME')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.waitTime ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('waitTime');
                                }}
                            />
                        </div>
                        {callsFilterVisible.waitTime && renderFilterTextField('waitTime', 100)}
                    </div>
                )
            },
            { 
                key: 'duration', 
                name: t('DURATION'), 
                fieldName: 'duration', 
                minWidth: callsFilterVisible.duration ? 250 : 100, 
                maxWidth: callsFilterVisible.duration ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.duration}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('DURATION')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.duration ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('duration');
                                }}
                            />
                        </div>
                        {callsFilterVisible.duration && renderFilterTextField('duration', 100)}
                    </div>
                )
            },
            { 
                key: 'alertTime', 
                name: t('AGENT_ALERT_TIME'), 
                fieldName: 'alertTime', 
                minWidth: callsFilterVisible.alertTime ? 250 : 150, 
                maxWidth: callsFilterVisible.alertTime ? 300 : 200,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.alertTime}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('AGENT_ALERT_TIME')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.alertTime ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('alertTime');
                                }}
                            />
                        </div>
                          {callsFilterVisible.alertTime && renderFilterTextField('alertTime', 100)}
                
                      
    
                    </div>
                )
            },
            { 
                key: 'queuedTimes', 
                name: t('QUEUE_ENTRIES'), 
                fieldName: 'queuedTimes', 
                minWidth: callsFilterVisible.queuedTimes ? 250 : 150,
                maxWidth: callsFilterVisible.queuedTimes ? 300 : 200,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.queuedTimes}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('QUEUE_ENTRIES')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.queuedTimes ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('queuedTimes');
                                }}
                            />
                        </div>
                        {callsFilterVisible.queuedTimes && renderFilterTextField('queuedTimes', 100)}
                    </div>
                )
            },
            { 
                key: 'terminationReason', 
                name: t('END_REASON'), 
                fieldName: 'terminationReason', 
                minWidth: callsFilterVisible.terminationReason ? 250 : 150, 
                maxWidth: callsFilterVisible.terminationReason ? 300 : 200,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.terminationReason}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('END_REASON')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.terminationReason ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('terminationReason');
                                }}
                            />
                        </div>
                        {callsFilterVisible.terminationReason && renderFilterDropdown('terminationReason', 120)}
                    </div>
                )
            },
            { 
                key: 'interactionType', 
                name: t('INTERACTION_TYPE'), 
                fieldName: 'interactionType', 
                minWidth: callsFilterVisible.interactionType ? 250 : 150, 
                maxWidth: callsFilterVisible.interactionType ? 300 : 200,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.interactionType}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('INTERACTION_TYPE')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.interactionType ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('interactionType');
                                }}
                            />
                        </div>
                        {callsFilterVisible.interactionType && renderFilterDropdown('interactionType', 120)}
                    </div>
                )
            },
            {
                key: 'callId',
                name: t('CALL_ID'),
                fieldName: 'callId',
                minWidth: callsFilterVisible.callId ? 250 : 230,
                maxWidth: callsFilterVisible.callId ? 300 : 250,
                onRender: (item) => {
                    // La ligne expand est rendue en pleine largeur par onRenderRow.
                    if (item.isExpandedRow) {
                        return null;
                    }
                    return <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px', paddingLeft: '8px' }}>{item.callId}</div>;
                },
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('CALL_ID')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.callId ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('callId');
                                }}
                            />
                        </div>
                        {callsFilterVisible.callId && renderFilterTextField('callId', 100)}
                    </div>
                )
            }
        ];
    }, [callsFilterVisible, callsFilters, t, handleCallsFilterChange, toggleCallsFilterVisibility, renderFilterTextField, renderFilterDropdown, expandedRows, selectedTimezone, i18n.language, supervisorNameById, resolveAgentName]);

    /**
     * Exporter la liste des appels vers Excel (async avec Blob Storage)
     */
    const handleExportToExcel = async () => {
        if (!appliedFilters || !token || !tenantId) {
            return;
        }

        setIsExportingExcel(true);
        setShowExportDialog(true);
        setExportProgress(0);
        setExportMessage(t('EXPORT_STARTING'));
        setExportDownloadUrl(null);

        try {
            // Start async export job — inject scope so Cosmos can use the HPK partition.
            // Les seuils du profil accompagnent l'export pour la MÊME raison que la liste
            // d'appels : sans eux le backend retombe sur son seuil de repli et la colonne
            // « Status » du fichier ne correspond plus à ce que montrent les cartes.
            const exportProfile = Object.values(analyticsProfiles).find(p => p.scope === decodedScope)
                || Object.values(analyticsProfiles).find(p => p.scope === 'default');
            const exportThresholds = exportProfile?.reportConfig?.thresholds;
            const exportFilters = {
                ...appliedFilters,
                scope: decodedScope,
                // Les filtres d'ÉCRAN accompagnent l'export, sinon le fichier ne correspond pas
                // à ce que l'utilisateur a sous les yeux. Ils couvrent le drill-down depuis une
                // carte KPI (`{category: '=handled'}`) ET les filtres de colonnes tapés à la
                // main : les deux vivaient uniquement dans `filteredCallsList`, jamais transmis.
                // Constaté : drill-down « Answered Calls » à 340 lignes à l'écran, 1200 dans le
                // fichier. Le backend applique les mêmes règles (cf. matchesScreenFilters).
                callsFilters,
                ...(exportThresholds ? { quickDropThreshold: exportThresholds.quickDropThreshold } : {})
            };
            const startResult = await startExcelExport(tenantId, exportFilters, token);

            if (!startResult.success) {
                throw new Error(startResult.error);
            }

            setExportMessage(t('EXPORT_IN_PROGRESS'));

            // Poll for status
            const finalStatus = await pollExportStatus(
                startResult.jobId,
                token,
                (status) => {
                    setExportProgress(status.progress || 0);
                    setExportMessage(status.message || 'En cours...');
                }
            );

            setExportProgress(100);
            setExportMessage(`${t('EXPORT_COMPLETED')} ${finalStatus.totalRecords} ${t('RECORDS_EXPORTED')}`);
            setExportDownloadUrl(finalStatus.downloadUrl);

            // Auto-download by navigating directly to the SAS URL (an <a> click).
            // We do NOT fetch() the blob: the app's CSP connect-src does not allow
            // *.blob.core.windows.net, so a fetch is refused ("Refused to connect").
            // A navigation/download is not governed by connect-src, and the blob is
            // served with an xlsx content-type (+ Content-Disposition attachment from
            // the backend) so the browser downloads it without leaving the SPA.
            try {
                const link = document.createElement('a');
                link.href = finalStatus.downloadUrl;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (downloadErr) {
                console.error('Auto-download failed, user can use the manual button:', downloadErr);
            }

        } catch (error) {
            setExportMessage(`${t('EXPORT_ERROR')}: ${error.message}`);
        } finally {
            setIsExportingExcel(false);
        }
    };

    /**
     * Fermer la dialog d'export
     */
    const closeExportDialog = () => {
        setShowExportDialog(false);
        setExportProgress(0);
        setExportMessage('');
        setExportDownloadUrl(null);
    };

    /**
     * Exporter l'ONGLET COURANT en Excel : un classeur, UNE FEUILLE PAR WIDGET.
     *
     * On réutilise les données DÉJÀ chargées (`widgetsData`) : aucun appel réseau, l'export
     * est instantané et reflète exactement ce que l'utilisateur voit à l'écran (mêmes
     * filtres, même période).
     */
    const handleExportTabExcel = () => {
        try {
            const tabId = selectedTab;

            // Widgets de l'onglet : ceux du Dashboard viennent de la config statique,
            // ceux des onglets custom des instances enregistrées dans le profil.
            let widgets;
            if (tabId === 'dashboard') {
                widgets = (dashboardConfig?.widgets || [])
                    .filter(w => DASHBOARD_WIDGET_IDS.includes(w.id))
                    .map(w => ({
                        title: t(w.titleKey || w.title, { defaultValue: w.title }),
                        data: widgetsData[w.id],
                        config: w
                    }));
            } else {
                widgets = Object.values(widgetInstances)
                    .filter(inst => inst.tabId === tabId)
                    .map(inst => {
                        const catalogWidget = WIDGET_CATALOG.find(c => c.id === inst.widgetId);
                        const dashWidget = (dashboardConfig?.widgets || []).find(w => w.id === inst.widgetId);
                        const cfg = dashWidget || catalogWidget || {};
                        // Un VRAI renommage prime ; un `name` resté égal à l'un des libellés par
                        // défaut du widget n'en est pas un → on repasse par le libellé traduit.
                        const title = resolveWidgetTitle(t, i18n, {
                            widgetId: inst.widgetId,
                            name: inst.name,
                            catalogName: catalogWidget?.name,
                            fallback: cfg.title || cfg.name
                        });
                        return { title, data: widgetsData[inst.id], config: cfg };
                    });
            }

            if (!widgets.length) {
                alert(t('NO_DATA_TO_EXPORT') || 'No data to export');
                return;
            }

            const tabName = tabId === 'dashboard'
                ? (t('DASHBOARD') || 'Dashboard')
                : (customTabs.find(tab => tab.id === tabId)?.name || tabId);

            const written = exportTabToExcel(widgets, tabName, decodedScope, t, selectedTimezone);

            // Aucun widget n'avait de donnée : le dire, plutôt que de livrer un fichier vide.
            if (written === 0) {
                alert(t('NO_DATA_TO_EXPORT') || 'No data to export');
            }
        } catch (error) {
            console.error('❌ Excel tab export failed:', error);
            alert(`${t('EXPORT_ERROR') || 'Export failed'}: ${error.message}`);
        }
    };

    /**
     * Exporter TOUT le rapport en PDF : page de garde + 1 section par tab.
     * Skip "Calls List" (pas pertinent pour un rapport analytique).
     */
    const handleExportPDF = async () => {
        setIsExporting(true);
        const originalTab = selectedTab;
        try {
            // Laisse le temps à la grille de se re-render sans CSS transforms
            // (sinon html2canvas capture les widgets à leur position untransformed = (0,0)).
            await new Promise(r => setTimeout(r, 500));

            // Liste des tabs à exporter dans l'ordre : Dashboard d'abord, puis tous les autres
            // (system + custom), excluant "calls" (Calls List).
            const tabsToExport = [
                { id: 'dashboard', displayName: t('DASHBOARD') || 'Dashboard' },
                ...customTabs
                    .filter(t => t.id !== 'calls')
                    .map(t => ({ id: t.id, displayName: t.name }))
            ];

            // Dates au format de la LANGUE de l'utilisateur (12/07/2026 en FR, 7/12/2026 en
            // EN) : elles étaient forcées en 'en-US', ce qui affichait une date américaine
            // sur un rapport français — et se lit de travers (le 7/12 est le 12 juillet).
            const locale = i18n.language || undefined;
            const startDate = filterValues.startDate?.toLocaleDateString(locale) || '';
            const endDate = filterValues.endDate?.toLocaleDateString(locale) || '';
            const filename = `analytics-report_${decodedScope}_${startDate.replace(/\//g, '-')}_to_${endDate.replace(/\//g, '-')}.pdf`;

            await exportTabsToPDF({
                tabs: tabsToExport,
                elementId: 'dashboard-container',
                filename,
                coverPageData: {
                    title: t('PDF_REPORT_TITLE'),
                    scope: decodedScope,
                    // Tiret simple, pas de flèche : les polices standard de jsPDF sont encodées
                    // en WinAnsi et « → » n'y existe pas — il s'imprimait « !’ ».
                    dateRange: startDate && endDate ? `${startDate} - ${endDate}` : '-',
                    generatedAt: new Date().toLocaleString()
                },
                // Le PDF était entièrement en anglais en dur, même pour un utilisateur en
                // français. L'util PDF ne connaît pas i18n : on lui passe les libellés déjà
                // traduits.
                labels: {
                    period: t('PDF_PERIOD'),
                    generated: t('PDF_GENERATED'),
                    contents: t('PDF_CONTENTS'),
                    page: t('PDF_PAGE'),
                    continued: t('PDF_CONTINUED')
                },
                onTabSwitch: async (tabId) => {
                    setSelectedTab(tabId);
                    // Attendre que React render + react-grid-layout settle.
                    await new Promise(r => setTimeout(r, 600));
                    // Poll : attendre que TOUS les widgets aient fini de loader
                    // (max 10s pour custom tabs avec batch fetch).
                    const startWait = Date.now();
                    while (Date.now() - startWait < 10000) {
                        const stillLoading = document.querySelectorAll(
                            '.widget-loading, .widget-loading-state'
                        ).length;
                        if (stillLoading === 0) break;
                        await new Promise(r => setTimeout(r, 200));
                    }
                    // Délai pour finir les animations Recharts (par défaut ~1.5s, mais marge pour donut/bar).
                    await new Promise(r => setTimeout(r, 3000));
                },
                onProgress: (current, total, name) => {
                    console.log(`📄 PDF: ${current}/${total} — ${name}`);
                    setPdfExportProgress({ current, total, tabName: name });
                }
            });

            console.log('✅ PDF export complete');
        } catch (error) {
            console.error('❌ PDF export failed:', error);
            alert(`Export PDF failed: ${error.message}`);
        } finally {
            setSelectedTab(originalTab);
            setIsExporting(false);
            setPdfExportProgress({ current: 0, total: 0, tabName: '' });
        }
    };

    /**
     * Dashboard positions now saved via Analytics Profile (saveDashboardLayoutToBackend)
     * Old Portal Settings method removed - all saves go through Analytics Profile service
     */

    /**
     * handleResetLayout removed - Reset button no longer in UI
     */

    /**
     * Ouvrir le catalogue et sélectionner un widget pour l'ajouter
     */
    const handleOpenCatalog = () => {
        setIsCatalogOpen(true);
    };

    /**
     * Sélectionner un widget depuis le catalogue pour l'instancier
     */
    const handleSelectWidgetFromCatalog = (widgetInfo) => {
        setSelectedWidgetInfo(widgetInfo);
        setEditingInstance(null);
        setIsCatalogOpen(false);
        setIsInstanceConfigOpen(true);
    };

    /**
     * Sauvegarder une instance de widget
     */
    const handleSaveWidgetInstance = (instance) => {
        const updatedWidgets = {
            ...widgetInstances,
            [instance.id]: {
                ...instance,
                updatedAt: new Date().toISOString()
            }
        };
        setWidgetInstances(updatedWidgets);
    };

    /**
     * Éditer une instance de widget existante
     */
    const handleEditWidgetInstance = (instanceId) => {
        const instance = widgetInstances[instanceId];
        if (!instance) return;

        const widgetInfo = WIDGET_CATALOG.find(w => w.id === instance.widgetId);
        setSelectedWidget(widgetInfo);  // Utiliser setSelectedWidget pour le panel
        setEditingInstance(instance);
        setShowConfigPanel(true);  // Ouvrir le panel à droite au lieu du dialog
    };

    /**
     * Supprimer une instance de widget d'un tab
     */
    const handleDeleteWidgetInstance = (instanceId) => {
        const updatedWidgets = { ...widgetInstances };
        delete updatedWidgets[instanceId];
        setWidgetInstances(updatedWidgets);
    };

    /**
     * New handlers for drag and drop from catalog panel
     */

    // Handle widget drag start from catalog
    const handleWidgetDragStart = (widget) => {
        setDraggedWidget(widget);
    };

    // Handle widget click from catalog (alternative to drag)
    const handleWidgetClick = (widget) => {
        setSelectedWidget(widget);
        setEditingInstance(null);
        setShowConfigPanel(true);
    };

    /**
     * Recharger les données d'un seul widget après modification de sa config
     */
      const reloadSingleWidgetData = async (widgetData) => {
        try {
            
            // Config : widget du Dashboard (dashboardConfig) OU widget de catalogue (onglet custom).
            // Sans le repli catalogue, un widget custom n'avait pas de queryId résolu ici → data=null.
            let widgetConfig = dashboardConfig?.widgets?.find(w => w.id === widgetData.widgetId);
            if (!widgetConfig) {
                const catalogWidget = WIDGET_CATALOG.find(w => w.id === widgetData.widgetId);
                if (catalogWidget?.dataSource?.queryId) {
                    widgetConfig = {
                        id: catalogWidget.id,
                        type: catalogWidget.type,
                        queryId: catalogWidget.dataSource.queryId,
                        config: catalogWidget.config || {}
                    };
                }
            }

            // Calculer les filtres pour ce widget
            let widgetFilters;
            
            if (widgetData.config?.useGlobalFilters !== false) {
                // Mode: FILTRES GLOBAUX
                widgetFilters = appliedFilters;
            } else {
                // Mode: FILTRES PERSONNALISÉS
                const customPeriod = widgetData.config?.customPeriod;
                const customFilters = widgetData.config?.customFilters || {};
                
                widgetFilters = {
                    scope: decodedScope,
                    timezone: selectedTimezone
                };
                
                if (customPeriod) {
                    const dateRange = calculateDateRangeFromPeriod(customPeriod, widgetData.config);
                    if (dateRange.startDate) widgetFilters.startDate = dateRange.startDate;
                    if (dateRange.endDate) widgetFilters.endDate = dateRange.endDate;
                }
                
                if (customFilters.flowKey) widgetFilters.flowKey = customFilters.flowKey;
                if (customFilters.queueKey) widgetFilters.queueKey = customFilters.queueKey;
                if (customFilters.agentKey) widgetFilters.agentKey = customFilters.agentKey;
                
            }
            
            // 🔹 Vérifier si le widget utilise reportConfig comme source
            let data;
            if (widgetConfig?.config?.dataSource === 'reportConfig' && widgetConfig?.config?.dataPath) {
                // Charger depuis reportConfig
                let analyticsProfile = Object.values(analyticsProfiles).find(
                    profile => profile.scope === decodedScope
                );
                
                if (!analyticsProfile) {
                    analyticsProfile = Object.values(analyticsProfiles).find(
                        profile => profile.scope === "default"
                    );
                }
                
                if (analyticsProfile && analyticsProfile.reportConfig) {
                    // Extraire la valeur depuis le reportConfig
                    const pathParts = widgetConfig.config.dataPath.split('.');
                    let value = analyticsProfile.reportConfig;
                    
                    for (const part of pathParts) {
                        value = value?.[part];
                    }
                    
                    // Pour les widgets DonutChart, convertir l'objet en segments
                    if (widgetConfig.type === WidgetTypes.DONUT) {
                        const colors = widgetConfig.config.colors || {};
                        const labels = widgetConfig.config.labels || {};
                        
                        data = Object.entries(value || {}).map(([key, val]) => ({
                            label: `${labels[key] || key} (${val}s)`,
                            value: val,
                            color: colors[key] || '#0078D4',
                            percentage: 0
                        }));
                    } else {
                        // Pour les METRIC widgets, extraire aussi targetValue et maxValue
                        let targetValue = null;
                        if (widgetConfig.config?.targetPath) {
                            const targetParts = widgetConfig.config.targetPath.split('.');
                            targetValue = analyticsProfile.reportConfig;
                            for (const part of targetParts) {
                                targetValue = targetValue?.[part];
                            }
                        }
                        
                        let maxValue = null;
                        if (widgetConfig.config?.maxPath) {
                            const maxParts = widgetConfig.config.maxPath.split('.');
                            maxValue = analyticsProfile.reportConfig;
                            for (const part of maxParts) {
                                maxValue = maxValue?.[part];
                            }
                        }
                        
                        data = { 
                            value: value || 0,
                            maxValue: maxValue,
                            targetValue: targetValue
                        };
                    }
                }
            } else if (widgetConfig?.queryId) {
                // ⚠️ MÊME enrichissement que les chargements par lot (onglet Dashboard et onglets
                // custom). Ce rechargement post-sauvegarde envoyait les filtres BRUTS :
                //   - sans les seuils du profil → le widget rechargé repartait sur les défauts
                //     (quick drop, SLA), donc une valeur différente de celle d'avant la sauvegarde ;
                //   - sans les listes provisionnées → le tableau « par flow » retombait sur les
                //     flows VUS EN CDR : après avoir édité un widget, ses colonnes passaient des
                //     10 flows de l'entité à 24 colonnes dont des flows morts et ceux d'autres
                //     entités, jusqu'au prochain chargement complet de l'onglet.
                let enrichedFilters = buildWidgetFilters(widgetConfig, widgetFilters);

                let profile = Object.values(analyticsProfiles || {}).find(p => p.scope === decodedScope);
                if (!profile) profile = Object.values(analyticsProfiles || {}).find(p => p.scope === 'default');
                if (profile?.reportConfig?.thresholds) {
                    const th = profile.reportConfig.thresholds;
                    enrichedFilters = {
                        ...enrichedFilters,
                        quickAnswerThreshold: th.quickAnswerThreshold,
                        standardAnswerThreshold: th.standardAnswerThreshold,
                        delayedAnswerThreshold: th.delayedAnswerThreshold,
                        quickDropThreshold: th.quickDropThreshold,
                        slaSeconds: th.slaSeconds
                    };
                }

                enrichedFilters = {
                    ...enrichedFilters,
                    ...buildColumnFilters(widgetConfig.queryId, enrichedFilters)
                };

                // Charger depuis l'API SEULEMENT si le widget a un queryId
                data = await fetchWidgetData(
                    widgetConfig,      // Passer la config complète du widget
                    enrichedFilters,   // Les filtres calculés + seuils + colonnes provisionnées
                    tenantId,
                    token
                );
            } else {
                // Pas de queryId ni reportConfig: pas de données à charger
                data = null;
            }
            
            // Écrit sous DEUX clés pour couvrir les deux rendus sans collision :
            //   - id d'INSTANCE : lu par le rendu des onglets custom (renderWidgetContent) ;
            //   - widgetId de BASE : lu par le rendu de l'onglet Dashboard (widgetsData[widget.id]).
            // Namespaces disjoints (id d'instance = "widget-instance-…" vs "widget-…"), donc aucun
            // écrasement croisé ; pour un doublon custom, la case widgetId partagée n'est jamais lue.
            setWidgetsData(prev => {
                const next = { ...prev, [widgetData.id]: data };
                if (widgetData.widgetId && widgetData.widgetId !== widgetData.id) {
                    next[widgetData.widgetId] = data;
                }
                return next;
            });
            
        } catch (error) {
            console.error('❌ Error reloading widget data:', error);
        }
    };
    

   

    // Handle config panel save
    const handleConfigSave = async (widgetData) => {
        const isNewWidget = !widgetInstances[widgetData.id];
        const previousWidgetInstances = widgetInstances;  // pour rollback si la sauvegarde échoue

        // Mettre à jour widgetInstances AVANT de sauvegarder
        const updatedWidgetData = {
            ...widgetData,
            updatedAt: new Date().toISOString()
        };

        // ✅ Créer le nouveau state AVANT de sauvegarder
        const updatedWidgetInstances = {
            ...widgetInstances,
            [widgetData.id]: updatedWidgetData
        };


        // ✅ Marquer qu'on est en train de sauvegarder pour éviter rechargement complet
        isSavingWidgetRef.current = true;

        // Mettre à jour le state
        setWidgetInstances(updatedWidgetInstances);

        // Sauvegarder dans Cosmos avec le state explicite pour éviter closure stale.
        // Un échec (403 droits insuffisants, réseau…) N'est PLUS avalé : on revient à l'état
        // d'avant, on affiche un message clair, et on GARDE le panneau ouvert (pas de fermeture
        // silencieuse qui laissait croire que c'était sauvé).
        const saveResult = await saveDashboardLayoutToBackend(updatedWidgetInstances);  // ← Passer explicitement le state
        if (!saveResult?.success) {
            isSavingWidgetRef.current = false;
            setWidgetInstances(previousWidgetInstances);
            // Un 401 n'est PAS un problème de droits : c'est le token AAD expiré. Le message
            // générique « réessayez » envoyait l'utilisateur cliquer en boucle sur un Save qui
            // ne pouvait plus passer, alors que la vraie action est de recharger la page.
            showError(
                saveResult?.status === 401
                    ? (t('SESSION_EXPIRED') || 'Your session has expired. Refresh the page, then save again.')
                    : saveResult?.status === 403
                        ? (t('SAVE_WIDGET_PERMISSION_DENIED') || 'You do not have permission to save this configuration. Contact an administrator.')
                        : (t('SAVE_WIDGET_ERROR') || 'Failed to save the widget configuration. Please try again.')
            );
            return; // panneau laissé ouvert : l'utilisateur voit l'échec et peut réessayer
        }

        await reloadSingleWidgetData(updatedWidgetData);  // ← Utiliser updatedWidgetData au lieu de widgetData
        
        // Si c'est un nouveau widget, initialiser sa position
        if (isNewWidget) {
            const tabId = widgetData.tabId;
            const existingPositions = tabLayouts[tabId] || {};

            // Calculer une position intelligente pour le nouveau widget
            const existingWidgets = Object.values(existingPositions);
            const cols = 12; // Nombre de colonnes dans le grid
            const defaultWidth = 4; // Largeur par défaut du widget
            const defaultHeight = 4; // Hauteur par défaut

            // Créer une grille pour voir les espaces occupés
            let maxY = 0;
            const occupiedSpaces = {};

            existingWidgets.forEach(pos => {
                const bottom = pos.y + pos.h;
                if (bottom > maxY) maxY = bottom;

                // Marquer les espaces occupés
                for (let y = pos.y; y < pos.y + pos.h; y++) {
                    if (!occupiedSpaces[y]) occupiedSpaces[y] = [];
                    for (let x = pos.x; x < pos.x + pos.w; x++) {
                        occupiedSpaces[y].push(x);
                    }
                }
            });

            // Chercher le premier espace libre
            let foundPosition = null;
            for (let y = 0; y <= maxY && !foundPosition; y++) {
                for (let x = 0; x <= cols - defaultWidth; x++) {
                    // Vérifier si l'espace est libre
                    let isFree = true;
                    for (let dy = 0; dy < defaultHeight && isFree; dy++) {
                        const row = occupiedSpaces[y + dy] || [];
                        for (let dx = 0; dx < defaultWidth; dx++) {
                            if (row.includes(x + dx)) {
                                isFree = false;
                                break;
                            }
                        }
                    }

                    if (isFree) {
                        foundPosition = { x, y };
                        break;
                    }
                }
            }

            // Si aucun espace trouvé, mettre en bas
            if (!foundPosition) {
                foundPosition = { x: 0, y: maxY };
            }

            // Position finale pour le nouveau widget
            const newPosition = {
                x: foundPosition.x,
                y: foundPosition.y,
                w: defaultWidth,
                h: defaultHeight
            };

            const updatedLayouts = {
                ...tabLayouts,
                [tabId]: {
                    ...existingPositions,
                    [widgetData.id]: newPosition
                }
            };

            setTabLayouts(updatedLayouts);

            const updatedWidgetsPositions = {
                ...tabWidgetsPositions,
                [tabId]: {
                    ...(tabWidgetsPositions[tabId] || {}),
                    [widgetData.id]: newPosition
                }
            };
            setTabWidgetsPositions(updatedWidgetsPositions);
        }

        setShowConfigPanel(false);
        setSelectedWidget(null);
        setEditingInstance(null);
    };
 
    // Handle config panel close
    const handleConfigClose = () => {
        setShowConfigPanel(false);
        setSelectedWidget(null);
        setEditingInstance(null);
    };

    // Handle config panel delete
    const handleConfigDelete = (instanceId) => {
        handleDeleteWidgetInstance(instanceId);
        setShowConfigPanel(false);
        setSelectedWidget(null);
        setEditingInstance(null);
    };

    // Handle drop on dashboard area
    const handleDashboardDrop = (e) => {
        e.preventDefault();
        setDropZoneActive(false);

        if (!draggedWidget) return;

        // Créer une instance du widget avec configuration par défaut
        const newWidgetInstance = {
            id: `widget-instance-${Date.now()}`,
            widgetId: draggedWidget.id,
            name: draggedWidget.name,
            tabId: selectedTab,
            config: {
                showTitle: true,
                showBorder: true
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        // Ajouter l'instance
        const updatedInstances = {
            ...widgetInstances,
            [newWidgetInstance.id]: newWidgetInstance
        };
        setWidgetInstances(updatedInstances);

        // Calculer la position pour le nouveau widget
        const tabId = selectedTab;
        const existingPositions = tabLayouts[tabId] || {};
        const existingWidgets = Object.values(existingPositions);
        const defaultWidth = 6; // Demi-ligne (6 colonnes sur 12)
        const defaultHeight = draggedWidget.defaultSize?.h || 4;

        // Calculer la position : alterner entre colonne 0 et colonne 6
        const widgetCount = existingWidgets.length;
        const isLeftColumn = widgetCount % 2 === 0; // Pair = gauche, Impair = droite
        const rowIndex = Math.floor(widgetCount / 2); // Numéro de ligne

        // Calculer le Y maximum pour cette ligne
        let maxYForRow = rowIndex * defaultHeight;
        if (!isLeftColumn) {
            // Pour la colonne de droite, vérifier si la colonne de gauche existe et ajuster Y
            const leftColumnWidget = existingWidgets[widgetCount - 1];
            if (leftColumnWidget) {
                maxYForRow = leftColumnWidget.y;
            }
        }

        const newPosition = {
            x: isLeftColumn ? 0 : 6, // Colonne 0 ou 6
            y: maxYForRow,
            w: defaultWidth,
            h: defaultHeight
        };

        const updatedLayouts = {
            ...tabLayouts,
            [tabId]: {
                ...existingPositions,
                [newWidgetInstance.id]: newPosition
            }
        };
        setTabLayouts(updatedLayouts);

        const updatedWidgetsPositions = {
            ...tabWidgetsPositions,
            [tabId]: {
                ...(tabWidgetsPositions[tabId] || {}),
                [newWidgetInstance.id]: newPosition
            }
        };
        setTabWidgetsPositions(updatedWidgetsPositions);

        setDraggedWidget(null);
    };

    // Handle drag over dashboard area
    const handleDashboardDragOver = (e) => {
        e.preventDefault();
        setDropZoneActive(true);
    };

    // Handle drag leave dashboard area
    const handleDashboardDragLeave = (e) => {
        e.preventDefault();
        setDropZoneActive(false);
    };

    // Edit existing widget instance
    const handleEditWidget = (instanceId) => {
        const instance = widgetInstances[instanceId];
        if (!instance) return;

        const widgetInfo = WIDGET_CATALOG.find(w => w.id === instance.widgetId);
        setSelectedWidget(widgetInfo);
        setEditingInstance(instance);
        setShowConfigPanel(true);
    };

    /**
     * Initialiser le layout avec les positions par défaut et sauvegarder immédiatement
     */
    const initializeDefaultLayout = useCallback(async () => {
        if (!tenantId || !token || !decodedScope || !analyticsProfiles) {
            console.warn('⚠️ Cannot initialize default layout: missing params');
            return;
        }

        // 🔹 Trouver le profile analytics par le champ 'scope'
        const analyticsProfile = Object.values(analyticsProfiles).find(
            profile => profile.scope === decodedScope
        );

        if (!analyticsProfile) {
            console.warn(`⚠️ Cannot find analytics profile for scope "${decodedScope}"`);
            return;
        }

        try {
            console.log('🎬 Initializing default dashboard layout with DEFAULT_WIDGET_POSITIONS');
            console.log('⚠️ This only happens on first load or when no positions exist in Cosmos');

            // 🔒 Récupérer les tabs existants pour PRÉSERVER les custom tabs
            const existingResponse = await getDashboardLayout(tenantId, analyticsProfile.id, token);
            const existingTabs = (existingResponse?.success && existingResponse?.data?.tabs) || {};

            // Ne garder que les custom tabs existants — les tabs system sont reset
            const preservedCustomTabs = {};
            Object.keys(existingTabs).forEach(tabId => {
                if (existingTabs[tabId].type === 'custom') {
                    preservedCustomTabs[tabId] = existingTabs[tabId];
                }
            });
            console.log('🔒 Preserving custom tabs:', Object.keys(preservedCustomTabs));

            // Construire les widgets prédéfinis du dashboard depuis dashboardConfig
            const defaultDashboardWidgets = {};
            if (dashboardConfig && dashboardConfig.widgets) {
                dashboardConfig.widgets.forEach(widget => {
                    defaultDashboardWidgets[widget.id] = {
                        widgetId: widget.id,
                        type: widget.type,
                        queryId: widget.queryId,
                        title: widget.title,
                        titleKey: widget.titleKey,
                        config: widget.config || {},
                        isPredefined: true,
                        createdAt: new Date().toISOString()
                    };
                });
            }

            // Structure : dashboard/calls + tabs système (sla, abandons, flows, trends) + custom préservés
            const now = new Date().toISOString();
            const systemTabsWithTimestamps = {};
            Object.keys(DEFAULT_SYSTEM_TABS).forEach(tabId => {
                systemTabsWithTimestamps[tabId] = { ...DEFAULT_SYSTEM_TABS[tabId], createdAt: now };
            });

            const defaultTabs = {
                dashboard: {
                    displayName: "Dashboard",
                    type: "system",
                    widgets: defaultDashboardWidgets,
                    widgetsPositions: { ...DEFAULT_WIDGET_POSITIONS },
                    createdAt: now
                },
                calls: {
                    displayName: "Calls List",
                    type: "system",
                    widgets: {},
                    widgetsPositions: {},
                    createdAt: now
                },
                ...systemTabsWithTimestamps,
                ...preservedCustomTabs
            };

            const dashboardLayoutData = {
                tabs: defaultTabs
            };

            console.log('💾 Saving default layout to backend (custom tabs preserved)...', dashboardLayoutData);

            const response = await saveDashboardLayout(
                tenantId,
                analyticsProfile.id,
                dashboardLayoutData,
                token
            );

            if (response.success) {
                // Mettre à jour l'état local avec les positions par défaut
                setTabWidgetsPositions({
                    dashboard: { ...DEFAULT_WIDGET_POSITIONS },
                    calls: {}
                });
                setTabLayouts({
                    dashboard: { ...DEFAULT_WIDGET_POSITIONS },
                    calls: {}
                });
            } else {
                console.error('❌ Failed to save default layout:', response.message);
            }
        } catch (error) {
            console.error('❌ Error initializing default layout:', error);
        }
    }, [tenantId, token]);

    /**
     * Sauvegarder le Dashboard Layout complet dans le backend
     */
    const saveDashboardLayoutToBackend = useCallback(async (explicitWidgetInstances = null) => {
        // ✅ Utiliser le state explicite si fourni, sinon utiliser widgetInstances du closure
        const currentWidgetInstances = explicitWidgetInstances || widgetInstances;
        
        if (!tenantId || !token || !decodedScope || !analyticsProfiles) {
            console.warn('⚠️ Cannot save dashboard layout: missing params');
            return { success: false, message: 'missing params' };
        }

        // 🔹 Trouver le profile analytics par le champ 'scope'
        const analyticsProfile = Object.values(analyticsProfiles).find(
            profile => profile.scope === decodedScope
        ) || Object.values(analyticsProfiles).find(
            profile => profile.scope === "default"
        );

        if (!analyticsProfile) {
            console.warn(`⚠️ Cannot find analytics profile for scope "${decodedScope}"`);
            return { success: false, message: 'analytics profile not found' };
        }

        try {
            // ✅ STEP 1: D'abord, forcer la mise à jour de tabWidgetsPositions avec layouts.lg actuel
            if (layouts && layouts.lg) {
                const currentPositions = {};
                layouts.lg.forEach(item => {
                    currentPositions[item.i] = {
                        x: item.x,
                        y: item.y,
                        w: item.w,
                        h: item.h
                    };
                });
                
                setTabWidgetsPositions(prev => ({
                    ...prev,
                    dashboard: currentPositions
                }));
            }
            
            let dashboardPositions = {};
            if (layouts && layouts.lg) {
                layouts.lg.forEach(item => {
                    dashboardPositions[item.i] = {
                        x: item.x,
                        y: item.y,
                        w: item.w,
                        h: item.h
                    };
                });
            } else if (tabWidgetsPositions['dashboard']) {
                dashboardPositions = tabWidgetsPositions['dashboard'];
            }
            
            // ✅ Construire les widgets dashboard en FUSIONNANT templates + configs personnalisées
            const dashboardWidgets = {};
            if (dashboardConfig && dashboardConfig.widgets) {
                dashboardConfig.widgets.forEach(widget => {
                    const instanceId = `dashboard-${widget.id}`;
                    const customConfig = currentWidgetInstances[instanceId];  // ← Utiliser currentWidgetInstances
                    
                    dashboardWidgets[widget.id] = {
                        widgetId: widget.id,
                        type: widget.type,
                        queryId: widget.queryId,
                        title: widget.title,
                        titleKey: widget.titleKey,
                        config: widget.config || {},
                        isPredefined: true,
                        createdAt: new Date().toISOString()
                    };
                    
                    // ✅ Si le widget a une config personnalisée, la sauvegarder
                    if (customConfig) {
                        dashboardWidgets[instanceId] = {
                            widgetId: widget.id,
                            name: customConfig.name,
                            config: customConfig.config,
                            createdAt: customConfig.createdAt,
                            updatedAt: customConfig.updatedAt
                        };
                    }
                });
            }

            // Construire la nouvelle structure avec tabs imbriqués
            const tabs = {
                dashboard: {
                    displayName: "Dashboard",
                    type: "system",
                    widgets: dashboardWidgets, // ← Contient les templates + configs personnalisées
                    widgetsPositions: dashboardPositions,
                    createdAt: new Date().toISOString()
                },
                calls: {
                    displayName: "Calls List",
                    type: "system",
                    widgets: {},
                    widgetsPositions: tabWidgetsPositions['calls'] || {},
                    createdAt: new Date().toISOString()
                }
            };

            // Ajouter les custom tabs
            customTabs.forEach(tab => {
                tabs[tab.id] = {
                    displayName: tab.name,
                    type: "custom",
                    widgets: {},
                    widgetsPositions: tabWidgetsPositions[tab.id] || {},
                    createdAt: tab.createdAt || new Date().toISOString()
                };
            });

            // Répartir les widgets dans leurs tabs respectifs
            Object.keys(currentWidgetInstances).forEach(instanceId => {  // ← Utiliser currentWidgetInstances
                const instance = currentWidgetInstances[instanceId];  // ← Utiliser currentWidgetInstances
                const tabId = instance.tabId;
                if (tabs[tabId]) {
                    tabs[tabId].widgets[instanceId] = {
                        widgetId: instance.widgetId,
                        name: instance.name,
                        config: instance.config,
                        createdAt: instance.createdAt,
                        updatedAt: instance.updatedAt
                    };
                    
                   
                }
            });

            const dashboardLayoutData = {
                tabs: tabs
            };

         

            const response = await saveDashboardLayout(
                tenantId,
                analyticsProfile.id,
                dashboardLayoutData,
                token
            );

            if (response.success) {
                const totalPositions = Object.keys(dashboardPositions).length;
              
                // ✅ IMPORTANT: Recharger le layout depuis Cosmos pour synchroniser l'état local
                try {
                    const reloadResponse = await getDashboardLayout(
                        tenantId,
                        analyticsProfile.id,
                        token
                    );

                    if (reloadResponse.success && reloadResponse.data && reloadResponse.data.tabs) {
                      
                        const tabs = reloadResponse.data.tabs;
                        
                        // Recharger les positions des widgets par tab
                        const positions = {};
                        Object.keys(tabs).forEach(tabId => {
                            if (tabs[tabId].widgetsPositions) {
                                positions[tabId] = tabs[tabId].widgetsPositions;
                            }
                        });

                        // Marquer qu'on charge depuis Cosmos pour permettre la mise à jour du layout
                        isLoadingFromCosmosRef.current = true;

                        setTabWidgetsPositions(positions);
                        setTabLayouts(positions);
                    }
                } catch (reloadError) {
                    console.error('⚠️ Error reloading layout after save:', reloadError);
                }
                return { success: true };
            } else {
                console.error('❌ Failed to save dashboard layout:', response.message);
                return { success: false, status: response?.status, message: response?.message };
            }
        } catch (error) {
            console.error('❌ Error saving dashboard layout:', error);
            return { success: false, status: error?.response?.status, message: error?.message };
        }
    }, [tenantId, token, decodedScope, analyticsProfiles, customTabs, widgetInstances, tabWidgetsPositions, layouts]);

    /**
     * Manual save only - triggered by Save Layout button
     * Auto-save removed per user request - all saves now happen when clicking "Save Layout"
     */

    /**
     * Gérer les tabs personnalisés : créer, éditer, supprimer
     */
    const handleCreateTab = async (tabName) => {

        // ✅ STEP 1: Vérifier les paramètres requis et loguer pour debug
        if (!tenantId || !token || !decodedScope || !analyticsProfiles) {
            console.error('[TAB_CREATE_ERROR] Paramètres manquants:', {
                tenantId,
                token,
                decodedScope,
                analyticsProfiles
            });
            showError(t('TAB_CREATE_ERROR') || 'Cannot create tab: missing required parameters');
            return;
        }

        // 🔹 Trouver le profile analytics par le champ 'scope'
     
        const analyticsProfile =
    Object.values(analyticsProfiles).find(
        profile => profile.scope === decodedScope
    ) ||
    Object.values(analyticsProfiles).find(
        profile => profile.scope === "default"
    );

        if (!analyticsProfile) {
            console.error('[TAB_CREATE_ERROR] Analytics profile introuvable pour scope:', decodedScope, analyticsProfiles);
            showError(t('TAB_CREATE_ERROR') || `Cannot find analytics profile for scope "${decodedScope}"`);
            return;
        }

        // ✅ STEP 2: Créer la structure du nouveau tab
        const newTab = {
            id: `tab-${Date.now()}`,
            name: tabName,
            type: 'custom',
            createdAt: new Date().toISOString()
        };

        try {
            console.log('💾 Saving new tab to Cosmos first...');
            console.log('[TAB_CREATE_DEBUG] Params:', {
                tenantId,
                analyticsProfileId: analyticsProfile.id,
                dashboardLayoutData :  analyticsProfile.dashboardLayout, // Envoyer le layout actuel du profile pour éviter d'écraser les changements non sauvegardés
                token
            });
            
            // ✅ STEP 3: Construire la structure tabs avec le nouveau tab
            const tabs = {
                dashboard: {
                    displayName: "Dashboard",
                    type: "system",
                    widgets: {},
                    widgetsPositions: tabWidgetsPositions['dashboard'] || {},
                    createdAt: new Date().toISOString()
                },
                calls: {
                    displayName: "Calls List",
                    type: "system",
                    widgets: {},
                    widgetsPositions: tabWidgetsPositions['calls'] || {},
                    createdAt: new Date().toISOString()
                }
            };

            // Ajouter tous les custom tabs existants + le nouveau
            const allTabs = [...customTabs, newTab];
            allTabs.forEach(tab => {
                tabs[tab.id] = {
                    displayName: tab.name,
                    type: "custom",
                    widgets: {},
                    widgetsPositions: tabLayouts[tab.id] || {},
                    createdAt: tab.createdAt || new Date().toISOString()
                };
            });

            // Répartir les widgets dans leurs tabs
            Object.keys(widgetInstances).forEach(instanceId => {
                const instance = widgetInstances[instanceId];
                const tabId = instance.tabId;
                if (tabs[tabId]) {
                    tabs[tabId].widgets[instanceId] = {
                        widgetId: instance.widgetId,
                        name: instance.name,
                        config: instance.config,
                        createdAt: instance.createdAt,
                        updatedAt: instance.updatedAt
                    };
                }
            });

            const dashboardLayoutData = { tabs };

            // ✅ STEP 4: Sauvegarder dans Cosmos
            const response = await saveDashboardLayout(
                tenantId,
                analyticsProfile.id,
                dashboardLayoutData,
                token
            );

            if (response.success) {
                // ✅ SUCCESS: Ajouter dans customTabs local APRÈS la sauvegarde réussie
                console.log('✅ Tab saved successfully to Cosmos');
                setCustomTabs(allTabs);
                setSelectedTab(newTab.id);
                
                // ✅ Mettre à jour Redux avec le nouveau dashboardLayout
                const updatedProfile = {
                    ...analyticsProfile,
                    dashboardLayout: dashboardLayoutData
                };
                const updatedProfiles = {
                    ...analyticsProfiles,
                    [analyticsProfile.id]: updatedProfile
                };
                dispatch(setAnalyticsProfiles(updatedProfiles));
                console.log('✅ Redux updated with new tab');
                
                showSuccess(t('TAB_CREATE_SUCCESS') || `Tab "${tabName}" created successfully!`);
            } else {
                // ❌ FAILED: Alert d'échec
                console.error('❌ Failed to save new tab to backend:', response);
                showError(t('TAB_CREATE_ERROR') || `Failed to create tab "${tabName}". Please try again.`);
            }
        } catch (error) {
            // ❌ ERROR: Alert d'erreur
            console.error('❌ Error saving new tab to backend:', error, {
                tenantId,
                analyticsProfile,
                dashboardLayoutData : analyticsProfile?.dashboardLayout,
                token
            });
            showError(t('TAB_CREATE_ERROR') || `Error creating tab "${tabName}". Please try again.`);
        }
    };

    const handleEditTab = async (tabId, newName) => {
        const updatedTabs = customTabs.map(tab =>
            tab.id === tabId ? { ...tab, name: newName } : tab
        );
        setCustomTabs(updatedTabs);

        // Le useEffect auto-save se chargera de sauvegarder dans le backend
    };

    const handleDeleteTab = async (tabId) => {
        // Garde : les onglets système (dashboard, calls, et les tabs de DEFAULT_SYSTEM_TABS
        // comme sla/abandons/flows/trends) ne sont PAS supprimables — seuls les custom tabs.
        if (tabId === 'dashboard' || tabId === 'calls' || DEFAULT_SYSTEM_TABS[tabId]) {
            showError(t('CANNOT_DELETE_SYSTEM_TAB') || 'Cannot delete system tabs');
            return;
        }

        // Supprimer toutes les instances de widgets associées à ce tab
        const updatedInstances = { ...widgetInstances };
        Object.keys(updatedInstances).forEach(instanceId => {
            if (updatedInstances[instanceId].tabId === tabId) {
                delete updatedInstances[instanceId];
            }
        });
        setWidgetInstances(updatedInstances);

        // Supprimer le tab
        const updatedTabs = customTabs.filter(t => t.id !== tabId);
        setCustomTabs(updatedTabs);

        // Supprimer le layout du tab
        const updatedLayouts = { ...tabLayouts };
        delete updatedLayouts[tabId];
        setTabLayouts(updatedLayouts);

        // Supprimer les positions du tab
        const updatedWidgetsPositions = { ...tabWidgetsPositions };
        delete updatedWidgetsPositions[tabId];
        setTabWidgetsPositions(updatedWidgetsPositions);

        // Si le tab actif était supprimé, basculer sur Dashboard
        if (selectedTab === tabId) {
            setSelectedTab('dashboard');
        }

        // Sauvegarder dans le backend (Cosmos DB)
        if (tenantId && token && decodedScope && analyticsProfiles) {
            // 🔹 Trouver le profile analytics par le champ 'scope'
            const analyticsProfile = Object.values(analyticsProfiles).find(
                profile => profile.scope === decodedScope
            );

            if (!analyticsProfile) {
                console.warn(`⚠️ Cannot find analytics profile for scope "${decodedScope}"`);
                return;
            }

            try {
            
                // Construire la structure tabs sans le tab supprimé
                const tabs = {
                    dashboard: {
                        displayName: "Dashboard",
                        type: "system",
                        widgets: {},
                        widgetsPositions: updatedWidgetsPositions['dashboard'] || {},
                        createdAt: new Date().toISOString()
                    },
                    calls: {
                        displayName: "Calls List",
                        type: "system",
                        widgets: {},
                        widgetsPositions: updatedWidgetsPositions['calls'] || {},
                        createdAt: new Date().toISOString()
                    }
                };

                // Ajouter uniquement les custom tabs restants (sans le tab supprimé)
                updatedTabs.forEach(tab => {
                    tabs[tab.id] = {
                        displayName: tab.name,
                        type: "custom",
                        widgets: {},
                        widgetsPositions: updatedLayouts[tab.id] || {},
                        createdAt: tab.createdAt || new Date().toISOString()
                    };
                });

                // Répartir les widgets restants dans leurs tabs
                Object.keys(updatedInstances).forEach(instanceId => {
                    const instance = updatedInstances[instanceId];
                    const tabIdOfInstance = instance.tabId;
                    if (tabs[tabIdOfInstance]) {
                        tabs[tabIdOfInstance].widgets[instanceId] = {
                            widgetId: instance.widgetId,
                            name: instance.name,
                            config: instance.config,
                            createdAt: instance.createdAt,
                            updatedAt: instance.updatedAt
                        };
                    }
                });

                const dashboardLayoutData = { tabs };

                const response = await saveDashboardLayout(
                    tenantId,
                    analyticsProfile.id,
                    dashboardLayoutData,
                    token
                );

                if (response.success) {
                    // ✅ Mettre à jour Redux avec le layout mis à jour (sans le tab supprimé)
                    const updatedProfile = {
                        ...analyticsProfile,
                        dashboardLayout: dashboardLayoutData
                    };
                    const updatedProfiles = {
                        ...analyticsProfiles,
                        [analyticsProfile.id]: updatedProfile
                    };
                    dispatch(setAnalyticsProfiles(updatedProfiles));
                    console.log('✅ Redux updated after tab deletion');
                    
                    showSuccess(t('TAB_DELETE_SUCCESS') || 'Tab deleted successfully!');
                } else {
                    console.error('❌ Failed to save tab deletion to backend:', response.message);
                    showError(t('TAB_DELETE_ERROR') || 'Failed to delete tab. Please try again.');
                }
            } catch (error) {
                console.error('❌ Error saving tab deletion to backend:', error);
                showError(t('TAB_DELETE_ERROR') || 'Failed to delete tab. Please try again.');
            }
        }
    };

    /**
     * Obtenir les instances de widgets pour un tab personnalisé
     */
    const getWidgetInstancesForTab = (tabId) => {
        const instances = Object.values(widgetInstances).filter(instance => instance.tabId === tabId);

        // Convertir les instances en format widget pour le grid avec catalogWidget et originalConfig
        return instances.map(instance => {
            const catalogWidget = WIDGET_CATALOG.find(w => w.id === instance.widgetId);

            // ✅ Si c'est un widget du Dashboard par défaut, récupérer sa config originale
            let originalConfig = null;
            if (dashboardConfig && dashboardConfig.widgets) {
                originalConfig = dashboardConfig.widgets.find(w => w.id === instance.widgetId);
            }

            // ✅ Widget ajouté depuis le store, ABSENT du dashboard par défaut : on synthétise
            // une config depuis le catalogue (queryId) pour qu'il soit rendu par <Widget> avec
            // la VRAIE donnée — jamais de données statiques pour un widget qui a un queryId.
            if (!originalConfig && catalogWidget?.dataSource?.queryId) {
                originalConfig = {
                    id: catalogWidget.id,
                    type: catalogWidget.type,
                    queryId: catalogWidget.dataSource.queryId,
                    // `catalogWidget.name` est un libellé FIGÉ EN FRANÇAIS dans le catalogue
                    // (aucune des 53 entrées n'a de titleKey). Le recopier tel quel affichait
                    // « Statistiques de statut des agents » y compris dans l'interface anglaise.
                    // Les traductions existent pourtant déjà, indexées par l'id du widget —
                    // c'est ce que fait le catalogue lui-même (WIDGET_NAMES.${widget.id}).
                    // On passe donc la CLÉ, et le nom français ne sert plus que de repli.
                    titleKey: catalogWidget.titleKey || `WIDGET_NAMES.${catalogWidget.id}`,
                    title: catalogWidget.name,
                    config: catalogWidget.config || {}
                };
            }

            return {
                ...instance,
                catalogWidget,
                originalConfig  // Ajouter la config originale du dashboard si elle existe
            };
        });
    };

    /**
     * Ouvrir le dialog pour déplacer un widget
     */
    const handleMoveWidgetInstance = (instanceId) => {
        handleEditWidgetInstance(instanceId);
    };

    /**
     * Exporter un widget individuel vers Excel
     */
    const handleExportWidgetToExcel = async (widgetInstance) => {
        try {
            // Déterminer les filtres du widget
            let widgetFilters;
            if (widgetInstance.config?.useGlobalFilters !== false) {
                widgetFilters = appliedFilters;
            } else {
                const customPeriod = widgetInstance.config?.customPeriod;
                const customFilters = widgetInstance.config?.customFilters || {};
                
                widgetFilters = {
                    scope: decodedScope,
                    timezone: selectedTimezone
                };
                
                if (customPeriod) {
                    const dateRange = calculateDateRangeFromPeriod(customPeriod, widgetInstance.config);
                    if (dateRange.startDate) widgetFilters.startDate = dateRange.startDate;
                    if (dateRange.endDate) widgetFilters.endDate = dateRange.endDate;
                }
                
                if (customFilters.flowKey) widgetFilters.flowKey = customFilters.flowKey;
                if (customFilters.queueKey) widgetFilters.queueKey = customFilters.queueKey;
                if (customFilters.agentKey) widgetFilters.agentKey = customFilters.agentKey;
            }

            // Récupérer les données du widget (clée par id d'instance)
            const widgetData = widgetsData[widgetInstance.id];
            const widgetName = resolveWidgetTitle(t, i18n, {
                widgetId: widgetInstance.widgetId,
                name: widgetInstance.name,
                catalogName: widgetInstance.catalogWidget?.name,
                fallback: 'widget'
            }) || 'widget';
            const widgetType = widgetInstance.originalConfig?.type || widgetInstance.catalogWidget?.type;

            if (!widgetData) {
                alert(t('NO_DATA_TO_EXPORT') || 'No data to export');
                return;
            }

            // Mise en lignes : SOURCE UNIQUE `Utils/excelExport` (buildWidgetRows).
            //
            // Il y avait ici un TROISIÈME exporteur, divergent : seul `type: 'table'` était
            // traité colonne par colonne ; tous les autres tableaux (agent-status-table,
            // ivr-stats-table, abandon-table, sla-table, agent-status-breakdown) tombaient dans
            // la branche « graphique » qui ne garde que Label/Value/Percentage — clés qu'aucune
            // de ces lignes ne porte. Le fichier sortait donc avec autant de lignes que d'agents
            // et TOUTES vides. Les métriques de durée, elles, sortaient en secondes brutes.
            // Ne pas réintroduire de mise en forme locale ici : tout passe par buildWidgetRows.
            const widgetExportConfig = {
                ...(widgetInstance.originalConfig || widgetInstance.catalogWidget || {}),
                type: widgetType,
                title: widgetName
            };
            const wsData = buildWidgetRows(widgetData, widgetExportConfig, t, selectedTimezone);

            if (wsData.length === 0) {
                alert(t('NO_DATA_TO_EXPORT') || 'No data to export');
                return;
            }

            const startDate = new Date(widgetFilters.startDate).toLocaleDateString('en-US').replace(/\//g, '-');
            const endDate = new Date(widgetFilters.endDate).toLocaleDateString('en-US').replace(/\//g, '-');
            const filename = `${widgetName}_${startDate}_to_${endDate}.xlsx`;

            exportSheetsToExcel([{ name: widgetName, rows: wsData }], filename);

        } catch (error) {
            console.error('❌ Error exporting widget to Excel:', error);
            alert(t('EXPORT_ERROR') || 'Error exporting data');
        }
    };

    /**
     * Rendre le menu contextuel d'un widget (3 points)
     */
    const renderWidgetMenu = (widgetInstance) => {
        return (
            <Menu>
                <MenuTrigger disableButtonEnhancement>
                    <Button
                        appearance="subtle"
                        icon={<FiMoreVertical size={20} />}
                        size="small"
                        style={{ minWidth: 'auto', padding: '4px 8px' }}
                    />
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        <MenuItem
                            icon={<Edit24Regular />}
                            onClick={() => handleEditWidgetInstance(widgetInstance.id)}
                        >
                            {t('EDIT_WIDGET') || 'Edit Widget'}
                        </MenuItem>
                        {/* <MenuItem
                            icon={<ArrowMove24Regular />}
                            onClick={() => handleMoveWidgetInstance(widgetInstance.id)}
                        >
                            MOVE_TO_TAB
                        </MenuItem> */}
                        <MenuItem
                            icon={<ArrowDownload24Regular />}
                            onClick={() => handleExportWidgetToExcel(widgetInstance)}
                        >
                            {t('EXPORT_EXCEL') || 'Export Excel'}
                        </MenuItem>
                        <MenuItem
                            icon={<ArrowDownload24Regular />}
                            onClick={() => {
                                // TODO: Implémenter l'export PDF
                            }}
                        >
                            {t('EXPORT_PDF') || 'Export PDF'}
                        </MenuItem>
                        <MenuItem
                            icon={<Delete24Regular />}
                            onClick={() => handleDeleteWidgetInstance(widgetInstance.id)}
                        >
                            {t('DELETE') || 'Delete'}
                        </MenuItem>
                    </MenuList>
                </MenuPopover>
            </Menu>
        );
    };

    /**
     * Pastille « filtres propres au widget », affichée à côté du titre.
     *
     * Un widget dont `useGlobalFilters` est false ne suit NI la période NI les filtres du
     * rapport : il porte les siens, enregistrés dans le profil. Rien ne le montrait. Deux copies
     * du même widget dans un onglet — c'est permis et ça existe en prod — sont alors
     * indistinguables : même titre, même allure, l'une filtrée sur deux flows, l'autre non.
     * Un client qui « nettoie » la mauvaise copie croit avoir corrigé son rapport.
     *
     * La pastille reste courte (compteurs) ; le détail nommé est dans l'infobulle, avec ⚠️ sur
     * ce qui n'appartient pas à l'entité du rapport — le cas qui ne peut produire aucune donnée.
     */
    const renderWidgetFilterBadge = (instanceConfig) => {
        if (!instanceConfig || instanceConfig.useGlobalFilters !== false) return null;

        const custom = instanceConfig.customFilters || {};
        const rawList = (v) => (Array.isArray(v) ? v : (v === undefined || v === null || v === '' ? [] : [v]));
        // DÉDOUBLONNÉ : une sélection est un ensemble. Un profil peut contenir la même clé
        // plusieurs fois (rien ne l'empêchait) ; la pastille compterait alors 6 filtres là où
        // le panneau n'en affiche que 2 — les tags sont rendus par clé, React n'en garde qu'un.
        const asList = (v) => [...new Set(rawList(v).map(String))];
        const flowKeys = asList(custom.flowKey);
        const queueKeys = asList(custom.queueKey);
        const agentKeys = asList(custom.agentKey);

        const duplicates = rawList(custom.flowKey).length + rawList(custom.queueKey).length
            + rawList(custom.agentKey).length - (flowKeys.length + queueKeys.length + agentKeys.length);

        const parts = [];
        const details = [];

        const describe = (keys, label, resolve, isInScope) => {
            if (keys.length === 0) return;
            parts.push(`${keys.length} ${label}`);
            details.push(`${label} : ${keys.map(k => {
                const name = resolve(String(k)) || String(k);
                return isInScope(String(k)) ? name : `⚠️ ${name} (${t('OUTSIDE_ENTITY') || 'hors entité'})`;
            }).join(', ')}`);
        };

        describe(flowKeys, t('FLOW') || 'Flow',
            (k) => allFlowNameById[k],
            (k) => provisionedFlows.some(f => f.id === k));
        describe(queueKeys, t('QUEUE') || 'Queue',
            (k) => k,   // le filtre file porte déjà le NOM de la file
            (k) => provisionedQueueNames.some(n => String(n) === k));
        describe(agentKeys, t('AGENT') || 'Agent',
            (k) => agentNameById[k.toLowerCase()],
            () => true);   // les agents ne sont pas des colonnes d'entité : pas de verdict

        // Période figée : elle aussi ignore le sélecteur de dates du rapport.
        if (instanceConfig.customPeriod) {
            const period = instanceConfig.customPeriod === 'custom'
                ? [instanceConfig.customStartDate, instanceConfig.customEndDate].filter(Boolean).join(' → ')
                : instanceConfig.customPeriod;
            parts.push(t('WIDGET_OWN_PERIOD'));
            details.push(`${t('CUSTOM_PERIOD')} : ${period}`);
        }

        if (parts.length === 0) return null;

        // Le profil garde des doublons tant que le widget n'a pas été resauvegardé : on le dit
        // plutôt que de laisser un écart inexpliqué entre la pastille et le panneau.
        if (duplicates > 0) {
            details.push(`⚠️ ${duplicates} ${t('DUPLICATE_FILTER_ENTRIES') || 'doublon(s) dans la config enregistrée — resauvegarder le widget les supprime'}`);
        }

        return (
            <span
                className="widget-filter-badge"
                title={`${t('WIDGET_OWN_FILTERS') || 'Filtres propres à ce widget'}\n${details.join('\n')}`}
            >
                {parts.join(' · ')}
            </span>
        );
    };

    /**
     * Rendre le contenu d'un widget instance avec les vraies données du dashboard
     */
    const renderWidgetContent = (widgetInstance) => {
        // widgetInstance contient : { id, widgetId, name, tabId, config, catalogWidget, originalConfig }
        const catalogWidget = widgetInstance.catalogWidget;
        // Injecter le nom personnalisé de l'INSTANCE dans la config rendue par <Widget>. Sans ça,
        // <Widget> affiche le titleKey/title de la config de BASE → le renommage restait invisible.
        // On efface titleKey car <Widget> fait `titleKey || title` : tant que titleKey existe, il
        // masque `title` (donc le nom custom).
        const baseConfig = widgetInstance.originalConfig; // Config originale du dashboard

        // À la CRÉATION, le panneau de config initialise `name` avec le libellé PAR DÉFAUT du
        // catalogue (aucune entrée n'a de titleKey). Ce nom est ensuite persisté tel quel : un
        // widget jamais renommé se retrouve donc avec un `name` qui masquait la traduction
        // (titleKey) et laissait « IA – Sentiment des appels » même en anglais.
        // On ne considère donc comme VRAI renommage qu'un `name` différent de TOUS les libellés
        // par défaut connus du widget ; sinon on repasse par WIDGET_NAMES.${widgetId}.
        // Voir Utils/widgetTitle.js — comparer au seul libellé courant du catalogue casse dès
        // qu'une entrée du catalogue est renommée (cas « Répartition des statuts par agent »).
        const catalogDefaultName = catalogWidget?.name;
        const hasCustomName = isCustomWidgetName(i18n, {
            widgetId: widgetInstance.widgetId,
            name: widgetInstance.name,
            catalogName: catalogDefaultName
        });
        const translatedTitle = t(`WIDGET_NAMES.${widgetInstance.widgetId}`, { defaultValue: catalogDefaultName || '' });

        // On efface titleKey car <Widget> fait `titleKey || title` : tant que titleKey existe, il
        // masque `title`. On injecte donc directement le titre déjà résolu (renommage OU traduction).
        const widgetConfig = baseConfig
            ? { ...baseConfig, title: hasCustomName ? widgetInstance.name : translatedTitle, titleKey: undefined }
            : baseConfig;

        const widgetTitle = hasCustomName ? widgetInstance.name : translatedTitle;
        
        // 🔹 Déterminer les filtres à utiliser pour ce widget
        let widgetFilters;
        
        if (widgetInstance.config?.useGlobalFilters !== false) {
            widgetFilters = appliedFilters;
        } else {
            const customPeriod = widgetInstance.config?.customPeriod;
            const customFilters = widgetInstance.config?.customFilters || {};
            
            widgetFilters = {
                scope: decodedScope,
                timezone: selectedTimezone
            };
            
            if (customPeriod) {
                const dateRange = calculateDateRangeFromPeriod(customPeriod, widgetInstance.config);
                if (dateRange.startDate) widgetFilters.startDate = dateRange.startDate;
                if (dateRange.endDate) widgetFilters.endDate = dateRange.endDate;
            }
            
            if (customFilters.flowKey) widgetFilters.flowKey = customFilters.flowKey;
            if (customFilters.queueKey) widgetFilters.queueKey = customFilters.queueKey;
            if (customFilters.agentKey) widgetFilters.agentKey = customFilters.agentKey;
            
          
        }
        
        const widgetData = widgetsData[widgetInstance.id];


        // ✅ Si c'est un widget du Dashboard avec config originale, utiliser le composant Widget.js avec vraies données
        if (widgetConfig) {
            return (
                <div
                    className="widget-instance-content"
                >
                    {widgetInstance.config?.showTitle !== false && (
                        <div className="widget-instance-header">
                            <h3>{widgetTitle}</h3>
                            <div className="widget-instance-actions">
                                {renderWidgetMenu(widgetInstance)}
                            </div>
                        </div>
                    )}
                    {/* Utiliser le composant Widget existant avec les filtres appropriés */}
                    <Widget
                        config={widgetConfig}
                        filterValues={widgetFilters}
                        filterBadge={renderWidgetFilterBadge(widgetInstance.config)}
                        isEditMode={isEditMode}
                        tenantId={tenantId}
                        token={token}
                        preloadedData={widgetData}
                        isLoading={isLoadingWidgets}
                        onEdit={() => handleEditWidgetInstance(widgetInstance.id)}
                        onMove={() => handleMoveWidgetInstance(widgetInstance.id)}
                        onDelete={() => handleDeleteWidgetInstance(widgetInstance.id)}
                    />
                </div>
            );
        }

        // Pour les nouveaux widgets ajoutés depuis le catalogue, utiliser les widgets statiques
        const staticData = STATIC_WIDGET_DATA[widgetInstance.widgetId];
        
        if (staticData && catalogWidget) {
            switch (catalogWidget.type) {
                case 'metric':
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentMetricWidget
                                    title={staticData.title}
                                    value={staticData.value}
                                    subtitle={staticData.subtitle}
                                    trend={staticData.trend}
                                    color={staticData.color || catalogWidget.color}
                                />
                            </div>
                        </div>
                    );

                case 'bar':
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentBarChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    color={staticData.color || catalogWidget.color}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                case 'donut':
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentDonutChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                case 'line':
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentLineChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    color={staticData.color || catalogWidget.color}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                case 'gauge':
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentHorizontalBarChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    color={staticData.color || catalogWidget.color}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                // Types additionnels - utiliser les mêmes composants selon le type de données
                case 'sparkline':
                case 'area':
                    // Utiliser le LineChart pour sparkline et area
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentLineChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    color={staticData.color || catalogWidget.color}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                case 'pie':
                    // Utiliser DonutChart pour pie
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentDonutChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                case 'grouped-bar':
                case 'heatmap':
                case 'funnel':
                case 'scatter':
                    // Utiliser BarChart pour ces types
                    return (
                        <div className="widget-instance-content">
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentBarChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    color={staticData.color || catalogWidget.color}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                case 'progress':
                    // Utiliser HorizontalBarChart pour progress
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentHorizontalBarChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    color={staticData.color || catalogWidget.color}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                default:
                    break;
            }
        }

        // Fallback : widget non supporté ou sans données
        return (
            <div
                className="widget-instance-content"
            >
                {widgetInstance.config?.showTitle !== false && (
                    <div className="widget-instance-header">
                        <h3>{widgetTitle}</h3>
                        <div className="widget-instance-actions">
                            {renderWidgetMenu(widgetInstance)}
                        </div>
                    </div>
                )}
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📊</div>
                    <div>Widget "{widgetTitle}"</div>
                    <div style={{ fontSize: '12px', marginTop: '5px' }}>
                        Type: {catalogWidget?.type || 'unknown'}
                    </div>
                </div>
            </div>
        );
    };

    /**
     * Restaurer un widget à sa position et taille par défaut
     */
    const handleRestoreWidget = (widgetId) => {
        const defaultPosition = DEFAULT_WIDGET_POSITIONS[widgetId];

        if (!defaultPosition) {
            console.warn(`No default position found for widget: ${widgetId}`);
            return;
        }

        // Mettre à jour le layout pour lg breakpoint
        setLayouts(prevLayouts => {
            const newLayouts = { ...prevLayouts };

            // Mettre à jour pour tous les breakpoints
            Object.keys(newLayouts).forEach(breakpoint => {
                const layoutForBreakpoint = newLayouts[breakpoint];
                const widgetLayoutIndex = layoutForBreakpoint.findIndex(item => item.i === widgetId);

                if (widgetLayoutIndex !== -1) {
                    // Restaurer la position par défaut
                    layoutForBreakpoint[widgetLayoutIndex] = {
                        ...layoutForBreakpoint[widgetLayoutIndex],
                        x: defaultPosition.x,
                        y: defaultPosition.y,
                        w: defaultPosition.w,
                        h: defaultPosition.h
                    };
                }
            });

            return newLayouts;
        });
    };

    /**
     * Éditer les filtres d'un widget du Dashboard
     * Crée ou charge une instance virtuelle pour stocker les filtres personnalisés
     */
    const handleEditDashboardWidget = (widgetId) => {
        // Trouver le widget dans la config du dashboard
        const widgetConfig = dashboardConfig?.widgets?.find(w => w.id === widgetId);
        if (!widgetConfig) {
            return;
        }

        // Trouver le widget dans le catalogue (ou créer une entrée temporaire)
        let catalogWidget = WIDGET_CATALOG.find(w => w.id === widgetId);
        if (!catalogWidget) {
            // Créer une entrée catalogue temporaire basée sur la config du dashboard
            catalogWidget = {
                id: widgetId,
                name: widgetConfig.title,
                description: widgetConfig.description,
                category: 'dashboard',
                type: widgetConfig.type,
                defaultSize: { w: widgetConfig.layout.w, h: widgetConfig.layout.h }
            };
        }

        // Chercher si une instance existe déjà pour ce widget du dashboard
        // Les widgets du dashboard ont un instanceId spécial: "dashboard-{widgetId}"
        const instanceId = `dashboard-${widgetId}`;
        let existingInstance = widgetInstances[instanceId];
        
        if (!existingInstance) {
            // Créer une instance virtuelle pour stocker la config de filtres
            existingInstance = {
                id: instanceId,
                widgetId: widgetId,
                name: widgetConfig.title,
                tabId: 'dashboard',
                enabled: true,
                config: {
                    useGlobalFilters: true,  // Par défaut, utilise les filtres globaux
                    customPeriod: 'month',
                    customFilters: {}
                }
            };
        }

        // Ouvrir le panel à droite pour éditer le widget
        setSelectedWidget(catalogWidget);
        setEditingInstance(existingInstance);  // Passer l'instance (nouvelle ou existante)
        setShowConfigPanel(true);
    };

    /**
     * Déplacer/copier un widget du Dashboard vers un autre tab
     */
    const handleMoveDashboardWidget = (widgetId) => {
        // Identique à Edit mais avec focus sur le tab de destination
        handleEditDashboardWidget(widgetId);
    };

    /**
     * Masquer un widget du Dashboard
     */
    const handleDeleteDashboardWidget = (widgetId) => {
        if (window.confirm('Voulez-vous masquer ce widget du Dashboard ?')) {
            // TODO: Implémenter la logique de masquage
            // Pour l'instant, on pourrait stocker une liste de widgets masqués
            // Vous pourriez ajouter un state pour les widgets masqués:
            // setHiddenWidgets(prev => [...prev, widgetId]);
        }
    };

    /**
     * Gérer le changement de layout du dashboard
     * Sauvegarde automatiquement dans Cosmos quand on déplace un widget
     */
    const handleLayoutChange = (layout, layouts) => {
        setLayouts(layouts);

        // Marquer la fin du drag/resize
        setIsDragging(false);
        setIsResizing(false);

        // Si on est en mode édition, mettre à jour tabWidgetsPositions pour le dashboard
        if (isEditMode && layouts && layouts.lg) {
            const updatedPositions = {};
            layouts.lg.forEach(item => {
                updatedPositions[item.i] = {
                    x: item.x,
                    y: item.y,
                    w: item.w,
                    h: item.h
                };
            });
            
            // Mettre à jour l'état local
            setTabWidgetsPositions(prev => ({
                ...prev,
                dashboard: updatedPositions
            }));
        }
    };

    /**
     * Gérer le changement de layout pour les widgets dans les tabs customisés
     * Met à jour l'état local - sera sauvegardé dans Cosmos quand on clique "Save Layout"
     */
    const handleTabWidgetLayoutChange = (tabId, layout) => {
        // Convertir le layout en format {instanceId: {x, y, w, h}}
        const positions = {};
        layout.forEach(item => {
            positions[item.i] = {
                x: item.x,
                y: item.y,
                w: item.w,
                h: item.h
            };
        });

        // Mettre à jour les deux états (tabLayouts et tabWidgetsPositions pour backend)
        const updatedLayouts = {
            ...tabLayouts,
            [tabId]: positions
        };
        setTabLayouts(updatedLayouts);

        const updatedWidgetsPositions = {
            ...tabWidgetsPositions,
            [tabId]: positions
        };
        setTabWidgetsPositions(updatedWidgetsPositions);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'row', height: '100%', position: 'relative', overflow: 'hidden' }}>
            {/* Left Side: Widget Catalog Panel */}
            {showCatalogPanel && (
                <AnalyticsWidgetCatalogPanel
                    isOpen={showCatalogPanel}
                    onToggle={setShowCatalogPanel}
                    onWidgetDragStart={handleWidgetDragStart}
                    onWidgetClick={handleWidgetClick}
                    availableWidgets={availableCatalogWidgetIds}
                    initiallyCollapsed={catalogInitiallyCollapsed}
                />
            )}

            {/* Main Content Area */}
            <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                padding: '20px',
                marginLeft: '0',
                transition: 'margin-left 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Drop zone overlay */}
                {dropZoneActive && draggedWidget && (
                    <div className="analytics-drop-zone active">
                        <div className="analytics-drop-zone-text">
                            <FiGrid size={24} />
                            Drop widget here to configure
                        </div>
                    </div>
                )}

                {/* En-tête du dashboard - FIXED */}
                <div className="analytics-header-fixed">
                    {/* Gauche: Bouton retour */}
                    <div className="analytics-header-left">
                        <IconButton
                            title={t('BACK_TO_SCOPES')}
                            ariaLabel={t('BACK_TO_SCOPES')}
                            onClick={() => navigate('/analytics')}
                            styles={{
                                root: {
                                    width: 40,
                                    height: 40,
                                    backgroundColor: '#5b5fc7',
                                    border: 'none'
                                },
                                rootHovered: {
                                    backgroundColor: '#4a4fb0'
                                }
                            }}
                        >
                            <FiArrowLeft style={{ fontSize: 16, color: 'white' }} />
                        </IconButton>
                    </div>

                    {/* Centre: Date range + Nom du scope */}
                    <div className="analytics-header-center">
                        <div style={{ margin: 0, color: '#323130', fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {decodedScope}
                            {appliedFilters && appliedFilters.startDate && appliedFilters.endDate && (
                                <span
                                    onClick={() => setShowFilters(!showFilters)}
                                    style={{ 
                                        fontSize: '12px', 
                                        fontWeight: 400, 
                                        color: 'rgb(96, 94, 92)', 
                                        cursor: 'pointer',
                                        transition: 'color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = '#5b5fc7'}
                                    onMouseLeave={(e) => e.target.style.color = 'rgb(96, 94, 92)'}
                                >
                                    ({new Date(appliedFilters.startDate).toLocaleDateString()} - {new Date(appliedFilters.endDate).toLocaleDateString()})
                                </span>
                            )}
                            {isLoadingWidgets && <Spinner size={SpinnerSize.small} />}
                        </div>
                    </div>

                    {/* Droite: Boutons Edit, Filter et PDF */}
                    <div className="analytics-header-right">
                        <IconButton
                            title={isEditMode ? t('SAVE_LAYOUT') : t('EDIT_LAYOUT')}
                            ariaLabel={isEditMode ? t('SAVE_LAYOUT') : t('EDIT_LAYOUT')}
                            onClick={async () => {
                                if (isEditMode) {
                                    // Save all tabs, widgets, and positions to backend
                                    await saveDashboardLayoutToBackend();
                                    // Exit edit mode and close catalog
                                    setIsEditMode(false);
                                    setShowCatalogPanel(false);
                                } else {
                                    // Enter edit mode and open catalog collapsed
                                    setIsEditMode(true);
                                    setShowCatalogPanel(true);
                                    setCatalogInitiallyCollapsed(true);
                                }
                            }}
                            styles={{
                                root: {
                                    width: 40,
                                    height: 40,
                                    backgroundColor: isEditMode ? '#107c10' : '#5b5fc7',
                                    border: 'none'
                                },
                                rootHovered: {
                                    backgroundColor: isEditMode ? '#0e6b0e' : '#4a4fb0'
                                }
                            }}
                        >
                            {isEditMode ? (
                                <FiSave style={{ fontSize: 16, color: 'white' }} />
                            ) : (
                                <FiEdit2 style={{ fontSize: 16, color: 'white' }} />
                            )}
                        </IconButton>
                        <IconButton
                            title={t('TOGGLE_FILTERS')}
                            ariaLabel={t('TOGGLE_FILTERS')}
                            onClick={() => setShowFilters(!showFilters)}
                            styles={{
                                root: {
                                    width: 40,
                                    height: 40,
                                    backgroundColor: showFilters ? '#4a4fb0' : '#5b5fc7',
                                    border: 'none'
                                },
                                rootHovered: {
                                    backgroundColor: '#4a4fb0'
                                }
                            }}
                        >
                            <FiFilter style={{ fontSize: 16, color: 'white' }} />
                        </IconButton>
                        {/* Catalog button removed - now controlled by Edit button only */}
                        {/* Manage Tabs button removed - now handled by + icon in tabs */}
                        {/* Export Excel de l'onglet courant : un classeur, une feuille par widget.
                            Instantané — il réutilise les données déjà à l'écran. */}
                        <IconButton
                            title={t('EXPORT_TAB_EXCEL')}
                            ariaLabel={t('EXPORT_TAB_EXCEL')}
                            onClick={handleExportTabExcel}
                            disabled={!appliedFilters || isExporting || selectedTab === 'calls'}
                            styles={{
                                root: {
                                    width: 40,
                                    height: 40,
                                    backgroundColor: '#107C41',   // vert Excel
                                    border: 'none'
                                },
                                rootHovered: {
                                    backgroundColor: '#0d6a37'
                                },
                                rootDisabled: {
                                    backgroundColor: '#e1dfdd',
                                    opacity: 0.5
                                }
                            }}
                        >
                            <FiFileText style={{ fontSize: 16, color: 'white' }} />
                        </IconButton>
                        <IconButton
                            title={t('EXPORT_PDF')}
                            ariaLabel={t('EXPORT_PDF')}
                            onClick={handleExportPDF}
                            disabled={!appliedFilters || isExporting}
                            styles={{
                                root: {
                                    width: 40,
                                    height: 40,
                                    backgroundColor: '#5b5fc7',
                                    border: 'none'
                                },
                                rootHovered: {
                                    backgroundColor: '#4a4fb0'
                                },
                                rootDisabled: {
                                    backgroundColor: '#e1dfdd',
                                    opacity: 0.5
                                }
                            }}
                        >
                            {isExporting ? (
                                <Spinner size={SpinnerSize.xSmall} />
                            ) : (
                                <FiDownload style={{ fontSize: 16, color: 'white' }} />
                            )}
                        </IconButton>
                    </div>
                </div>

                {/* Filtres - affichés conditionnellement */}
                {showFilters && (
                    <div style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                            {/* Fuseau du rapport — champ de RECHERCHE, pas une liste figée.
                                Avec 418 fuseaux, dérouler ne suffit plus : on tape « euro » et
                                l'Europe s'affiche, « new_y » et New York arrive. Le filtre porte
                                sur le nom IANA complet, donc la région est une clé de recherche
                                à part entière.
                                Valeur par défaut : le fuseau du navigateur. */}
                            <Combobox
                                aria-label={t('TIMEZONE')}
                                title={t('TIMEZONE')}
                                placeholder={t('TIMEZONE')}
                                value={timezoneQuery}
                                selectedOptions={[selectedTimezone]}
                                freeform
                                onChange={(e) => setTimezoneQuery(e.target.value)}
                                onOptionSelect={(_, data) => {
                                    if (!data.optionValue) return;
                                    setSelectedTimezone(data.optionValue);
                                    setTimezoneQuery(data.optionValue);
                                }}
                                // Une saisie abandonnée (« Euro » sans rien choisir) ne doit pas
                                // rester affichée comme si c'était le fuseau retenu : au départ
                                // du focus, le champ redit la sélection réelle.
                                onBlur={() => setTimezoneQuery(selectedTimezone)}
                                style={{ minWidth: '230px' }}
                            >
                                {filteredTimezones.map((tz) => (
                                    <Option key={tz.key} value={tz.key} text={tz.text}>
                                        {tz.text}
                                    </Option>
                                ))}
                                {filteredTimezones.length === 0 && (
                                    <Option key="_none" value="" disabled text="">
                                        {t('NO_DATA_AVAILABLE')}
                                    </Option>
                                )}
                            </Combobox>

                            {/* Filtre Heures ouvrées (IS_BUSINESSHOURS) */}
                            <Dropdown
                                selectedKey={appliedFilters?.businessHours || 'all'}
                                onChange={(e, option) => setAppliedFilters(prev => prev ? { ...prev, businessHours: option.key } : prev)}
                                options={[
                                    { key: 'all', text: t('BUSINESS_HOURS_ALL', { defaultValue: 'All hours' }) },
                                    { key: 'business', text: t('BUSINESS_HOURS_IN', { defaultValue: 'Business hours' }) },
                                    { key: 'outside', text: t('BUSINESS_HOURS_OUT', { defaultValue: 'Out of business hours' }) },
                                ]}
                                title={t('BUSINESS_HOURS', { defaultValue: 'Business hours' })}
                                styles={{ root: { width: 190, minWidth: 190 }, dropdown: { height: 32, minHeight: 32 } }}
                            />

                            {/* Filtre Jours ouvrés (IS_BUSINESSDAYS) */}
                            <Dropdown
                                selectedKey={appliedFilters?.businessDays || 'all'}
                                onChange={(e, option) => setAppliedFilters(prev => prev ? { ...prev, businessDays: option.key } : prev)}
                                options={[
                                    { key: 'all', text: t('BUSINESS_DAYS_ALL', { defaultValue: 'All days' }) },
                                    { key: 'business', text: t('BUSINESS_DAYS_IN', { defaultValue: 'Business days' }) },
                                    { key: 'holiday', text: t('BUSINESS_DAYS_OUT', { defaultValue: 'Holidays / weekends' }) },
                                ]}
                                title={t('BUSINESS_DAYS', { defaultValue: 'Business days' })}
                                styles={{ root: { width: 190, minWidth: 190 }, dropdown: { height: 32, minHeight: 32 } }}
                            />

                            {/* Filtres de date */}
                            <DatePicker
                                value={filterValues.startDate}
                                onSelectDate={handleStartDateChange}
                                placeholder={t('SELECT_DATE')}
                                ariaLabel={t('START_DATE')}
                                formatDate={(date) => date.toLocaleDateString('fr-FR')}
                                maxDate={new Date()}
                                styles={{
                                    root: { width: 130 },
                                    textField: { height: 32 }
                                }}
                            />
                            <DatePicker
                                value={filterValues.endDate}
                                onSelectDate={handleEndDateChange}
                                placeholder={t('SELECT_DATE')}
                                ariaLabel={t('END_DATE')}
                                formatDate={(date) => date.toLocaleDateString('fr-FR')}
                                maxDate={new Date()}
                                styles={{
                                    root: { width: 130 },
                                    textField: { height: 32 }
                                }}
                            />

                            {/* Dynamic Filters (Flow, Queue, Agent) - inline */}
                            <DashboardFilters
                                tenantId={tenantId}
                                scope={decodedScope}
                                onFilterChange={handleDynamicFilterChange}
                                initialFilters={dynamicFilters}
                            />

                            {/* Bouton Apply tout à droite */}
                            <Button
                                appearance="primary"
                                onClick={handleApplyFilters}
                                disabled={!filterValues.startDate || !filterValues.endDate}
                                style={{ height: '32px', marginLeft: 'auto' }}
                            >
                                {t('APPLY')}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Pivot principal - Dashboard, Calls List ET tabs personnalisés au même niveau */}
                {appliedFilters && (
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                        <Pivot
                            aria-label="Dashboard Navigation"
                            selectedKey={selectedTab}
                            onLinkClick={(item) => {
                                setSelectedTab(item.props.itemKey);
                            }}
                            headersOnly={true}
                            styles={{
                                root: { display: 'inline-flex', gap: '0' },
                                link: {
                                    marginLeft: '0px',
                                    marginRight: '0px',
                                    padding: '0 8px',
                                    '::after': {
                                        left: '0px',
                                        right: '0px'
                                    }
                                },
                                linkIsSelected: {
                                    marginLeft: '0px',
                                    marginRight: '0px',
                                    padding: '0 8px',
                                    '::before': {
                                        left: '0px',
                                        right: '0px'
                                    }
                                }
                            }}
                        >
                            <PivotItem headerText={t('DASHBOARD')} itemKey="dashboard" />
                            {customTabs.map((tab) => {
                                const localizedName = t(`TAB_${String(tab.id).toUpperCase()}`, { defaultValue: tab.name });
                                return (
                                    <PivotItem
                                        key={tab.id}
                                        headerText={
                                            (isEditMode && tab.type !== 'system') ? (
                                                <div >
                                                    <span>{localizedName}</span>
                                                    <Button
                                                        appearance="subtle"
                                                        icon={<DismissFilled />}
                                                        aria-label="Delete"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDeleteTabConfirm({
                                                                open: true,
                                                                tabId: tab.id,
                                                                tabName: localizedName
                                                            });
                                                        }}
                                                        className="tab-delete-button"
                                                    />
                                                </div>
                                            ) : localizedName
                                        }
                                        itemKey={tab.id}
                                    />
                                );
                            })}
                            <PivotItem headerText={t('CALLS_LIST')} itemKey="calls" />
                        </Pivot>
                        <IconButton
                            title={t('ADD_TAB') || 'Add Tab'}
                            ariaLabel="Add Tab"
                            onClick={() => {
                                setNewTabName('');
                                setIsAddTabDialogOpen(true);
                            }}
                            styles={{
                                root: {
                                    marginTop: "5px",
                                    width: 30,
                                    height: 30,
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    marginLeft: '8px',
                                    paddingBottom: '2px'
                                },
                                rootHovered: {
                                    backgroundColor: 'transparent'
                                }
                            }}
                        >
                            <span style={{ fontSize: 28, color: '#605e5c', fontWeight: 'bold' }}>+</span>
                        </IconButton>
                    </div>
                )}

                {/* Scrollable content */}
                <div
                    className="analytics-content-scrollable"
                    onDrop={handleDashboardDrop}
                    onDragOver={handleDashboardDragOver}
                    onDragLeave={handleDashboardDragLeave}
                >
                    {/* Dashboard Tab - Ancien système de widgets */}
                    {selectedTab === 'dashboard' && (
                        <div id="dashboard-container" data-export-tab="dashboard">
                            {/* Edit mode info message */}
                            {isEditMode && (
                                <MessageBar
                                    messageBarType={MessageBarType.info}
                                    isMultiline={false}
                                    onDismiss={() => setIsEditMode(false)}
                                    dismissButtonAriaLabel={t('SAVE_LAYOUT')}
                                    style={{ marginBottom: '15px' }}
                                >
                                    {t('EDIT_MODE_INFO')}
                                </MessageBar>
                            )}

                            {/* Discrete progress bar during loading */}
                            {isLoadingWidgets && (
                                <div style={{
                                    width: '100%',
                                    height: '2px',
                                    backgroundColor: '#f3f2f1',
                                    marginBottom: '10px',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        height: '100%',
                                        width: '40%',
                                        backgroundColor: '#0078d4',
                                        animation: 'progressBar 1.5s ease-in-out infinite'
                                    }} />
                                </div>
                            )}

                            {/* Widget Grid - Génération dynamique du layout depuis tabWidgetsPositions */}
                            {dashboardConfig && dashboardConfig.widgets && appliedFilters && (

                                <ResponsiveGridLayout
                                    key="dashboard-main"
                                    className="layout"
                                    layouts={(() => {
                                        const layout = dashboardConfig.widgets
                                            .filter(widget => DASHBOARD_WIDGET_IDS.includes(widget.id))
                                            .map(widget => {
                                                const savedPos = tabWidgetsPositions['dashboard']?.[widget.id];
                                                const defaultPos = DEFAULT_WIDGET_POSITIONS[widget.id];
                                                const pos = savedPos || defaultPos || { x: 0, y: 0, w: 3, h: 2 };
                                                const { minW, minH } = getMinSizeForType(widget.type);
                                                return { i: widget.id, x: pos.x, y: pos.y, w: pos.w, h: pos.h, minW, minH, static: false };
                                            });
                                        // Même layout pour tous les breakpoints — la responsivité
                                        // se fait uniquement par la taille des widgets via container queries.
                                        return { lg: layout, md: layout, sm: layout, xs: layout, xxs: layout };
                                    })()}
                                    breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                                    cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
                                    rowHeight={70}
                                    onLayoutChange={handleLayoutChange}
                                    onDragStart={() => setIsDragging(true)}
                                    onDragStop={() => setIsDragging(false)}
                                    onResizeStart={() => setIsResizing(true)}
                                    onResizeStop={() => setIsResizing(false)}
                                    isDraggable={isEditMode}
                                    isResizable={isEditMode}
                                    compactType="vertical"
                                    preventCollision={false}
                                    useCSSTransforms={!isExporting}
                                >
                                    {dashboardConfig.widgets
                                        .filter(widgetConfig => DASHBOARD_WIDGET_IDS.includes(widgetConfig.id))
                                        .map((widgetConfig) => (
                                        <div key={widgetConfig.id}>
                                            <Widget
                                                config={widgetConfig}
                                                filterValues={appliedFilters}
                                                // Un widget de l'onglet Dashboard peut lui aussi
                                                // porter ses propres filtres, via l'instance
                                                // `dashboard-<id>` (cf. profil analytics).
                                                filterBadge={renderWidgetFilterBadge(widgetInstances[`dashboard-${widgetConfig.id}`]?.config)}
                                                isEditMode={isEditMode}
                                                tenantId={tenantId}
                                                token={token}
                                                preloadedData={widgetsData[widgetConfig.id]}
                                                isLoading={isLoadingWidgets}
                                                onRestore={handleRestoreWidget}
                                                onEdit={handleEditDashboardWidget}
                                                onMove={handleMoveDashboardWidget}
                                                onDelete={handleDeleteDashboardWidget}
                                                onDrillDown={CALL_DRILLDOWNS[widgetConfig.id] ? handleWidgetDrillDown : null}
                                            />
                                        </div>
                                    ))}
                                </ResponsiveGridLayout>
                            )}
                        </div>
                    )}

                    {/* Tabs personnalisés - Affichage direct quand sélectionné dans le Pivot */}
                    {selectedTab !== 'dashboard' && selectedTab !== 'calls' && customTabs.find(t => t.id === selectedTab) && (
                        <div id="dashboard-container" data-export-tab={selectedTab}>
                            {/* Edit mode info message */}
                            {isEditMode && (
                                <MessageBar
                                    messageBarType={MessageBarType.info}
                                    isMultiline={false}
                                    onDismiss={() => setIsEditMode(false)}
                                    dismissButtonAriaLabel={t('SAVE_LAYOUT')}
                                    style={{ marginBottom: '15px' }}
                                >
                                    {t('EDIT_MODE_INFO')}
                                </MessageBar>
                            )}

                            {/* Empty state - No widgets */}
                            {getWidgetInstancesForTab(selectedTab).length === 0 && (
                                <div className="dashboard-empty-state">
                                    <div className="empty-state-icon">📊</div>
                                    <div className="empty-state-title">No widgets in this tab</div>
                                    <div className="empty-state-description">
                                       Add widgets to this tab
                                    </div>
                                </div>
                            )}

                            {/* Widget Grid pour custom tabs avec drag & drop */}
                            {getWidgetInstancesForTab(selectedTab).length > 0 && (
                                <ResponsiveGridLayout
                                    className="layout"
                                    layouts={(() => {
                                        const layout = getWidgetInstancesForTab(selectedTab).map(inst => {
                                            const pos = tabLayouts[selectedTab]?.[inst.id];
                                            return {
                                                i: inst.id,
                                                x: pos?.x ?? 0,
                                                y: pos?.y ?? 0,
                                                w: pos?.w ?? 6,
                                                h: pos?.h ?? 4,
                                                minW: 2,
                                                minH: 2,
                                                maxW: 12,
                                                static: false
                                            };
                                        });
                                        return { lg: layout, md: layout, sm: layout, xs: layout, xxs: layout };
                                    })()}
                                    breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                                    cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
                                    rowHeight={70}
                                    onLayoutChange={(layout, layouts) => handleTabWidgetLayoutChange(selectedTab, layout)}
                                    onDragStart={() => setIsDragging(true)}
                                    onDragStop={() => setIsDragging(false)}
                                    onResizeStart={() => setIsResizing(true)}
                                    onResizeStop={() => setIsResizing(false)}
                                    isDraggable={isEditMode}
                                    isResizable={isEditMode}
                                    compactType="vertical"
                                    preventCollision={false}
                                    useCSSTransforms={!isExporting}
                                    resizeHandles={['se']}
                                    draggableCancel="button, .widget-instance-actions"
                                    autoSize={true}
                                >
                                    {getWidgetInstancesForTab(selectedTab).map(widgetInstance => (
                                        <div key={widgetInstance.id}>
                                            {renderWidgetContent(widgetInstance)}
                                        </div>
                                    ))}
                                </ResponsiveGridLayout>
                            )}
                        </div>
                    )}

                    {/* Calls List Tab */}
                    {selectedTab === 'calls' && appliedFilters && (
                        // flex:1 + minHeight:0 : le panneau remplit exactement l'espace dispo
                        // de la colonne flex parente (au lieu d'une hauteur fixe calc(100vh-180px)
                        // qui débordait → 2e scrollbar sur .init-route-container). Seul le scroll
                        // interne de la table subsiste.
                        <div style={{ padding: '20px', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                            {/* Bandeau de drill-down : la liste est filtrée, il faut le DIRE.
                                Sans ça, l'utilisateur arrivant depuis une carte croit que la
                                période ne contient que ces appels-là. */}
                            {drillDownSource && (
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                                    marginBottom: '12px', padding: '8px 12px', flexShrink: 0,
                                    background: 'rgba(0, 120, 212, 0.08)',
                                    border: '1px solid rgba(0, 120, 212, 0.25)', borderRadius: '6px'
                                }}>
                                    <span style={{ fontSize: '14px' }}>
                                        {t('DRILLDOWN_ACTIVE', { defaultValue: 'Filtered from widget' })}
                                        {' : '}
                                        <strong>{t(`WIDGET_NAMES.${drillDownSource}`, { defaultValue: drillDownSource })}</strong>
                                        {' — '}
                                        {t('DRILLDOWN_COUNT', {
                                            shown: filteredCallsList.filter(c => !c.isExpandedRow).length,
                                            total: callsList.length,
                                            defaultValue: '{{shown}} of {{total}} calls'
                                        })}
                                    </span>
                                    <Button appearance="subtle" size="small" onClick={clearDrillDown}>
                                        ✕ {t('DRILLDOWN_CLEAR', { defaultValue: 'Show all calls' })}
                                    </Button>
                                </div>
                            )}
                            {/* Action bar with export button */}
                            <Stack horizontal tokens={{ childrenGap: 10 }} style={{ marginBottom: '20px', flexShrink: 0 }}>
                                <Button
                                    appearance="primary"
                                    onClick={handleExportToExcel}
                                    disabled={isLoadingCalls || isExportingExcel || filteredCallsList.length === 0}
                                >
                                    📊 {t('EXPORT_TO_EXCEL')}
                                </Button>
                                {isLoadingCalls && (
                                    <span style={{
                                        fontSize: '14px',
                                        color: '#0078D4',
                                        fontStyle: 'italic',
                                        lineHeight: '32px'
                                    }}>
                                      {t('LOADING')} 
                                    </span>
                                )}
                                {!isLoadingCalls && filteredCallsList.length > 0 && (
                                    <MessageBar messageBarType={MessageBarType.info} isMultiline={false}>
                                        {t('CALLS_DISPLAYED_INFO', { count: filteredCallsList.length })}
                                    </MessageBar>
                                )}
                            </Stack>

                            {/* On garde la table visible pendant le rafraîchissement
                                (l'ancienne data reste affichée + indicateur "Loading" au-dessus),
                                au lieu de vider la vue. */}
                            {callsList.length > 0 ? (
                                <div ref={tableContainerRef} style={{ overflowX: 'auto', overflowY: 'auto', width: '100%', flex: 1, opacity: isLoadingCalls ? 0.5 : 1, transition: 'opacity 0.15s' }}>
                                    <DetailsList
                                        items={filteredCallsList}
                                        columns={callsListColumns}
                                        layoutMode={DetailsListLayoutMode.justified}
                                        selectionMode={SelectionMode.none}
                                        isHeaderVisible={true}
                                        getKey={(item) => item.uniqueKey || item.callId}
                                        onRenderRow={(rowProps, defaultRender) => {
                                            // Ligne expand = pleine largeur (recording + transcript sur une ligne,
                                            // pas confinée à une cellule → plus de double-scroll ni de clipping).
                                            if (rowProps && rowProps.item && rowProps.item.isExpandedRow) {
                                                return (
                                                    <div style={{ background: '#faf9f8', borderBottom: '1px solid #edebe9' }}>
                                                        {renderCallExpandedDetail(rowProps.item)}
                                                    </div>
                                                );
                                            }
                                            return defaultRender ? defaultRender(rowProps) : <DetailsRow {...rowProps} />;
                                        }}
                                    />
                                </div>
                            ) : !isLoadingCalls && (
                                <MessageBar messageBarType={MessageBarType.info}>
                                    {t('NO_DATA_AVAILABLE')}
                                </MessageBar>
                            )}
                        </div>
                    )}
                </div>

                {/* PDF export blocking dialog — empêche l'utilisateur de toucher au dashboard
                    pendant que les tabs sont capturés un par un. */}
                <FluentDialog
                    hidden={!isExporting}
                    onDismiss={() => {}}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: t('EXPORTING_PDF') || 'Export PDF en cours…',
                        subText: t('EXPORTING_PDF_WARNING') || 'Ne touchez pas au rapport pendant la génération. L\'écran va défiler entre les onglets — c\'est normal.'
                    }}
                    modalProps={{
                        isBlocking: true,
                        styles: { main: { maxWidth: 480 } }
                    }}
                >
                    <ProgressIndicator
                        label={pdfExportProgress.tabName
                            ? `${pdfExportProgress.current} / ${pdfExportProgress.total} — ${pdfExportProgress.tabName}`
                            : t('PREPARING') || 'Préparation…'}
                        percentComplete={pdfExportProgress.total > 0 ? pdfExportProgress.current / pdfExportProgress.total : undefined}
                    />
                </FluentDialog>

                {/* Export progress dialog */}
                <Dialog
                    hidden={!showExportDialog}
                    onDismiss={closeExportDialog}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: t('EXPORTING_EXCEL'),
                        subText: exportMessage
                    }}
                    modalProps={{
                        isBlocking: isExportingExcel,
                        styles: { main: { maxWidth: 450 } }
                    }}
                >
                    <ProgressIndicator
                        label={exportProgress > 0 ? `${exportProgress}%` : ''}
                        percentComplete={exportProgress / 100}
                    />
                    {exportDownloadUrl && (
                        <Stack tokens={{ childrenGap: 15 }} style={{ marginTop: 20 }}>
                            <MessageBar messageBarType={MessageBarType.success}>
                                {t('EXPORT_COMPLETED')}
                            </MessageBar>
                        </Stack>
                    )}
                    <DialogFooter>
                        {exportDownloadUrl ? (
                            <>
                                <PrimaryButton
                                    onClick={() => {
                                        // Direct navigation to the SAS URL (no fetch — CSP
                                        // connect-src blocks *.blob.core.windows.net). The blob
                                        // downloads via its xlsx content-type + attachment header.
                                        const link = document.createElement('a');
                                        link.href = exportDownloadUrl;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                    }}
                                    text={t('DOWNLOAD_EXCEL')}
                                />
                                <DefaultButton onClick={closeExportDialog} text={t('CLOSE')} />
                            </>
                        ) : (
                            <DefaultButton
                                onClick={closeExportDialog}
                                text={t('CLOSE')}
                                disabled={isExportingExcel}
                            />
                        )}
                    </DialogFooter>
                </Dialog>

                {/* Widget Catalog Dialog */}
                <WidgetCatalog
                    isOpen={isCatalogOpen}
                    onDismiss={() => setIsCatalogOpen(false)}
                    onSelectWidget={handleSelectWidgetFromCatalog}
                />

                {/* Widget Instance Configuration Dialog */}
                <WidgetInstanceConfig
                    isOpen={isInstanceConfigOpen}
                    onDismiss={() => setIsInstanceConfigOpen(false)}
                    widgetInfo={selectedWidgetInfo}
                    instance={editingInstance}
                    onSave={handleSaveWidgetInstance}
                    availableTabs={[
                        { id: 'dashboard', name: 'Dashboard', type: 'dashboard' },
                        { id: 'calls', name: 'Calls List', type: 'calls' },
                        ...customTabs
                    ]}
                    currentTabId={selectedTab}
                />

                {/* Tab Manager Dialog */}
                <TabManager
                    isOpen={isTabManagerOpen}
                    onDismiss={() => setIsTabManagerOpen(false)}
                    tabs={[
                        { id: 'dashboard', name: 'Dashboard', isSystemTab: true, type: 'dashboard' },
                        { id: 'calls', name: 'Calls List', isSystemTab: true, type: 'calls' },
                        // Les tabs système (sla/abandons/flows/trends, type:'system') restent
                        // dans customTabs pour l'affichage mais ne doivent pas être supprimables.
                        ...customTabs.map(tab => ({ ...tab, isSystemTab: tab.isSystemTab || tab.type === 'system' }))
                    ]}
                    onCreateTab={handleCreateTab}
                    onEditTab={handleEditTab}
                    onDeleteTab={handleDeleteTab}
                    currentTabId={selectedTab}
                    onSelectTab={setSelectedTab}
                />

                {/* Confirm Delete Tab Dialog */}
                <ConfirmAlert
                    open={deleteTabConfirm.open}
                    onConfirm={() => {
                        handleDeleteTab(deleteTabConfirm.tabId);
                        setDeleteTabConfirm({ open: false, tabId: null, tabName: '' });
                    }}
                    onCancel={() => setDeleteTabConfirm({ open: false, tabId: null, tabName: '' })}
                    name={deleteTabConfirm.tabName}
                />

                {/* Add Tab Dialog - Same style as AddRoutingDialog */}
                <Dialog
                    open={isAddTabDialogOpen}
                    onOpenChange={(_, data) => {
                        if (!data.open) {
                            setIsAddTabDialogOpen(false);
                            setNewTabName('');
                        }
                    }}
                >
                    <DialogSurface>
                        <DialogBody>
                            <DialogTitle>{t('ADD_NEW_TAB') || 'Add New Tab'}</DialogTitle>
                            <DialogContent className={dialogStyles.dialogContent}>
                                <div className={dialogStyles.inputContainer}>
                                    <Label required>{t('TAB_NAME') || 'Tab Name'}</Label>
                                    <Input
                                        value={newTabName}
                                        onChange={(_, data) => setNewTabName(data.value)}
                                        placeholder={t('ENTER_TAB_NAME_PLACEHOLDER') || 'e.g., Sales Dashboard'}
                                        autoFocus
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && newTabName.trim()) {
                                                handleCreateTab(newTabName.trim());
                                                setIsAddTabDialogOpen(false);
                                                setNewTabName('');
                                            }
                                        }}
                                    />
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <DialogTrigger disableButtonEnhancement>
                                    <Button
                                        appearance="secondary"
                                        onClick={() => {
                                            setIsAddTabDialogOpen(false);
                                            setNewTabName('');
                                        }}
                                    >
                                        {t('CANCEL') || 'Cancel'}
                                    </Button>
                                </DialogTrigger>
                                <Button
                                    appearance="primary"
                                    onClick={() => {
                                        if (newTabName.trim()) {
                                            handleCreateTab(newTabName.trim());
                                            setIsAddTabDialogOpen(false);
                                            setNewTabName('');
                                        }
                                    }}
                                    disabled={!newTabName.trim()}
                                >
                                    {t('ADD') || 'Add'}
                                </Button>
                            </DialogActions>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>
            </div>

            {/* Right Side: Widget Configuration Panel */}
            <AnalyticsWidgetConfigPanel
                isOpen={showConfigPanel}
                widget={selectedWidget}
                widgetInstance={editingInstance}
                tabs={[
                    { id: 'dashboard', name: 'Dashboard' },
                    { id: 'calls', name: 'Calls List' },
                    ...customTabs
                ]}
                currentTabId={selectedTab}
                tenantId={tenantId}
                scope={decodedScope}
                onSave={handleConfigSave}
                onClose={handleConfigClose}
                onDelete={handleConfigDelete}
            />

            {/* Transcription Drawer */}
            <Drawer
                type="overlay"
                position="end"
                open={showTranscriptionPanel}
                onOpenChange={(_, { open }) => setShowTranscriptionPanel(open)}
                size="small"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                icon={<Dismiss24Regular />}
                                onClick={() => setShowTranscriptionPanel(false)}
                                aria-label={t('CLOSE')}
                            />
                        }
                    >
                        {t('TRANSCRIPTION')}
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    {selectedTranscription && (
                        <div style={{ padding: '12px' }}>
                            {(Array.isArray(selectedTranscription.segments) && selectedTranscription.segments.length > 0) ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {selectedTranscription.segments.map((seg, i) => {
                                        const isAgent = seg.role === 'agent';
                                        return (
                                            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: isAgent ? 'flex-start' : 'flex-end' }}>
                                                <div style={{ fontSize: '11px', color: '#605e5c', margin: '0 6px 2px' }}>
                                                    {seg.speaker || (isAgent ? t('AGENT') : t('CALLER'))} · {seg.time}
                                                </div>
                                                <div style={{
                                                    maxWidth: '85%',
                                                    padding: '8px 12px',
                                                    borderRadius: '12px',
                                                    borderBottomLeftRadius: isAgent ? '2px' : '12px',
                                                    borderBottomRightRadius: isAgent ? '12px' : '2px',
                                                    background: isAgent ? '#f3f2f1' : '#e8eff9',
                                                    color: '#201f1e',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word'
                                                }}>
                                                    {seg.text}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : selectedTranscription.text ? (
                                <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', fontSize: '14px', color: '#242424' }}>
                                    {selectedTranscription.text}
                                </div>
                            ) : (
                                <MessageBar messageBarType={MessageBarType.info}>
                                    {t('NO_TRANSCRIPTION_AVAILABLE')}
                                </MessageBar>
                            )}
                        </div>
                    )}
                </DrawerBody>
            </Drawer>
        </div>
    );
};

export default AnalyticsDashboard;
